import { CreditCardTracking } from '../../helpers/credit-card-tracking';
import { EliteProSettings } from '../elite-pro/elite-pro';
import { tracking, _t, _l } from '@bark/bark-core';
import moment from 'moment';
import { _n, currency } from '@bark/bark-core/src/helpers/number-formatting';
import { showModalErrorText } from '../payments/credit-card';
import { getContactPreferencesText } from './contact_preferences';
import {
	isUSTaxComplianceActive,
	isUSTaxCompliant,
	saveBusinessAddress,
	continueToBusinessAddress,
	initializeGooglePlacesAutoComplete,
	EntityTracking,
} from '@bark/bark-core';
import { triggerEnquiriesBanner } from '../index';
import { creditUpsellModalBindTrackingListeners } from './credit_upsell_modal';
import { barkApi } from '@bark/bark-core/src/components/legacy-scripts/barklib';
import {
	shouldShowLeadsListRct,
	loadLeadsListRct,
	leadsListRctRemoveProjectFromList,
	leadsListRctNotInterested,
	LeadsListRctDispatcher,
	loadValueBadgesLeadDetailsRct,
	loadProjectDetailsBookingDetails,
} from './leads_list_rct';
import {
	shouldShowLeadDetailsNewDesign,
	applyLeadDetailsNewDesignQnAChanges,
	getContactButtonContainerSelector,
	scrollToTopOnMobile,
} from './lead_details_redesign';

import { showQuickFilters } from './dashboard_quick_filters';

import {
	shouldShowProjectDetailsLeadDetailsRct,
	loadProjectDetailsLeadDetailsRct,
	shouldShowResponsesLeadDetailsRct,
	loadResponsesLeadDetailsRct,
	shouldShowBuyerInteractionLeadDetailsRct,
	loadBuyerInteractionLeadDetailsRct,
	shouldShowBookingDetailsRct,
	showSetupJobsModal,
	showBookingRequestOnboardingModal,
} from './lead_details_rct';

import {
	toggleMobileStickyHeaderView,
	shouldShowMobileStickyHeaderRct,
	loadMobileStickyHeaderRct,
	setMobileStickyHeaderRctShowPassBtn,
} from './mobile_sticky_header_rct';

/**
 * @typedef ProjectCustomField
 * @property {String} question
 * @property {String} answer
 */

/**
 * @typedef CreditStatusResponse
 * @property {Boolean} autoTopupPack
 * @property {{}[]} creditPacks
 * @property {Number} creditsBalance
 * @property {Number} creditsJustPurchased
 * @property {String} currencySymbol E.g. $
 * @property {Boolean} hasFreeResponses
 * @property {Boolean} hasVat
 * @property {Boolean} isResellerClient
 * @property {Boolean} notEligibleForStarterPack Awkward property name. Inverse of eligibleForStarterPack
 * @property {Boolean} oneClickEnabled
 * @property {Number} previousPackId
 * @property {Boolean} proPlanUpsell
 * @property {Boolean} purchasedStartedPack
 * @property {Boolean} requiredToBuyStarterPack
 * @property {string|false} stripeCustomerId
 * @property {{}[]|null} upgradePack
 * @property {Number} vatRate
 */

/**
 * @typedef ProjectObject
 * @property {Number} bark_country_id
 * @property {String} bark_country_name
 * @property {Boolean} be_first
 * @property {String} bg_color
 * @property {String|null} buyer_avatar
 * @property {String} buyer_email
 * @property {Boolean} buyer_interaction_callout_text
 * @property {mixed} buyer_interaction_most_significant
 * @property {mixed} buyer_messages
 * @property {String} buyer_share_name
 * @property {String} buyer_telephone
 * @property {String} buyer_telephone_type
 * @property {Number} buyer_user_id
 * @property {Boolean} categ_promo
 * @property {Number} category_id
 * @property {String} city_string
 * @property {mixed} clicked
 * @property {Number} created
 * @property {Number} credits_required
 * @property {Number|null} credits_required_before_discount
 * @property {Number} credits_required_original
 * @property {ProjectCustomField[]} custom_fields
 * @property {String} estimate_highest
 * @property {String} estimate_lower
 * @property {String} estimate_lowest
 * @property {String} estimate_type
 * @property {String} estimate_upper
 * @property {Boolean} estimator_enabled
 * @property {Boolean} estimator_value
 * @property {mixed} expected_credits_required
 * @property {Boolean} expecting_free_responses
 * @property {String[]} files
 * @property {Boolean} hide_prev_cr
 * @property {String[]} images
 * @property {Number} is_buyer_phone_verified
 * @property {Boolean} is_free
 * @property {Number} is_local
 * @property {Boolean} is_remote_supported
 * @property {Boolean} is_shortlisted
 * @property {Boolean} is_reoccurring_buyer
 * @property {Boolean} is_likely_to_hire
 * @property {String} latitude
 * @property {String} longitude
 * @property {String} national_label
 * @property {Boolean} opened_bark
 * @property {String|null} project_detail
 * @property {Number} project_id
 * @property {String} project_title
 * @property {String} project_postcode
 * @property {Number} response_cap
 * @property {Number} response_count
 * @property {Boolean} resubmitted_project
 * @property {String} since
 * @property {String} summary
 * @property {String} time_since
 * @property {Boolean} viewed
 * @property {mixed[]} highlights
 * @property {Boolean} is_urgent
 * @property {Boolean} is_contact_pref_type_email
 * @property {Boolean} is_contact_pref_type_phone
 * @property {Boolean} is_contact_pref_type_sms
 * @property {Boolean} is_top_opportunity
 * @property {Object} trusted_form
 * @property {Boolean} trusted_form.has_trusted_form_certificate
 * @property {mixed} trusted_form.certificate_id
 * @property {mixed} trusted_form.certificate_url
 * @property {Number} id
 * @property {String} category_name
 * @property {mixed} detail
 * @property {String|null} avatar_url
 * @property {String} buyer_name
 * @property {String} country_name
 * @property {String} need_list
 * @property {Boolean} has_card_details
 * @property {String} provider_noun
 * @property {String} provider_noun_plural
 * @property {mixed} one_click_enabled
 * @property {String} lat
 * @property {String} lng
 * @property {String} time_ago
 * @property {String} is_seller_shortlisted
 * @property {String} buyer_interaction_top_text
 * @property {mixed[]} buyer_interaction_messages
 * @property {mixed[]} buyer_interactions
 * @property {ProjectCustomField[]} answers
 * @property {String} currency_symbol
 * @property {String} price_per_credit
 * @property {mixed} estimate
 * @property {Boolean} has_files
 * @property {Boolean} has_images
 * @property {Boolean} is_project_alert_free
 * @property {Boolean} is_free_with_subscription
 * @property {Number} n_free_leads_remaining
 */

const isDebug = () => new URL(window.location.href).searchParams.get('debug') === '1';

export function SellerDashboard() {
	var Bark = (window.Bark = window.Bark || {});
	let LeadsFilterHeaderDangerouslySetState = null;
	let CompleteProfileHighlightDispatcher = null;
	let openCompleteProfileModal = null;
	let showCompleteProfileHighlight = null;

	const { Handlebars } = window;
	const creditCardTracking = CreditCardTracking();

	const SAVED_CARD = 'saved-card';
	const NEW_CARD = 'new-card';
	const sellerDashboardProjectDetailsSelector =
		'.seller-dash-v2 .dashboard-project-details';

	const verifiedPhoneNumberTemplate = Handlebars.compile(
		document.getElementById('verified-phone-modal-template').innerHTML,
	);

	const entityTracking = new EntityTracking({ nameFormat: 'title', debug: isDebug() });

	var purchasing = false;

	const getUserIdFromModal = () => {
		return Bark.seller.isMultiPaymentModalAvailable()
			? Number($('#multi-payment-modal').data('userId'))
			: Number($('#payment-card-modal').data('userId'));
	};

	// Note, in this pattern, the following should only ever be Bark.seller = Bark.seller || {};
	//  As it is not, $.extend has to be used...
	Bark.seller = $.extend(
		{
			// seller id
			id: Bark.seller.id,

			getUserIdFromModal,

			eps: null,

			currency_symbol: Bark.seller.currencySymbol,

			one_click_enabled: Bark.seller.oneClick,

			// Should render first item when loading records
			showFirstItem: true,

			// Flag that indicates data isbeing fetched
			fetchingRecords: null,
			fetchingFilterCounts: null,

			// leads_filter_header_exp: Following are a number of services and locations the seller has
			servicesCount: null,
			locationsCount: null,

			// More records to load?
			moreRecords: true,

			// Project currently shown
			currentlyShownProjectId: null,

			// any errors for projects stored from api
			projectErrors: {},

			// Onload flag
			isFirstDataLoad: true,

			// viewport settings
			mobileThreshold: 768,
			tabletThreshold: 992,
			// vieport flags
			isMobile: false,
			isTablet: false,
			isDesktop: false,
			isInNewUIExperiment: $('#js-show-new-ui')?.val() == 1 ?? false,
			isUSTaxComplianceExperiment: $('#tax-compliance-exp-v')?.val() == 1 ?? false,
			shouldSeeNewHeader: $('#js-show-new-header').val() == '1' ?? false,

			// Flag to indicate project interaction
			interactingWithProject: false,

			// Current page
			page: Bark.seller.page,

			// Stripe token return when buing credits (for the first time?)
			stripeToken: '',

			// Payment type used to buy credits
			paymentType: '',

			// Plan Id used to make a payment
			planId: '',

			salesTeamQuoteId: 0,

			// Send message is now shown
			sendMessageFormShown: false,

			// The selector used to reopen the last pack selector
			lastPackModalSelector: '',
			currentModalSelector: '',

			// Seller's credit status
			creditStatus: {},

			projectLogs: {},

			welcomeShown: false,

			nextPage: '',

			perPage: 15,

			unreadCount: 0,

			searchTermOnModalOpen: '',

			initialQueryPayload: {},

			newQueryPayload: {},

			/**
			 * @property {PaymentRequestOptions} currentPaymentRequest
			 */
			currentPaymentRequest: {},

			lastRefresh: new Date(),

			projectBundle: Bark.seller.projectBundle,
			acceptedBundleCreditPurchase: false,

			currentRequestPage: 1,

			lastProjectViewed: null,

			sellerSelfObject: {},

			testing: QS.getValue('testing', 0),

			lastPurchasedPackId: false,

			mostRecentCreditsPurchased: 0,

			lastAttemptedResponseType: null,

			cardModalSelector: '.js-card-payment-modal',
			multiPaymentModalSelector: '#multi-payment-modal',
			addressComplianceModalSelector: '#addressCompliance',
			addressComplianceConfirmCancelModalSelector: '#addressComplianceConfirmCancel',
			isUSTaxCompliant: null,

			isMultiPaymentModalAvailable: function () {
				return document.getElementById('multi-payment-modal');
			},

			waitForElement: function (elementPath, callBack) {
				window.setTimeout(function () {
					if ($(elementPath).length && $(elementPath).is(':visible')) {
						callBack(elementPath, $(elementPath));
					} else {
						Bark.seller.waitForElement(elementPath, callBack);
					}
				}, 500);
			},
			$cardModal: null,

			starterPackModalSelector: '#sellerRespondUpsell',
			purchaseCreditsErrorModalSelector: '#purchaseCreditsError',

			projectViewed: function (projectId) {
				var $project = $('.dashboard-projects-item[data-project-id="' + projectId + '"]');
				if (Bark.seller.lastProjectViewed !== projectId) {
					Bark.seller.lastProjectViewed = projectId;
					Bark.seller.logProjectView(projectId);
					if ($project.data('unread') === true) {
						$project.data('unread', false);
						$project.find('.unread-notification').removeClass('show');
						if (Bark.seller.unreadCount >= 0) {
							Bark.seller.unreadCount--;
							$('#viewed_unread_checkbox')
								.closest('.custom-control')
								.find('span.count')
								.text(' (' + Bark.seller.unreadCount + ')');
						}
					}
				}
			},
			openUSTaxCompliantModal: function (atu = false) {
				$(Bark.seller.addressComplianceModalSelector).modal('show');

				if (!atu) {
					let page = 'purchase credits';
					let context = 'Purchase credits';
					continueToBusinessAddress(context);

					$(
						Bark.seller.addressComplianceModalSelector +
							' .js-close-business-address-modal',
					).removeClass('d-none');
					$(
						Bark.seller.addressComplianceModalSelector +
							' .js-close-confirm-business-address-modal',
					).addClass('d-none');
					if (!Bark.seller.cardLast4 || Bark.seller.isMultiPaymentModalAvailable()) {
						$('#next-button-business-address').removeClass('btn-success');
						$('#next-button-business-address').addClass('btn-primary');
					} else {
						$('#next-button-business-address').html(
							_t('seller_dashboard:card-modal.auto-topup-confirm-button'),
						);
					}
					tracking('Wayfair', `US Tax Compliance - ${page} begin`, {
						context: context,
					});
				} else {
					let page = 'auto-top up';
					let context = 'Auto-top up response';
					continueToBusinessAddress(context);

					$('#next-button-business-address').html(
						_t('seller_dashboard:card-modal.auto-topup-confirm-button'),
					);
					$(
						Bark.seller.addressComplianceModalSelector +
							' .js-close-business-address-modal',
					).addClass('d-none');
					$(
						`${Bark.seller.addressComplianceModalSelector} .js-close-confirm-business-address-modal, ${Bark.seller.addressComplianceModalSelector} .js-confirm-cancel-close`,
					).removeClass('d-none');

					$(
						`${Bark.seller.addressComplianceModalSelector} .js-close-confirm-business-address-modal, ${Bark.seller.addressComplianceModalSelector} .js-confirm-cancel-close`,
					).click(function () {
						Bark.seller.lastPackModalSelector =
							Bark.seller.addressComplianceModalSelector;
						$(Bark.seller.addressComplianceModalSelector).modal('hide');
						$(Bark.seller.addressComplianceConfirmCancelModalSelector).modal('show');
					});
					tracking('Wayfair', `US Tax Compliance - ${page} begin`, {
						context: context,
					});
				}
			},
			bindUSTaxComplianceModalNextAction: function ($callback, atu = false) {
				if (!$('#street_address').val() && !Bark.isMobile()) {
					$('#street_address').focus();
				}
				$('#next-button-business-address')
					.off('click')
					.on('click', async function () {
						Bark.logEvent('click', 'next button business address');
						if (isUSTaxComplianceActive()) {
							let context = !atu ? 'Purchase credits' : 'Auto-top up response';
							saveBusinessAddress(context, function () {
								Bark.seller.isUSTaxCompliant = true;
								let $container = $(Bark.seller.addressComplianceModalSelector);
								$container.modal('hide');
								$callback();
							});
						}
					});
			},
			bindUSTaxComplianceConfirmCancelModal: function () {
				$(
					Bark.seller.addressComplianceConfirmCancelModalSelector +
						' .js-close-business-address-modal',
				).click(function () {
					$(Bark.seller.addressComplianceModalSelector).modal('show');
					$(Bark.seller.addressComplianceConfirmCancelModalSelector).modal('hide');
				});

				$(
					`${Bark.seller.addressComplianceConfirmCancelModalSelector} .js-quit-confirm-business-address-modal, ${Bark.seller.addressComplianceConfirmCancelModalSelector} .close`,
				).click(function () {
					Bark.seller.lastPackModalSelector =
						Bark.seller.addressComplianceConfirmCancelModalSelector;
					Bark.seller.hideProjectInteractionLoader();
					$(Bark.seller.addressComplianceConfirmCancelModalSelector).modal('hide');
				});
			},
			bindEvents: function () {
				if ($('#notInterestedSurvey').length > 0) {
					$('#notInterestedSurvey .js-reason-row')
						.off('mouseenter mouseleave')
						.hover(function () {
							$(this).toggleClass('bg-grey-25');
						});

					$('#notInterestedSurvey')
						.off('hide.bs.modal')
						.on('hide.bs.modal', function () {
							$('#notInterestedSurvey')
								.find('p.text-dark-blue')
								.removeClass('text-dark-blue')
								.addClass('text-grey-600');
							$('#notInterestedSurvey')
								.find('span.bark-svg-icon.bsi-primary-primary')
								.removeClass('bsi-primary-primary')
								.addClass('bsi-primary-grey-600');
							$('#surveyOther')
								.val('')
								.removeClass('is-invalid')
								.removeClass('mb-2')
								.parent()
								.removeClass('bg-grey-25');
							$('#notInterestedSurvey .other-error').text('');
							$('#notInterestedSurvey .submit-survey:visible').addClass('d-none');
							var pid = parseInt($('.dashboard-project-details').attr('data-project-id'));
							if (shouldShowLeadsListRct()) {
								leadsListRctNotInterested(pid);
							} else {
								Bark.seller.removeProject(pid);
							}

							if (showQuickFilters()) {
								//Updates quick filters counts
								setTimeout(function () {
									Bark.seller.fetchFilteredRecords();
								}, 1000);
							}
						});

					$('#notInterestedSurvey .skip-survey')
						.off('click')
						.on('click', function (e) {
							var pid = parseInt($('.dashboard-project-details').attr('data-project-id'));
						});

					$('#surveyOther')
						.off('focus')
						.on('focus', function () {
							$(this).parent().addClass('bg-grey-25');
							$('#notInterestedSurvey .submit-survey')
								.removeClass('d-none')
								.off('click')
								.on('click', function () {
									// validate Other:
									var otherReason = $('#surveyOther').val();
									if (otherReason.length > 4) {
										$('#notInterestedSurvey .other-error').text();
										var sid = Bark.seller.id;
										var pid = parseInt(
											$('.dashboard-project-details').attr('data-project-id'),
										);
										Bark.seller.showProjectInteractionLoader();
										setTimeout(function () {
											$('#notInterestedSurvey').modal('hide');
										}, 150);
										Bark.api(
											'seller/not-interested/',
											{
												pid: pid,
												rid: 1,
												other: otherReason,
											},
											function (data) {
												$('#surveyOther').val('');
												Bark.seller.hideProjectInteractionLoader();
											},
											function (err) {
												bugsnagClient.notify(err);
												Bark.seller.hideProjectInteractionLoader();
											},
											'POST',
											Bark.apiVersionHeader('v2'),
										);
									} else {
										$('#notInterestedSurvey .other-error').text(
											otherReason.length === 0
												? _t('seller_dashboard:not-interested.give-a-reason')
												: _t('seller_dashboard:not-interested.minimum-characters'),
										);
										$('#surveyOther').addClass('is-invalid').addClass('mb-2');
									}
								});
						});

					$('#notInterestedSurvey .js-reason-row')
						.off('click')
						.on('click', function () {
							var reasonId = parseInt($(this).data('rid'));
							var pid = parseInt($('.dashboard-project-details').attr('data-project-id'));
							var sid = Bark.seller.id;

							$(this).find('p').addClass('text-dark-blue').removeClass('text-grey-600');
							$(this)
								.find('span.bark-svg-icon')
								.removeClass('bsi-primary-grey-600')
								.addClass('bsi-primary-primary');

							setTimeout(function () {
								$('#notInterestedSurvey').modal('hide');
							}, 150);

							Bark.api(
								'seller/not-interested/',
								{
									pid: pid,
									rid: reasonId,
								},
								function (data) {
									Bark.seller.hideProjectInteractionLoader();
								},
								function (err) {
									bugsnagClient.notify(
										new Error(`Could not mark seller as not interested.`),
									);
									Bark.seller.hideProjectInteractionLoader();
								},
								'POST',
								Bark.apiVersionHeader('v2'),
							);
						});
				}
				//If they click outside of the modal and it is open we want to hide the filter and reset to what it was when it was opened.
				//Basically discard changes
				$(document).click(function (event) {
					var target = $(event.target);
					if (
						!target.closest('.modal-content').length &&
						$('body').hasClass('modal-open')
					) {
						Bark.seller.hideFilterPanelAndResetValues();
					}
				});

				$(document).keyup(function (e) {
					if (e.key === 'Escape') {
						if ($('body').hasClass('modal-open')) {
							Bark.seller.hideFilterPanelAndResetValues();
						}
					}
				});

				$('#dashboard-sidebar-modal').on('shown.bs.modal', function (e) {
					if (Bark.seller.isMobile || Bark.seller.isTablet) {
						$(this).find('.modal-content').animate({ scrollTop: 0 });
					}
					tracking('seller-leads-dashboard-&-responses', 'Filters - view', {
						pagename: window.location.pathname,
					});
				});

				$('#dashboard-sidebar-modal').on('hide.bs.modal', function (e) {
					$('#dashboard-sidebar-modal .collapse')
						.not('#collapse-keyword, #collapseOne')
						.each(function (index, collapse) {
							$(collapse).collapse('hide');
						});
				});

				//Bark.seller.debug(Bark.seller.id);
				Bark.seller.debug('- bindEvents()');

				// Make sure this SPA only starts on this page
				if (!$('.seller-dash-v2').length) {
					return false;
				}

				// Old design
				$('.last-updated .refresh-link').click(function (event) {
					event.preventDefault();
					Bark.seller.fetchRecords(true, true);
					tracking('seller-leads-dashboard-&-responses', 'Refresh dashboard', {
						pagename: window.location.pathname,
					});
				});

				$('.close-modal').click(function () {
					$(this).closest('.modal').modal('hide');
				});

				$('.welcomerow .howworks').on('click', function (e) {
					$('.howworks img.chevron').toggleClass('down');
					$('.howworkslist').slideToggle();
				});

				$('.welcomerow a.js-wdismiss').on('click', function (e) {
					var t = $(this);

					e.preventDefault();
					$.post('/sellers/api/banner-dismiss/', { type: t.data('type') });

					$('.welcomerow').slideUp(400, function () {
						Bark.seller.setPanelHeights();
					});
				});

				//Exclude our dropdown menu checkboxes because otherwise we will fetch records twice
				$('#dashboard-sidebar-modal').on('click', '.clear-all-filters', function (event) {
					event.preventDefault();
					Bark.seller.processClearFilters($(this).hasClass('override'));
				});

				/**
				 * Event listeners for new header
				 */

				if ($('#js-show-new-header').val() === '1') {
					$('#js-clear-filters-button').on('click', function () {
						Bark.seller.processClearFilters(true);
						Bark.seller.fetchRecords(true, true);
					});
					$('#js-edit-filters-button').click(function (event) {
						event.preventDefault();
						Bark.seller.showFilterPanel();
					});
				}

				// New refresh link
				$('#js-refresh-filtered-results').click(function (event) {
					event.preventDefault();
					const $refreshLink = $('#js-refresh-filtered-results');
					if ($refreshLink.is(':disabled')) {
						// No op
					} else {
						Bark.seller.fetchRecords(true, true);
						tracking('seller-leads-dashboard-&-responses', 'Refresh dashboard', {
							pagename: window.location.pathname,
						});
					}
					$refreshLink.prop('disabled', true).addClass('disabled-refresh-button');
					setTimeout(function () {
						$refreshLink.prop('disabled', false).removeClass('disabled-refresh-button');
					}, 3000); //To avoid button mashing
				});

				$('.clear-all-filters').click(function (event) {
					event.preventDefault();
					Bark.seller.processClearFilters($(this).hasClass('override'));
					tracking('seller-leads-dashboard-&-responses', 'Filter lead prefs - cleared', {
						pagename: window.location.pathname,
					});
				});

				$('.js-lead-prefs-from-lead-link').click(function (event) {
					tracking(
						'Experiment',
						'Seller-leads-dashboard-&-responses - clicked lead preferences from lead',
						{},
					);
				});

				$('.sidebar-content-wrapper').on(
					'click',
					'input:checkbox, input:radio',
					function () {
						var self = $(this);

						if (self.closest('.child-checkboxes').length > 0) {
							var wrapper = self.closest('.checkbox-list');
							var overrideCheckbox = wrapper.find(
								'.big-daddy-checkbox .custom-control-input',
							)[0];

							if (wrapper.find('input:checked').length > 0) {
								$(overrideCheckbox).prop('indeterminate', true);
							} else {
								$(overrideCheckbox).prop('indeterminate', false);
							}
						}

						if (self.closest('.grouped-radio').length > 0) {
							var radioGroup = self.closest('.grouped-radio');

							// We only want to include the radio filter if all or none of the children are selected
							if (
								radioGroup.find('input:checkbox').length !==
									radioGroup.find('input:checkbox:checked').length &&
								radioGroup.find('input:checkbox:checked').length !== 0
							) {
								radioGroup.find('.override').addClass('js-omit-from-filters');
							} else {
								radioGroup.find('.override').removeClass('js-omit-from-filters');
							}
						}

						if (self.hasClass('override')) {
							Bark.seller.selectAllChildren(self);
						}

						if (self.is(':radio')) {
							Bark.seller.selectAllRadioChildren(self);
						}

						Bark.seller.updateAccordianHeaderCounts();
						Bark.seller.setFiltersDirty();
						Bark.seller.delayFetch(false);
					},
				);

				$(document).on('click', '.allow-focus', function (e) {
					e.stopPropagation();
				});

				$('.sidebar-content-wrapper').on('change', 'select', function () {
					Bark.seller.delayFetch();
				});

				$('#dashboard-sidebar-modal .filter-results').on('click', function () {
					Bark.seller.dataFiltered = Bark.seller.filtersAreDirty;
					Bark.seller.sendFilterHeapEvents(Bark.seller.buildFilterQueryString());
					Bark.seller.fetchRecords(true, true);

					$('#dashboard-sidebar-modal').modal('hide');
				});

				$('.clear-input').click(function (event) {
					event.preventDefault();

					$(this).addClass('d-none');
					$(this).closest('.input-group').find('input').val('');

					Bark.seller.fetchFilteredRecords();
					//Bark.seller.hideApplyFiltersPanel();
				});

				$('#keyword-search').on('keyup paste', function (event) {
					if ($(this).val().length > 0) {
						$(this).closest('.input-group').find('.clear-input').removeClass('d-none');
					} else {
						$(this).closest('.input-group').find('.clear-input').addClass('d-none');
					}

					Bark.seller.setFiltersDirty();

					Bark.seller.delayFetch(false);
				});

				$('#sidebarCollapse').click(function (event) {
					if ($('#sidebar').hasClass('active')) {
						Bark.seller.hideFilterPanelAndResetValues();
					} else {
						Bark.seller.showFilterPanel();
					}
				});

				$('.filter-mobile').click(function (event) {
					event.preventDefault();
					Bark.seller.showFilterPanel();
				});

				// You're in My requests, you respond to a bark and the details change to orange, the top "My Response" becomes a link now
				$(document).on(
					'click',
					'.seller-dash-v2.leads .project-top-header.just-responded .list-type .for-my-responses',
					function () {
						location.href = _l('/sellers/my-barks/');
					},
				);
				$(document).on(
					'click',
					'.seller-dash-v2.not-interested .project-top-header.just-responded .list-type .for-my-responses',
					function () {
						location.href = _l('/sellers/my-barks/');
					},
				);

				$(document).on('change', '#project-status-select', function () {
					var projectId = $('.dashboard-project-details').attr('data-project-id');
					var projectStatusId = $(this).val();
					var showSellerHiredModal =
						projectStatusId == 3 && !$('#project-status-select').data('has-hired-data');

					Bark.api(
						'response/update-seller-status',
						{
							project_id: projectId,
							status_id: projectStatusId,
						},
						function (data) {
							if (showSellerHiredModal) {
								Bark.seller.resetHiredModal();
								$('#sellerHiredModal').modal('show');
							}
						},
						function (error) {
							console.log('failed', error);
						},
						'POST',
					);
				});

				$('.estimate-amount').on('keydown keyup change', function () {
					var $el = $('.estimate-value-input .input-group-text');
					if ($(this).val().length > 0) {
						$el.removeClass('text-grey-400');
					} else {
						$el.addClass('text-grey-400');
					}
				});

				// project open
				var eventHandled = false;
				const sellerEnquiriesBanner = $('#__rctSellerEnquiriesBanner');
				$(document).on('click', '.dashboard-projects-item', function () {
					if (!eventHandled) {
						// To prevent a ghost click on devices that render both click and touchstart. touchstart is needed.
						eventHandled = true;
						setTimeout(function () {
							eventHandled = false;
						}, 300);

						if (Bark.seller.interactingWithProject) {
							return false;
						}
						var projectId = $(this).attr('data-project-id');

						//hide the response header if we click on another project
						if (!$(this).hasClass('selected')) {
							$('.my-response-header').hide();
						}

						Bark.seller.renderProject(projectId, true);

						$('.right-panel').animate({ scrollTop: 0 }, 100);
						if (Bark.seller.isMobile) {
							if (sellerEnquiriesBanner.length) {
								sellerEnquiriesBanner.addClass('d-none');
							}

							if (
								$('#__rctSellerEnquiriesBanner').length &&
								!!$('#__rctSellerEnquiriesBanner').html().length
							) {
								$('.bark-header.sticky-top').css(
									'top',
									$('#__rctSellerEnquiriesBanner').height(),
								);
							}
						}
					}
				});

				$(window).resize(function () {
					if (!Bark.seller.isMobile) {
						sellerEnquiriesBanner.removeClass('d-none');
					} else {
						if (
							$('#__rctSellerEnquiriesBanner').length &&
							!!$('#__rctSellerEnquiriesBanner').html().length
						) {
							$('#navbarSupportedContent').css('top', $('#bark-header').height());
						}
					}
				});

				// project close
				$('.seller-dash-v2 .back-to-list a').click(function (e) {
					e.preventDefault();
					let barkHeader = $('.bark-header');
					sellerEnquiriesBanner.removeClass('d-none');
					if (
						$('#__rctSellerEnquiriesBanner').length &&
						!!$('#__rctSellerEnquiriesBanner').html().length
					) {
						// if review banner is present on mobile remove stickiness
						if (
							$('#__rctSellerEnquiriesBanner > .tw-sticky >[aria-label="review-banner"]')
								.length
						) {
							barkHeader.css('top', 0);
						} else {
							barkHeader.css('top', $('#__rctSellerEnquiriesBanner').height());
						}
					}
					if (Bark.seller.interactingWithProject) {
						return false;
					}
					Bark.seller.hideProject();

					if (Bark.seller.isMobile) {
						// Set the URL hash, which will allow the user to go back to the lead if they click the back button
						Bark.setPageState({ projectId: 0 }, null, true);
						Bark.seller.processNoRecords();
					}
				});

				// project close from infobox
				$(document).on('click', 'a.infobox-back-to-list', function (e) {
					$('.seller-dash-v2 .back-to-list a').trigger('click');
				});

				// Load more records
				$('.seller-dash-v2 .dashboard-projects .load-more').click(function () {
					Bark.seller.fetchRecords();
				});

				// not interested
				$('.seller-dash-v2 .calls-to-action .not-interested').click(function () {
					var projectId = $('.dashboard-project-details').attr('data-project-id');
					if (!projectId) {
						return false;
					}

					if (
						$('.seller-dash-v2 .calls-to-action .not-interested').hasClass('shortlisted')
					) {
						$('#confirmNotInterested').modal('show');
					} else {
						Bark.seller.processNotInterested(projectId);
					}
				});

				// not interested undo
				$('.seller-dash-v2 .calls-to-action .undo-not-interested').click(function () {
					var projectId = $('.dashboard-project-details').attr('data-project-id');
					if (!projectId) {
						return false;
					}

					Bark.seller.processUndoNotInterested(projectId);
				});

				// cancel decline request
				$(
					'.seller-dash-v2 .js-decline-request-confirmation .cancel-decline-request',
				).click(function () {
					$('#confirmNotInterested').modal('hide');
				});

				// Cancel setting up one-click response
				$('#oneClickDashboardSetup .cancel-one-click-setup').click(function () {
					$('.seller-dash-v2 .js-one-click-setup').slideUp({ duration: 200 });
				});

				// decline request / not interested for shortlisted
				$('#confirmNotInterested .decline-request').click(function () {
					$('#confirmNotInterested').modal('hide');
					var projectId = $('.dashboard-project-details').attr('data-project-id');
					Bark.seller.processNotInterested(projectId);
				});

				// Search submit
				$('.seller-dash-v2 .dashboard-projects-filters .search-keyword input').keyup(
					function (e) {
						e.preventDefault();
						if (e.keyCode === Bark.consts.KEY_CODE_ENTER) {
							Bark.seller.submitProjectSearchForm();
						}
					},
				);

				// Submit search
				$('.seller-dash-v2 .form-search-submit, .seller-dash-v2 .js-submit-form').click(
					function () {
						Bark.seller.debug('Form submit click');
						Bark.seller.submitProjectSearchForm();
					},
				);

				// Reset search form
				$('.seller-dash-v2 .js-reset-form').click(function () {
					Bark.seller.resetProjectSearchForm();
				});

				// Extra search filters - show / hide
				$('.seller-dash-v2 .dashboard-projects-filters .filter').click(function () {
					if ($('.seller-dash-v2 .search-extras').is(':visible')) {
						Bark.seller.closeSearchFormExtras();
					} else {
						Bark.seller.openSearchFormExtras();
					}
				});

				// Location autocomplete
				$('.dashboard-projects-filters .field-location').streamComplete({
					src: '/json/address-autocomplete-lat-lng/',
					delay: 50,
					onsearch: function (results) {
						var elem = $('.dashboard-projects-filters .field-location');

						if (results.length === 1) {
							elem.data({ value: results[0].id });
							elem.data({ resultslist: null });
						} else {
							elem.data({ value: '' });
						}

						if (results.length > 1) {
							elem.data({ resultslist: results });
						}
					},
				});

				// Hide .no-location-found / .no-match errors if attempting to correct it
				$('.dashboard-projects-filters .field-location').focus(function () {
					$(
						'.seller-dash-v2 .no-location-found, .dashboard-projects .no-match, .dashboard-projects .did-you-mean',
					).slideUp({ duration: 100 });
				});

				// Select location suggestion if not selected
				$('.seller-dash-v2 .did-you-mean select').change(function () {
					var id = $('.seller-dash-v2 .did-you-mean select').val();
					var val = $('.seller-dash-v2 .did-you-mean select option:selected').text();
					$('.seller-dash-v2 .dashboard-projects-filters .field-location').streamComplete(
						'setValue',
						{ id: id, value: val },
					);
					$('.seller-dash-v2 .did-you-mean').slideUp({ duration: 100 });
					Bark.seller.submitProjectSearchForm();
				});

				// Submit your details / Send custom message
				$(document).on(
					'click',
					'.seller-dash-v2 .submit-your-details, .seller-dash-v2 .send-custom-message',
					function () {
						if (!$(this).hasClass('one-click-response')) {
							if (shouldShowLeadDetailsNewDesign()) {
								scrollToTopOnMobile();
							}
							Bark.seller.processSubmitYourDetails();
						}
					},
				);

				// One click response
				$(document).on('click', '.seller-dash-v2 .one-click-response', function () {
					if (shouldShowLeadDetailsNewDesign()) {
						scrollToTopOnMobile();
					}
					Bark.seller.processSubmitYourDetails(true);
				});

				$(document).on('click', '.seller-dash-v2 .js-edit-default', function () {
					Bark.seller.showCampaignEditor();
				});

				$(document).on('click', '.seller-dash-v2 .js-hide-template-builder', function () {
					Bark.seller.hideCampaignEditor();
				});

				$(document).on('click', '.seller-dash-v2 .js-send-custom-template', function () {
					Bract.getHtml(function (response) {
						console.info(response);
						Bark.seller.hideCampaignEditor();
					});
				});

				// Hide error if quote type selected
				$('.seller-dash-v2 .respond-message-estimate .estimate-type').change(function () {
					if ($('.seller-dash-v2 .respond-message-estimate .estimate-type').val() != '') {
						$(
							'.seller-dash-v2 .respond-message-estimate .error-choose-quote-type',
						).slideUp({ duration: 200 });
						$('.seller-dash-v2 .respond-message-estimate .estimate-amount').removeClass(
							'is-error',
						);
					}
				});

				// Hide error if quote entered
				$('.seller-dash-v2 .respond-message-estimate .estimate-amount').keyup(
					function () {
						if (
							$('.seller-dash-v2 .respond-message-estimate .estimate-amount').val() != ''
						) {
							$(
								'.seller-dash-v2 .respond-message-estimate .error-enter-estimate',
							).slideUp({ duration: 200 });
							$('.seller-dash-v2 .respond-message-estimate .estimate-amount').removeClass(
								'is-error',
							);
						}
					},
				);

				// Hide error if typed message longer than 30 characters
				$(
					'#barkSendEstimateModal .respond-message-estimate .respond-textarea-field',
				).keyup(function () {
					var msg = $(
						'#barkSendEstimateModal .respond-message-estimate .respond-textarea-field',
					).val();
					if (msg && msg.length > 30) {
						$(
							'#barkSendEstimateModal .respond-message-estimate .error-write-message',
						).slideUp({ duration: 200 });
						$(
							'#barkSendEstimateModal .respond-message-estimate .respond-textarea-field',
						).removeClass('is-error');
					}
				});

				// Send message later
				$('.respond-message-estimate .send-later').click(function (e) {
					e.preventDefault();
					Bark.seller.processSendMessageLater();
				});

				// Show filters
				$('.seller-dash-v2 .dashboard-projects-filters').show();

				// Close starter pack modal
				$(`${Bark.seller.starterPackModalSelector} .close-response-upsell-modal`).click(
					function () {
						$(Bark.seller.starterPackModalSelector).modal('hide');
					},
				);

				$(Bark.seller.starterPackModalSelector).on('show.bs.modal', function (e) {
					if (Bark.seller.isRespondingViaEmail()) {
						Bark.recordHotjarEvent('starter-pack-modal-from-email');
					} else {
						Bark.recordHotjarEvent('starter-pack-modal-from-leads');
					}
				});

				// SellerViewBuyerPhoneVerificationExperiment: show the explainer modal
				$(document).on('click', '.verified-phone-button', () => {
					modalManager.set(
						'Verified phone numbers',
						null,
						verifiedPhoneNumberTemplate({}),
						[],
						['verified-phone-modal', 'reduced-padding-header'],
					);
					Bark.logEvent(
						'show-buyer-phone-verified-modal',
						'click',
						'phone-verified-leads',
					);
					return false;
				});

				// You're nearly there 'Buy Now' click - shows card payment modal to buy credits or sends a response
				$(`${Bark.seller.starterPackModalSelector} .purchase-upsell-welcome-offer`).bind(
					'click',
					function (e) {
						if (isUSTaxComplianceActive() && !Bark.seller.isUSTaxCompliant) {
							Bark.seller.lastPackModalSelector =
								'#' + $(this).closest('.modal').attr('id');
							$(Bark.seller.lastPackModalSelector).modal('hide');
							let btn = $(this);
							let $callback = function () {
								Bark.seller.initPurchaseStarterPack(btn);
							};
							Bark.seller.openUSTaxCompliantModal();
							Bark.seller.bindUSTaxComplianceModalNextAction($callback);
						} else {
							Bark.seller.initPurchaseStarterPack($(this));
						}
					},
				);

				// Close credit packs modal
				$('.credit-pack-selection .close-respond-creditpack-upsell-modal')
					.off('click.close')
					.on('click.close', function () {
						$(this).closest('.modal').modal('hide');
					});

				// Click Buy Now on the credits upgrade modal
				$('#creditPurchaseSuccessNew .purchase-credit-pack').click(function () {
					$('#creditPurchaseSuccessNew .purchase-credit-pack').prop('disabled', true);
					$('#creditPurchaseSuccessNew').modal('hide');
					Bark.seller.processBuyUpgradeCredits();
				});

				// Click "Skip - I don't want the special offer" on the upgrade offer modal
				$('#creditPurchaseSuccessNew .js-credit-pack-upgrade-declined').click(
					function () {
						$('#creditPurchaseSuccessNew').modal('hide');
						$.post('/api/event-log/', {
							event_category:
								'credit_pack_upgrade_plan' + $(this).data('upgrade-opportunity'),
							event_name: 'click',
							event_id: 'skip',
						});
					},
				);

				// Click Upgrade to Pro
				$('#creditPurchaseSuccessNew .purchase-elite-pro').click(function () {
					var button = $(this);
					button
						.addClass('loading')
						.html('<span class="spinner-border spinner-border-sm"></span>')
						.prop('disabled', true);

					$.post(
						'/sellers/pro/subscribe/',
						{
							pid: $(this).data('elite-plan-id'),
							upgrade_source: $('#plan_upgrade_source').val(),
						},
						function (data) {
							if (data.success) {
								button.closest('.modal').modal('hide');
								$('#eliteProSuccess').modal('show');
							} else {
								Bark.alertModal(
									_t('seller_dashboard:elite-subscription.error-message'),
									null,
									null,
								);
								button.removeClass('loading').html('Upgrade now').prop('disabled', false);
							}
						},
						'json',
					).fail(function () {
						Bark.alertModal(
							_t('seller_dashboard:elite-subscription.error-message'),
							null,
							null,
						);
						button.removeClass('loading').html('Upgrade now').prop('disabled', false);
					});
				});

				/**
				 * @param {Object} options
				 * @param {Number} options.totalPack Total price of the pack, as pounds, ex VAT
				 * @param {Number} options.nCredits Number of credits being purchased
				 * @param {Number} options.planId
				 */
				const setCreditPackSelectionPaymentRequest = (options) => {
					const { totalPack, nCredits, planId } = options;

					Bark.seller.currentPaymentRequest = {
						label: _t('seller_dashboard:card-modal.purchase-details-alt.header', {
							total_credits: nCredits,
						}),
						amount: totalPack * 100,
						userId: getUserIdFromModal(),
						cpId: planId,
						isUpsell: false,
						projectId: Bark.seller.currentlyShownProjectId,
						packType: 'credit-pack',
						onWalletPaymentComplete: async (
							_,
							__,
							initPaymentIntentResponse,
							___,
							paymentMethodType,
						) => {
							Bark.showLoading();
							const isOneClick =
								Bark.seller.lastAttemptedResponseType === 'oneClickResponse';
							purchasing = false;

							Bark.seller.lastPurchasedPackId = planId;
							Bark.seller.mostRecentCreditsPurchased = initPaymentIntentResponse.nCredits;

							if (Bark.seller.currentlyShownProjectId !== false) {
								Bark.seller.submitYourDetailsCall(
									Bark.seller.currentlyShownProjectId,
									isOneClick,
								);
							}

							if (Number(planId) === 1 || planId === 'spack') {
								Bark.seller.trackStarterPackPurchase();
							}

							if (paymentMethodType !== 'paypal' || !Bark.seller.setAutoTopup) {
								// Do not allow setting of ATU with Paypal, as it breaks flows downstream
								// Allow a seller to turn off ATU if they're paying with Paypal
								if (Bark.seller.setAutoTopup !== undefined) {
									await Bark.seller.saveAutoTopupStatus(Bark.seller.setAutoTopup);
								}
							} else if (Bark.seller.setAutoTopup) {
								tracking('alternative-payments', 'Cannot enable auto-topup with Paypal');
							}

							Bark.hideLoading();
						},
					};
				};

				// credit modal variation
				$('.credit-pack-selection .credit-upsell-topup-submit').click(function (e) {
					e.preventDefault();
					var button = $(this);
					var originalText = button.text();
					button.addClass('loading').prop('disabled', true);
					var $container = $(this).closest('.bark-modal');

					var modalId = '#' + $container.prop('id');
					var $selectedPack = $container.find(
						'.credit-pack-option-container.selected-pack',
					);

					var packDiscount = $selectedPack.data('discount');
					var nCredits = $selectedPack.data('ncredits');

					var planId = $selectedPack.data('plan-id');
					var salesTeamQuoteId = $selectedPack.data('sales-team-quote-id');
					Bark.seller.paymentType = 'pack_' + planId;

					Bark.seller.salesTeamQuoteId = salesTeamQuoteId;

					const project = Bark.seller.getProjectDataById(
						Bark.seller.currentlyShownProjectId,
					);
					const ppc = project.price_per_credit * ((100 - packDiscount) / 100);
					const totalPack = (nCredits * ppc).toFixed(2);

					Bark.seller.currentPaymentRequest = {};

					var eliteProUpgrade =
						$container.find('.credit-upsell-topup-elite-pro input:checked').length > 0;
					if (eliteProUpgrade) {
						$.post(
							'/sellers/pro/subscribe/',
							{
								pid: $container.find('input[name="elite_plan_id"]').val(),
								upgrade_source: $container
									.find('input[name="elite_upgrade_source"]')
									.val(),
							},
							function (data) {
								if (data.success) {
									$('#elite-pro-upgrade-banner-dashboard').hide();
									$('#creditPurchaseSuccessNew .top-up-pro-upgrade').show();
									Bark.seller.creditPackUpsellModalSubmit(
										modalId,
										planId,
										packDiscount,
										nCredits,
									);
								} else {
									Bark.alertModal(
										_t('seller_dashboard:elite-subscription.error-message'),
										null,
										null,
									);
									button.removeClass('loading').prop('disabled', false);
								}
							},
							'json',
						).fail(function () {
							Bark.alertModal(
								_t('seller_dashboard:elite-subscription.error-message'),
								null,
								null,
							);
							button.removeClass('loading').prop('disabled', false);
						});
					} else {
						//card does not exist and multi payment modal is available
						if (Bark.seller.isMultiPaymentModalAvailable() && !Bark.seller.cardLast4) {
							let button = $(this);
							button.addClass('loading').prop('disabled', true);
							Bark.seller.lastPackModalSelector = '#sellerResponseCreditPackUpsell1';
							let $container = $(Bark.seller.lastPackModalSelector);

							let $selectedPack = $container.find(
								'.credit-pack-option-container.selected-pack',
							);

							setCreditPackSelectionPaymentRequest({ totalPack, nCredits, planId });

							packDiscount = $selectedPack.data('discountprice');
							nCredits = $selectedPack.data('ncredits');
							planId = $selectedPack.data('plan-id');
							$container.modal('hide');
							Bark.seller.$multiPaymentModal.data({
								planId: planId,
								credits: nCredits,
								packDiscount: packDiscount,
								defaultPaymentMethod: Bark.seller.cardLast4 ? SAVED_CARD : NEW_CARD,
								'pack-type': 'credit-pack',
							});

							Bark.seller.showMultiplePaymentEntryForm({
								...Bark.seller.generateMultiPaymentModalOptions(
									true,
									nCredits,
									parseFloat(nCredits) > 0 ? packDiscount / nCredits : null,
									packDiscount,
								),
								enableWallets: true,
								paymentOptions: {
									label: _t('seller_dashboard:card-modal.purchase-details-alt.header', {
										total_credits: nCredits,
									}),
									amount: packDiscount * 100,
									cpId: planId,
									userId: getUserIdFromModal(),
									isUpsell: false,
									projectId: Bark.seller.currentlyShownProjectId,
									packType: 'credit-pack',
									onWalletPaymentComplete: async (
										_,
										__,
										___,
										____,
										paymentMethodType,
									) => {
										Bark.showLoading();

										if (Bark.seller.currentlyShownProjectId) {
											const isOneClick =
												Bark.seller.lastAttemptedResponseType === 'oneClickResponse';
											Bark.seller.submitYourDetailsCall(
												Bark.seller.currentlyShownProjectId,
												isOneClick,
											);
										}

										if (paymentMethodType !== 'paypal' || !Bark.seller.setAutoTopup) {
											// Do not allow setting of ATU with Paypal, as it breaks flows downstream
											// Allow a seller to turn off ATU if they're paying with Paypal
											if (Bark.seller.setAutoTopup !== undefined) {
												await Bark.seller.saveAutoTopupStatus(Bark.seller.setAutoTopup);
											}
										} else if (Bark.seller.setAutoTopup) {
											tracking(
												'alternative-payments',
												'Cannot enable auto-topup with Paypal',
											);
										}

										Bark.hideLoading();
										Bark.seller.trackStarterPackPurchase();
									},
								},
							});
						} else {
							if (isUSTaxComplianceActive() && !Bark.seller.isUSTaxCompliant) {
								Bark.seller.lastPackModalSelector =
									'#' + $(this).closest('.modal').attr('id');
								$(Bark.seller.lastPackModalSelector).modal('hide');
								let $callback = function () {
									setCreditPackSelectionPaymentRequest({ totalPack, nCredits, planId });
									Bark.seller.creditPackUpsellModalSubmit(
										modalId,
										planId,
										packDiscount,
										nCredits,
										salesTeamQuoteId,
									);
								};
								Bark.seller.openUSTaxCompliantModal();
								Bark.seller.bindUSTaxComplianceModalNextAction($callback);
							} else {
								setCreditPackSelectionPaymentRequest({ totalPack, nCredits, planId });
								Bark.seller.creditPackUpsellModalSubmit(
									modalId,
									planId,
									packDiscount,
									nCredits,
									salesTeamQuoteId,
								);
							}
						}
					}
				});

				// credit model variation
				$('.credit-pack-selection .credit-pack-option-container').click(function (e) {
					$(this)
						.closest('.upsell-variation')
						.find('.selected-pack')
						.removeClass('selected-pack');
					$(this)
						.closest('.upsell-variation')
						.find('input.credit-pack-selector')
						.prop('checked', false);
					$(this).closest('.credit-pack-option-container').addClass('selected-pack');
					$(this)
						.closest('.credit-pack-option-container')
						.find('input.credit-pack-selector')
						.prop('checked', true);
				});

				$('.elite-pro-tickbox label').click(function (e) {
					e.preventDefault();
					var $input = $(this).closest('.elite-pro-tickbox').find('input');
					if ($input.prop('checked')) {
						$input.prop('checked', false);
						$(this.closest('.credit-upsell-topup-elite-pro')).removeClass(
							'upgrade-selected',
						);
					} else {
						$input.prop('checked', true);
						$(this.closest('.credit-upsell-topup-elite-pro')).addClass(
							'upgrade-selected',
						);
					}
				});

				// Banner dismiss log
				$(document).on('close.fndtn.alert-box', function (event) {
					Bark.seller.sendBannerDismiss(event.target.getAttribute('id'));
				});

				// Banner dismiss log
				$('.seller-dash-v2 .banner .close').click(function () {
					Bark.seller.sendBannerDismiss($(this).parent().attr('id'));
				});

				// Open the 'what is bark' modal
				$('a.what-are-credits').on('click', function (e) {
					var t = $(this);
					var s = t.data('source');
					//window.whatIsBarkIndex = 1;
					//window.whatIsBarkShowAsBack = true;
					window.ga?.('send', 'event', 'dashboard', 'button click', 'what are credits');
					location.hash = '#what-are-credits';

					//currentModalSelector = '#'+s;
					$(this).closest('.bark-modal').modal('hide');
					$('#whatIsBarkModal').modal('show');
					e.preventDefault();
				});
				$(document).on('click', 'a.credit-back-guarantee', function (e) {
					window.ga?.(
						'send',
						'event',
						'dashboard',
						'link click',
						'credit back guarantee',
					);
					location.hash = '#our-guarantee';

					//currentModalSelector = '#'+s;
					$(this).closest('.bark-modal').modal('hide');
					$('#whatIsBarkModal').modal('show');
					e.preventDefault();
				});

				// Close the what is bark modal
				$('#whatIsBarkModal .close-modal-cross').on('click', function (e) {
					window.ga?.(
						'send',
						'event',
						'dashboard',
						'button click',
						'what are credits - close',
					);
					//window.whatIsBarkShowAsBack = false;
					if (Bark.seller.currentModalSelector) {
						$(Bark.seller.currentModalSelector).modal('show');
					}
				});

				// Click I've been hired button
				$('.seller-dash-v2 .have-been-hired').click(function () {
					if ($('.seller-dash-v2 .have-been-hired-form').is(':visible')) {
						Bark.seller.hideHaveBeenHiredForm();
					} else {
						Bark.seller.showHaveBeenHiredForm();
					}
				});

				// Have been hired 'I dont want to disclose...' is checked
				$('.seller-dash-v2 .have-been-hired-form #cant_disclose').click(function () {
					if ($('.seller-dash-v2 .have-been-hired-form #cant_disclose').is(':checked')) {
						$('.seller-dash-v2 .have-been-hired-form .estimate-amount')
							.val('')
							.prop('disabled', true)
							.addClass('disabled');
						$('.seller-dash-v2 .have-been-hired-form .estimate-type')
							.val('')
							.prop('disabled', true)
							.addClass('disabled');
					} else {
						$('.seller-dash-v2 .have-been-hired-form .estimate-amount')
							.prop('disabled', false)
							.removeClass('disabled');
						$('.seller-dash-v2 .have-been-hired-form .estimate-type')
							.prop('disabled', false)
							.removeClass('disabled');
					}
				});

				// Click Cancel on the 'Have been hired' form
				$('.seller-dash-v2 .have-been-hired-form .js-cancel-have-been-hired').click(
					function () {
						Bark.seller.hideHaveBeenHiredForm();
					},
				);

				// Click submit on the Have-been-hired form
				$('.seller-dash-v2 .js-have-been-hired-button').click(function () {
					Bark.seller.processHaveBeenHiredForm();
				});

				// Hide error if quote type selected
				$('.seller-dash-v2 .have-been-hired-form .estimate-type').change(function () {
					if ($('.seller-dash-v2 .have-been-hired-form .estimate-type').val() != '') {
						$('.seller-dash-v2 .have-been-hired-form .error-choose-quote-type').slideUp({
							duration: 200,
						});
						$('.seller-dash-v2 .have-been-hired-form .estimate-amount').removeClass(
							'is-error',
						);
					}
				});

				// Hide error if quote entered
				$('.seller-dash-v2 .have-been-hired-form .estimate-amount').keyup(function () {
					if ($('.seller-dash-v2 .have-been-hired-form .estimate-amount').val() != '') {
						$('.seller-dash-v2 .have-been-hired-form .error-enter-estimate').slideUp({
							duration: 200,
						});
						$('.seller-dash-v2 .have-been-hired-form .estimate-amount').removeClass(
							'is-error',
						);
					}

					if (
						parseInt($('.seller-dash-v2 .have-been-hired-form .estimate-amount').val()) >
						1
					) {
						$(
							'.seller-dash-v2 .have-been-hired-form .error-enter-positive-estimate',
						).slideUp({ duration: 200 });
						$('.seller-dash-v2 .have-been-hired-form .estimate-amount').removeClass(
							'is-error',
						);
					}
				});

				$('.seller-dash-v2 .js-open-confirm-email').click(function () {
					$('#fix-email-issue').foundation('reveal', 'open');
				});

				$('#fix-email-address-confirm').click(function (event) {
					event.preventDefault();
					$('#fix-email-address-error').hide();
					var postData = { email: $('#fix-email-address-email').val() };
					$.post(
						'/seller_fix_email/',
						postData,
						function (data) {
							if (data.success) {
								$('#fix-email-issue-done').foundation('reveal', 'open');
								$('.js-cannot-notify-seller').slideUp({ duration: 100 });
							} else {
								if (data.errors.length && data.errors.hasOwnProperty('general')) {
									$('#fix-email-address-error span').text(data.errors.general);
								} else if (data.errors.length && data.errors.hasOwnProperty('email')) {
									$('#fix-email-address-error span').text(data.errors.email);
								} else {
									$('#fix-email-address-error span').text(
										_t('seller_dashboard:update-email.error-message'),
									);
								}
								$('#fix-email-address-error').slideDown({ duration: 100 });
							}
						},
						'json',
					);
				});

				// Close a banner
				$('.seller-dash-v2 .banner .close').click(function () {
					$(this)
						.parent()
						.slideUp({
							duration: 200,
							complete: function () {
								Bark.seller.showHideBanners();
							},
						});
				});

				//button appears in
				$('#saved-card-payment .js-change-card')
					.off('click.change-card')
					.on('click.change-card', function (e) {
						e.preventDefault();
						let button = $(this);
						button.addClass('loading').prop('disabled', true);
						let $container = $(this).closest('.bark-modal');
						let $selectedPack = $container.find(
							'.credit-pack-option-container.selected-pack',
						);
						let discount = $selectedPack.data('discountprice');
						let totalCredits = $selectedPack.data('ncredits');
						let ppc = $selectedPack.data('price-per-credit');
						let planId = $selectedPack.data('plan-id');
						let totalPack = (totalCredits * ppc).toFixed(2);

						Bark.seller.currentPaymentRequest = {};

						Bark.seller.paymentType = 'pack_' + planId;

						if (!$selectedPack.length) {
							$selectedPack = $(this)
								.closest('.bark-modal')
								.find('.purchase-credit-pack');
							discount = $selectedPack.data('discountprice');
							ppc = $selectedPack.data('pricePerCredit');
							totalCredits = $selectedPack.data('ncredits');
						}

						if (
							$selectedPack.data('pack-id') ??
							$selectedPack.data('plan-id') === 'spack'
						) {
							Bark.seller.paymentType = '';
						}
						Bark.seller.lastPackModalSelector =
							'#' + $(this).closest('.modal').closest('.modal').attr('id');
						$(this).closest('.modal').modal('hide');

						Bark.seller.$multiPaymentModal.data({
							planId: $selectedPack.data('pack-id') ?? $selectedPack.data('plan-id'),
							credits: totalCredits,
							packDiscount: discount,
						});
						Bark.seller.generateMultiPaymentModalOptions(false, totalCredits, null, null);
						Bark.seller.showMultiplePaymentEntryForm();
					});

				$('.js-reenter-card-details').click(function () {
					const enableWallets = Boolean(
						Object.keys(Bark.seller.currentPaymentRequest).length,
					);

					$(this).closest('.modal').modal('hide');
					Bark.seller.lastPackModalSelector = '#purchaseCreditsError';

					if (Bark.seller.isMultiPaymentModalAvailable()) {
						let { credits } = Bark.seller.$multiPaymentModal.data();
						Bark.seller.showMultiplePaymentEntryForm({
							...Bark.seller.generateMultiPaymentModalOptions(false, credits, null, null),
							enableWallets,
							paymentOptions: Bark.seller.currentPaymentRequest,
						});
					} else {
						Bark.seller.showCardEntryForm({
							...Bark.seller.generateCardModalOptions(false),
							enableWallets,
							paymentOptions: Bark.seller.currentPaymentRequest,
						});
					}
				});

				$('.js-close-purchase-credits-error-modal').click(function () {
					$('#purchaseCreditsError').modal('hide');
				});

				$('.js-dismiss-banner').click(function () {
					$('#service-area-changes-notification').slideUp();
					$.post('/sellers/api/banner-dismiss/', {
						type: 'service-area-forced-changes-notification',
					});
				});

				$('.js-toggle-banner-toggle').click(function () {
					$(this).parents('.js-banner-container').toggleClass('expanded');
					$(this)
						.parents('.js-banner-container')
						.find('.js-toggle-banner-content')
						.slideToggle(1000);
				});

				// Hired modal
				$('#sellerHiredModal #hired-amount').on('keyup blur', function () {
					$('#sellerHiredModal .hired-amount-value-input')[
						($(this).val().length ? 'add' : 'remove') + 'Class'
					]('has-value');
				});
				$('#sellerHiredModal #hired-no-disclose').on('click', function () {
					var disabled = $(this).prop('checked');
					$('#sellerHiredModal #hired-amount, #sellerHiredModal #hired-type').prop(
						'disabled',
						disabled,
					);
					$('#sellerHiredModal .hired-amount')[(disabled ? 'add' : 'remove') + 'Class'](
						'disabled',
					);
				});
				$('#sellerHiredModal form').on('submit', function (e) {
					Bark.seller.processHiredModal();
					e.preventDefault();
					return false;
				});

				// Infobox
				$(document).on('click', '.show-hide-link', function () {
					Bark.api(
						'/banner/dismiss/',
						{ type: 'infobox' },
						function (data) {},
						function (error) {
							console.error(error);
						},
						'POST',
						Bark.apiVersionHeader('v2'),
					);
					$('.infobox-container').fadeOut(400, function () {
						Bark.seller.setViewport(); // to adjust the height of the projects div
					});
					return false;
				});

				$(document).on('change', '.response-contact-action', function (e) {
					e.preventDefault();
					Bark.seller.updateResponseContact(
						$(this).data('response-id'),
						$(this).data('project-id'),
						$(this).data('response-contact-type'),
						$(this).is(':checked'),
					);
				});

				$(document).on('change keyup', '.notes-input', function (e) {
					$('.submit-notes')
						.removeClass('btn-success')
						.addClass('btn-primary')
						.prop('disabled', false)
						.find('.submit-text')
						.text(_t('seller_dashboard:update-button'));
				});

				$(document).on('click', '.submit-notes', function (e) {
					e.preventDefault();
					Bark.seller.updateResponseNote(
						$(this).data('response-id'),
						$(this).data('project-id'),
						$('.notes-input').val(),
					);
				});

				$(document).on('change', '#project-status-select', function (e) {
					Bark.seller.updateStatusSelectColour();
				});

				// Handle state changes
				$(window).on('popstate', function (e) {
					if (!Bark.seller.isMobile) {
						return true;
					}
					var state = (e.originalEvent || e).state;
					if (state && state.projectId) {
						Bark.seller.showProject(state.projectId);
					} else {
						Bark.seller.hideProject();
					}
				});

				// Set the page
				if (Bark.seller.page) {
					$('.seller-dash-v2')
						.removeClass()
						.addClass('seller-dash-v2')
						.addClass(Bark.seller.page);
				}

				$(document).on(
					'click',
					'.dashboard-projects-item, .dashboard-project-details',
					function () {
						var projectId = $(this).data('project-id');
						Bark.seller.projectViewed(projectId);
					},
				);

				$('.right-panel').on('scroll', function () {
					var projectId = $(this).find('.dashboard-project-details').data('project-id');
					Bark.seller.projectViewed(projectId);

					if (shouldShowMobileStickyHeaderRct()) {
						toggleMobileStickyHeaderView();
					}
				});

				if (navigator.platform === 'iPhone' || navigator.platform === 'iPad') {
					// remind iOS to change the active nav item when you tap it
					$(document).on('touchstart', '.lead-action-options .nav-item', function (e) {});
				}

				$(document).on('click', '.buy-project-bundle', function (e) {
					e.preventDefault();
					if (Bark.seller.testing) {
						Bark.alertModal('Testing mode active');
					} else {
						$('.buy-project-bundle').addClass('submitting').css('pointer-events', 'none');
						$('.dismiss-project-bundle').css('pointer-events', 'none');
						Bark.seller.preProjectBundlePurchase();
					}
				});

				$('.modal .confirm-buy-credits-project-bundle').click(function () {
					$('.confirm-buy-credits-project-bundle')
						.addClass('submit-waiting')
						.css('pointer-events', 'none');
					$('.cancel-buy-credits-project-bundle').css('pointer-events', 'none');
					Bark.seller.acceptedBundleCreditPurchase = true;
					Bark.seller.processProjectBundlePurchase();
				});

				$('#projectBundleNotEnoughCredits .close, .cancel-buy-credits-project-bundle').on(
					'click',
					function () {
						Bark.seller.removeBundlePurchaseWaiting();
					},
				);

				if (QS.hasKey('berr')) {
					var bundleErrorType = QS.getValue('berr');
					var errorMessage = _t('seller_dashboard:bundle.error-message');
					if (bundleErrorType === 'prelaunch') {
						errorMessage = _t('seller_dashboard:bundle.prelaunch-error-message');
					}
					if (bundleErrorType === 'expired') {
						errorMessage = _t('seller_dashboard:bundle.expired-error-message');
					}
					Bark.alertModal(errorMessage);
				}

				Bark.seller.trackAccordionHeaderClick($('.js-keyword-filter-header'), {
					id: 'keyword',
					title: _t('seller_dashboard:dashboard-filters.keyword-title'),
				});

				setTimeout(function () {
					// set the viewport and sizes
					$(window).resize(function () {
						Bark.seller.setViewport();
					});
					Bark.seller.setViewport();
				}, 200);

				if ($('.ghg-tooltip-container')) {
					let ghgTooltipTimer;

					$('.ghg-tooltip-container')
						.off('mouseenter.tooltip,focus.tooltip')
						.on('mouseenter.tooltip,focus.tooltip', () => {
							if (ghgTooltipTimer !== undefined) clearTimeout(ghgTooltipTimer);

							$('.ghg-tooltip-container .ghg-tooltip-box').attr('data-open', 'true');
							$('.ghg-tooltip-container .ghg-tooltip').attr('aria-hidden', 'false');
						});

					$('.ghg-tooltip-container')
						.off('mouseleave.tooltip')
						.on('mouseleave.tooltip', () => {
							ghgTooltipTimer = setTimeout(() => {
								$('.ghg-tooltip-container .ghg-tooltip-box').attr('data-open', 'false');
								$('.ghg-tooltip-container .ghg-tooltip').attr('aria-hidden', 'true');
							}, 250);
						});
				}

				Bark.seller.enableStarterPackChangeCard();

				$('.vr-failed-purchase-lead')
					.off('click.purchaseLead')
					.on('click.purchaseLead', () => {
						const isOneClick =
							Bark.seller.lastAttemptedResponseType === 'oneClickResponse';
						Bark.seller.processSubmitYourDetails(isOneClick);
					});
			},

			enableStarterPackChangeCard: () => {
				$('.js-starter-pack-change-card')
					.off('click.changeCard')
					.on('click.changeCard', () => {
						Bark.seller.hasCard = false;
						Bark.seller.initPurchaseStarterPack(
							$(`${Bark.seller.starterPackModalSelector} .purchase-upsell-welcome-offer`),
						);
					});
			},

			saveAutoTopupStatus: async (status) => {
				try {
					return await Bark.api(
						'seller/autotopup-status',
						{ status },
						null,
						null,
						'POST',
					);
				} catch (e) {
					console.error(e);
					return;
				}
			},

			initPurchaseStarterPack: (btn) => {
				Bark.seller.debug(
					`${Bark.seller.starterPackModalSelector} .purchase-upsell-welcome-offer - click`,
				);
				Bark.recordHotjarEvent('starter-pack-purchase-click');
				var data = btn.data();
				var ncredits = btn.data('ncredits');
				const rawDiscountPrice = btn.data('discountprice-raw');
				var ppc = rawDiscountPrice / ncredits;
				var totalpack = btn.data('discountprice');
				var planId = btn.data('plan-id');
				var hasVat = btn.data('has-vat');
				var packId = btn.data('pack-id');

				Bark.seller.currentPaymentRequest = {
					label: _t('seller_dashboard:card-modal.purchase-details-alt.header', {
						total_credits: ncredits,
					}),
					amount: rawDiscountPrice * 100,
					cpId: 1,
					userId: getUserIdFromModal(),
					isUpsell: false,
					projectId: Bark.seller.currentlyShownProjectId,
					packType: 'credit-pack',
					onWalletPaymentComplete: async (_, __, ___, ____, paymentMethodType) => {
						Bark.showLoading();
						const isOneClick =
							Bark.seller.lastAttemptedResponseType === 'oneClickResponse';
						Bark.seller.submitYourDetailsCall(
							Bark.seller.currentlyShownProjectId,
							isOneClick,
						);

						if (paymentMethodType !== 'paypal' || !Bark.seller.setAutoTopup) {
							// Do not allow setting of ATU with Paypal, as it breaks flows downstream
							// Allow a seller to turn off ATU if they're paying with Paypal
							if (Bark.seller.setAutoTopup !== undefined) {
								await Bark.seller.saveAutoTopupStatus(Bark.seller.setAutoTopup);
							}
						} else if (Bark.seller.setAutoTopup) {
							tracking('alternative-payments', 'Cannot enable auto-topup with Paypal');
						}

						Bark.hideLoading();
						Bark.seller.trackStarterPackPurchase();
					},
				};

				if (hasVat) {
					$('.response-modal-show-if-vat').removeClass('d-none');
				} else {
					$('.response-modal-show-if-vat').addClass('d-none');
				}

				$('.response-modal-credits-for-pack').text(ncredits);
				$('.response-modal-price-for-pack').text(totalpack);
				$('.response-modal-price-per-credit-payment').text(ppc.toFixed(2));

				Bark.seller.paymentType = 'pack_upsell_welcome';

				Bark.seller.planId = planId;

				Bark.seller.autoTopupAvailable = data.autoTopupAvailable || 0;
				Bark.seller.setAutoTopup = $('#autotop-enabler-starter-pack').is(':checked')
					? 1
					: 0;

				Bark.seller.lastPackModalSelector = '#' + btn.closest('.modal').attr('id');

				window.ga?.(
					'send',
					'event',
					'seller respond',
					'button click',
					'buy now - starter pack',
				);

				tracking('starter_pack_modal', 'starter_pack_modal_buy_now', {
					project_id: Bark.seller.currentlyShownProjectId,
					seller_profile_id: Bark.seller.id,
				});

				var project = Bark.seller.getProjectDataById(Bark.seller.currentlyShownProjectId);
				if (!project) {
					return false;
				}

				// Eligible for a starter pack but already has card details? Yes - for example when they set up a one-click response or purchased an elite pro
				if (Bark.seller.hasCard) {
					Bark.seller.debug('has card details');
					Bark.seller.currentModalSelector = '#sellerResponseCreditPackUpsell1';

					$(
						`${Bark.seller.starterPackModalSelector} .purchase-upsell-welcome-offer`,
					).prop('disabled', true);
					$(Bark.seller.starterPackModalSelector).modal('hide');

					if (window.Bark.expAct) {
						window.Bark.expAct.click(
							'buy_welcome_offer',
							'button',
							'no-credits/upsell_modal/',
							project.id,
						);
					}

					Bark.seller.purchaseCreditsThroughApi(
						packId,
						Bark.seller.setAutoTopup,
						Bark.seller.stripeToken,
						Bark.seller.currentlyShownProjectId,
					);
				} else {
					// Desn't have card details, show the credit card modal
					Bark.seller.debug('does not have card details');
					if (window.Bark.expAct) {
						window.Bark.expAct.click(
							'buy_welcome_offer',
							'button',
							'no-credits-no-card/upsell_first_creditpack_modal/',
							project.id,
						);
					}
					// multi payment modal
					if (Bark.seller.isMultiPaymentModalAvailable()) {
						Bark.seller.$multiPaymentModal.data({
							planId: 'spack',
						});
						Bark.seller.showMultiplePaymentEntryForm({
							...Bark.seller.generateMultiPaymentModalOptions(
								true,
								ncredits,
								ppc,
								rawDiscountPrice,
							),
							enableWallets: true,
							paymentOptions: Bark.seller.currentPaymentRequest,
						});
					} else {
						Bark.seller.showCardEntryForm({
							...Bark.seller.generateCardModalOptions(
								true,
								ncredits,
								ppc,
								rawDiscountPrice,
							),
							enableWallets: true,
							paymentOptions: Bark.seller.currentPaymentRequest,
						});
					}

					window.ga?.(
						'set',
						'page',
						window.location.pathname + 'credits-upsell/payment/',
					);
					window.ga?.('send', 'pageview');
				}
			},

			removeBundlePurchaseWaiting: function () {
				setTimeout(function () {
					$('.buy-project-bundle').removeClass('submitting').css('pointer-events', '');
					$('.confirm-buy-credits-project-bundle')
						.removeClass('submit-waiting')
						.css('pointer-events', '');
					$('.dismiss-project-bundle, .cancel-buy-credits-project-bundle').css(
						'pointer-events',
						'',
					);
				}, 50);
			},

			trackStarterPackPurchase: (params = {}) => {
				tracking('starter_pack_modal', 'starter_pack_payment_success', {
					project_id: Bark.seller.currentlyShownProjectId,
					seller_profile_id: Bark.seller.id,
					...params,
				});
			},

			updateLatestMessageSection: function (newData) {
				var project = $(
					'.dashboard-projects div[data-project-id="' + newData.project_id + '"]',
				);
				var lastMessage = project.find('.latest-action .message').text();

				if (
					typeof newData !== 'undefined' &&
					typeof newData.last_message !== 'undefined' &&
					newData.last_message !== null &&
					typeof newData.last_message.label !== 'undefined' &&
					newData.last_message.label !== lastMessage
				) {
					project
						.find('.latest-action .message')
						.text(Bark.seller.truncateMessage(newData.last_message.label, 70));
					project
						.find('.latest-action .time-posted')
						.text(Bark.seller.getTimeSince(newData.last_message.time));
				}
			},

			resetNotesButton: function () {
				$('.calls-to-action .submit-notes')
					.removeClass('btn-success')
					.addClass('btn-primary')
					.prop('disabled', false)
					.find('.submit-text')
					.text(_t('seller_dashboard:update-button'));
			},

			renderProject: function (projectId, reloadBark) {
				Bark.seller.resetNotesButton();
				reloadBark = typeof reloadBark !== 'undefined' ? reloadBark : false;
				if (
					$('#dashboard-project-details[data-project-id="' + projectId + '"]').length > 0
				) {
					// Don't re-render the project if on desktop. Mobile requires re-rendering in order to move from the project list to the project details.
					if (!Bark.seller.isMobile) {
						// project already rendered
						return false;
					}
				}
				if (Bark.seller.page == 'my-responses' && reloadBark) {
					console.error(
						'Simon, you removed something you shouldnt have. Put it back (1595)',
					);
					// $('.dashboard-projects-item').removeClass('selected');
					// $('.dashboard-projects div[data-project-id="' + projectId + '"]').addClass(
					// 	'selected',
					// );
					// $('.project-details-loader').removeClass('d-none');
					// $('.project-details-project-container').addClass('d-none');
					// var headers = {
					// 	Accept: 'application/vnd.bark.v2+json',
					// };
					// Bark.api(
					// 	'seller/response/' + projectId + '/',
					// 	{},
					// 	function (data) {
					// 		Bark.seller.updateDataRecord(data.data);
					// 		Bark.seller.renderProject(projectId, false);
					// 		Bark.seller.updateLatestMessageSection(data.data);
					// 	},
					// 	function (error) {
					// 		console.log('Failed to retrieve refreshed response.');
					// 	},
					// 	'GET',
					// 	headers,
					// );
				} else {
					Bark.seller.showProject(projectId);
					if (Bark.sellerResponseFlow) {
						Bark.sellerResponseFlow.resetStatus();
					}
					setTimeout(function () {
						$('.project-details-loader').addClass('d-none');
						$('.project-details-project-container').removeClass('d-none');
					}, 500);
				}
			},

			updateAccordianHeaderCounts: function () {
				$('.has-selected-count').each(function () {
					var $countEl = $(this).find('.filter-title .count');
					var count = $(this).find(':checked').not('.override').length;
					if (count > 0) {
						$countEl.text('(' + count + ')');
					} else {
						$countEl.text('');
					}
				});
			},

			updateStatusSelectColour: function () {
				var safeList = ['custom-select', 'drop-arrow', 'w-25'];
				var $select = $('#project-status-select');
				var newClasses = [];
				switch (parseInt($select.val())) {
					case 1:
						newClasses = ['bg-primary text-white'];
						break;
					case 3:
						newClasses = ['bg-success text-white'];
						break;
					default:
						newClasses = ['bg-secondary text-grey-100'];
						break;
				}
				$select.removeClass(function () {
					var removeClasses = [];
					$select
						.prop('class')
						.split(' ')
						.forEach(function (className) {
							if (safeList.indexOf(className) === -1) {
								removeClasses.push(className);
							}
						});
					return removeClasses;
				});
				$select.addClass(newClasses);
			},

			updateResponseContact: function (responseId, projectId, type, enabled) {
				Bark.api(
					'response/contact/',
					{
						response_id: responseId,
						type: type,
						enabled: enabled ? 1 : 0,
					},
					function (data) {
						// update local record
						var prop = null;
						switch (type) {
							case 'phone':
								prop = 'has_phoned';
								break;
							case 'sms':
								prop = 'sent_sms';
								break;
							case 'email':
								prop = 'sent_email';
								break;
						}
						Bark.seller.updateLocalProject(projectId, prop, enabled);
					},
					function (error) {
						// failed to update on the api, reset checkbox
						$(
							'[data-response-id="' +
								responseId +
								'"][data-response-contact-type="' +
								type +
								'"]',
						).prop('checked', !enabled);
					},
					'POST',
					{
						Accept: 'application/vnd.bark.v2+json',
					},
				);
			},

			updateResponseNote: function (responseId, projectId, note) {
				$('.notes-error').text('');
				var $el = $('.submit-notes');
				$el
					.addClass('submitting')
					.prop('disabled', true)
					.find('.submit-text')
					.text(_t('seller_dashboard:updating-button'));
				Bark.api(
					'response/note/',
					{
						response_id: responseId,
						note: note,
					},
					function (data) {
						// do nothing, successful call
						Bark.seller.updateLocalProject(projectId, 'note', note);
						$el
							.removeClass('btn-primary')
							.addClass('btn-success')
							.removeClass('submitting')
							.find('.submit-text')
							.text('Success');

						setTimeout(function () {
							Bark.seller.resetNotesButton();
						}, 3000);
					},
					function (error) {
						// failed to update on the api, show error message
						$('.notes-error').text(_t('seller_dashboard:response-note.error-message'));
						$el.removeClass('submitting').prop('disabled', false);
					},
					'POST',
					{
						Accept: 'application/vnd.bark.v2+json',
					},
				);
			},

			creditPackUpsellModalSubmit: function (
				modalId,
				planId,
				packDiscount,
				nCredits,
				salesTeamQuoteId,
			) {
				var project = Bark.seller.getProjectDataById(Bark.seller.currentlyShownProjectId);
				var ppc = project.price_per_credit * ((100 - packDiscount) / 100);
				var totalpack = (nCredits * ppc).toFixed(2);
				if (!project) {
					return false;
				}

				$('.response-modal-credits-for-pack').text(nCredits);
				$('.response-modal-price-for-pack').text(totalpack);
				$('.response-modal-price-per-credit-payment').text(ppc.toFixed(2));

				Bark.seller.paymentType = 'pack_' + planId;

				Bark.seller.lastPackModalSelector = modalId;

				// has card - close and submit response
				if (Bark.seller.hasCard) {
					Bark.seller.currentModalSelector = modalId;
					$(modalId).modal('hide');

					if (window.Bark.expAct) {
						window.Bark.expAct.click(
							'buy_now_pack',
							'button',
							'no-credits/upsell_modal/',
							Bark.seller.currentlyShownProjectId,
						);
					}

					Bark.seller.purchaseCreditsThroughApi(
						planId,
						Bark.seller.setAutoTopup,
						Bark.seller.stripeToken,
						Bark.seller.currentlyShownProjectId,
					);

					// doesnt have card details - open a modal to buy credits by card
				} else {
					if (window.Bark.expAct) {
						window.Bark.expAct.click(
							'buy_now_pack',
							'button',
							'no-credits-no-card/upsell_modal/',
							Bark.seller.currentlyShownProjectId,
						);
					}
					if (Bark.seller.isMultiPaymentModalAvailable()) {
						Bark.seller.showMultiplePaymentEntryForm({
							...Bark.seller.generateCardModalOptions(true, nCredits, ppc, totalpack),
							enableWallets: true,
							paymentOptions: Bark.seller.currentPaymentRequest,
						});
					} else {
						Bark.seller.showCardEntryForm({
							...Bark.seller.generateCardModalOptions(true, nCredits, ppc, totalpack),
							enableWallets: true,
							paymentOptions: Bark.seller.currentPaymentRequest,
						});
					}
				}
			},

			getSellerCurrentCardInfo: async function () {
				const setNoCard = () => {
					Bark.seller.cardId = null;
					Bark.seller.hasCard = false;
					Bark.seller.cardLast4 = null;
					Bark.seller.cardExpiry = null;
					Bark.seller.cardBrand = null;
					Bark.seller.cardBrandImgSrc = null;
				};
				try {
					const primaryPaymentMethod = await getPrimaryPaymentMethod(Bark.seller.id);

					if (primaryPaymentMethod) {
						Bark.seller.cardId = primaryPaymentMethod.id;
						Bark.seller.hasCard = true;
						Bark.seller.cardLast4 = primaryPaymentMethod.card_number;
						Bark.seller.cardExpiry = `${
							primaryPaymentMethod.card_expiry_month
						}/${primaryPaymentMethod.card_expiry_year.slice(-2)}`;
						Bark.seller.cardBrandImgSrc = primaryPaymentMethod.card_brand_image;
						Bark.seller.cardBrand = primaryPaymentMethod.card_brand;

						// To protect against the user adding a card in another window and continuing on this window, rebind
						//  the following event
						Bark.seller.enableStarterPackChangeCard();
					} else {
						setNoCard();
					}
				} catch (e) {
					setNoCard();
					window.bugsnagClient.notify(new Error('Could not get card details'), {
						metaData: { error: e },
					});

					throw e;
				}
			},

			preProjectBundlePurchase: function () {
				Bark.seller.getSellerCurrentCardInfo();
				setTimeout(function () {
					Bark.json({ url: '/sellers/credit-status/' })
						.done(
							/** @param {CreditStatusResponse} creditStatus */ function (creditStatus) {
								Bark.seller.creditStatus = creditStatus;
								var requiredCredits = Bark.seller.projectBundle.credits;
								var currentCredits = creditStatus.creditsBalance;
								var difference = currentCredits - requiredCredits;
								if (difference < 0) {
									// not enough credits
									if (
										creditStatus.autoTopupPack ||
										Bark.seller.acceptedBundleCreditPurchase
									) {
										// has autoTopup nvm we will get credits from there
										Bark.seller.processProjectBundlePurchase();
									} else {
										// not enough credits, need to buy some more
										var creditsToPurchase = Math.abs(difference);
										var pricePerCredit = Bark.ProjectsData.price_per_credit;
										var cost = Number(
											parseFloat(creditsToPurchase * pricePerCredit),
										).toFixed(2);
										var currencySymbol = creditStatus.currencySymbol;
										var hasVat = creditStatus.hasVat;
										var projectBundleModal = $('#projectBundleNotEnoughCredits');
										projectBundleModal
											.find('.credits-required')
											.text(
												requiredCredits !== 1
													? requiredCredits + ' credits'
													: requiredCredits + ' credit',
											);
										projectBundleModal
											.find('.credits-balance')
											.text(
												currentCredits !== 1
													? currentCredits + ' credits'
													: currentCredits + ' credit',
											);
										projectBundleModal
											.find('.extra-credits')
											.text(
												creditsToPurchase !== 1
													? creditsToPurchase + ' extra credits'
													: creditsToPurchase + ' extra credit',
											);
										projectBundleModal
											.find('.extra-credits-price')
											.text(
												hasVat
													? currencySymbol + cost + ' (ex VAT)'
													: currencySymbol + cost,
											);
										projectBundleModal.modal('show');
									}
								} else {
									Bark.seller.processProjectBundlePurchase();
								}
							},
						)
						.fail(function (error) {
							Bark.seller.removeBundlePurchaseWaiting();
							Bark.alertModal(
								_t('seller_dashboard:bundle.purchase-error-message'),
								null,
								null,
							);
						});
				}, 250);
			},

			processProjectBundlePurchase: function () {
				var bundleId = Bark.seller.projectBundle.id;
				if (!Bark.seller.hasCard) {
					Bark.seller.showBundleCardEntryForm(false);
					return;
				}
				Bark.api(
					'seller/bundle/',
					{ bundle_id: bundleId },
					function (data) {
						if (typeof data.status === 'undefined') {
							Bark.seller.handleBundlePurchaseError();
						} else {
							if (data.status === true) {
								var route =
									'/sellers/my-barks/?bbid=' +
									bundleId +
									'&bbc=' +
									data.data.total_cost +
									'&&bbp=' +
									data.data.projects_count;
								location.href = route;
								return;
							}
							var message = null;
							var cardError = false;
							if (typeof data.errors !== 'undefined') {
								if (typeof data.errors.card !== 'undefined') {
									message = data.errors.card;
									cardError = true;
								} else {
									message = data.errors.error;
								}
							}
							Bark.seller.handleBundlePurchaseError(message, cardError);
							return;
						}
					},
					Bark.seller.handleBundlePurchaseError,
					'POST',
					{
						Accept: 'application/vnd.bark.v2+json',
					},
				);
			},

			handleBundlePurchaseError: function (message, cardError) {
				if (typeof message === 'undefined' || message === null) {
					message = _t('seller_dashboard:bundle.purchase-error-message');
				}
				if (typeof cardError !== 'undefined' && cardError) {
					//popup card modal;
					message = message.replace('[[card_last_4]]', ' ' + Bark.seller.cardLast4);
					Bark.seller.showBundleCardEntryForm(message);
				} else {
					Bark.seller.removeBundlePurchaseWaiting();
					$('#projectBundleNotEnoughCredits').modal('hide');
					Bark.alertModal(message, null, null);
				}
			},

			renderPurchasedBundleModal: function () {
				if (QS.hasKey('bbid')) {
					var pbid = QS.getValue('bbid');
					var pbp = QS.getValue('bbp');
					var pbc = QS.getValue('bbc');
					var $modal = $('#purchasedBundleModal');
					$modal
						.find('.bundle-purchase-count')
						.text(pbp === 1 ? pbp + ' lead' : pbp + ' leads');
					$modal
						.find('.bundle-purchase-credits')
						.text(pbc === 1 ? pbc + ' credit' : pbc + ' credits');
					$modal.data('bundle-id', pbid);
					Bark.seller.bindBoughtProjectBundleEvents();
					$modal.modal('show');
				}
			},

			bindBoughtProjectBundleEvents: function () {
				var $modal = $('#purchasedBundleModal');
				$modal.on('change', '[name="bundle-response"]', function () {
					if ($(this).val() === 'message') {
						$modal.find('.bundle-response-message-container').removeClass('d-none');
					} else {
						$modal.find('.bundle-response-message-container').addClass('d-none');
					}
					$modal.find('.message-send-error').addClass('d-none').removeClass('d-flex');
				});
				$modal.on('click', '.modal-submit', function (e) {
					e.preventDefault();
					$(this).addClass('submit-waiting').css('pointer-events', 'none');
					$modal.find('.close-modal').css('pointer-events', 'none');
					$('#bundle-response-message-content').removeClass('is-invalid');
					$modal.find('.message-send-error').addClass('d-none').removeClass('d-flex');
					var messageType = $('[name="bundle-response"]:checked').val();
					if (typeof messageType === 'undefined') {
						messageType = 'message';
					}
					var customMessage = $('#bundle-response-message-content').val();
					var payload = {
						bundle_id: $modal.data('bundle-id'),
						message_type: messageType,
					};
					if (messageType === 'message') {
						if (customMessage == null || customMessage.length < 30) {
							$('#bundle-response-message-content').addClass('is-invalid');
							$modal
								.find('.modal-submit')
								.removeClass('submit-waiting')
								.css('pointer-events', '');
							$modal.find('.close-modal').css('pointer-events', '');
							return;
						}
						payload.custom_message = customMessage;
					}
					Bark.api(
						'seller/bundle/message',
						payload,
						function (data) {
							$modal
								.find('.modal-submit')
								.removeClass('submit-waiting')
								.css('pointer-events', '');
							$modal.find('.close-modal').css('pointer-events', '');
							if (typeof data.status === 'undefined' || data.status !== true) {
								$modal
									.find('.message-send-error')
									.addClass('d-flex')
									.removeClass('d-none');
								return;
							} else {
								QS.deleteValue('bbid');
								QS.deleteValue('bbc');
								QS.deleteValue('bbp');
								QS.updateBrowserQuerystring();
								Bark.seller.fetchRecords(true, true);
								$modal.modal('hide');
							}
						},
						function (data) {
							$modal.find('.message-send-error').addClass('d-flex').removeClass('d-none');
							$modal
								.find('.modal-submit')
								.removeClass('submit-waiting')
								.css('pointer-events', '');
							$modal.find('.close-modal').css('pointer-events', '');
						},
						'POST',
						{
							Accept: 'application/vnd.bark.v2+json',
						},
					);
				});
			},

			setLastAttemptedResponseType: function ($responseType) {
				Bark.seller.lastAttemptedResponseType = $responseType;
			},

			processSubmitYourDetails: async function (isOneClick) {
				await Bark.seller.getSellerCurrentCardInfo();
				var project = Bark.seller.getProjectDataById(Bark.seller.currentlyShownProjectId);
				if (!project) {
					return false;
				}

				Bark.seller.paymentType = '';
				Bark.seller.showProjectInteractionLoader();

				// init this here to load stripe up in case we need it soon
				Bark.seller.initPaymentModals();

				let action = isOneClick
					? 'Submit details - one-click response'
					: 'Submit details';
				tracking('seller-leads-dashboard', `${action}`, {
					project_id: project.project_id,
					lead_type: 'project',
				});

				$('.js-card-details-row').addClass('d-none');

				const cardInfoPromise = Bark.seller.getSellerCurrentCardInfo();
				cardInfoPromise
					.then(() => {
						if (Bark.seller.cardLast4) {
							$('.response-modal-card-number-suffix').text(Bark.seller.cardLast4);

							if (Bark.seller.isMultiPaymentModalAvailable()) {
								$('.response-modal-card-expiry-date').text(Bark.seller.cardExpiry);
								$('.response-modal-card-brand-name').text(Bark.seller.cardBrand);
								$('.response-modal-card-brand')
									.empty()
									.append(
										$('<img>', {
											class: 'img-fluid',
											src: Bark.seller.cardBrandImgSrc,
										}),
									);
								$('.js-saved-card').removeClass('d-none');
								$('.js-card-details-row').removeClass('d-none');
							} else {
								$('.response-modal-card-brand')
									.empty()
									.append(
										$('<img>', {
											src: Bark.seller.cardBrandImgSrc,
											height: 22,
											width: 65,
										}),
									);
							}
							$('.js-card-details-row').removeClass('d-none');
						} else {
							$('.js-card-details-row').addClass('d-none');
						}
					})
					.catch();

				// Get this seller fresh credit status first
				Bark.json({ url: '/sellers/credit-status/' })
					.done(function (creditStatus) {
						cardInfoPromise.then(() => {
							// Handle an expired session
							if (typeof creditStatus.session_expired !== 'undefined') {
								Bark.seller.hideListLoader();
								Bark.alertModal(
									_t('common:expired-session-login'),
									_t('common:login'),
									function () {
										Bark.seller.showListLoader();
										location.href = _l('/login');
									},
								);
								return false;
							}

							Bark.seller.updateCreditBalanceDom(creditStatus);

							// respond / use-credit-balance
							if (
								creditStatus.creditsBalance >= project.credits_required ||
								project.is_free
							) {
								//if pre-purchase for autotopup will happen and wayfair active, show wayfair
								if (
									isUSTaxComplianceActive() &&
									!Bark.seller.isUSTaxCompliant &&
									creditStatus.autoTopupPack &&
									creditStatus.creditsBalance < project.credits_required * 2
								) {
									let $callback = function () {
										Bark.seller.submitYourDetailsCall(project.id, isOneClick);
									};
									Bark.seller.openUSTaxCompliantModal(true);
									Bark.seller.bindUSTaxComplianceModalNextAction($callback, true);
									Bark.seller.bindUSTaxComplianceConfirmCancelModal();
								} else {
									Bark.seller.submitYourDetailsCall(project.id, isOneClick);
								}
							}

							// display packs
							else if (creditStatus.requiredToBuyStarterPack == 0) {
								if (creditStatus.autoTopupPack) {
									Bark.seller.paymentType =
										'autotopup_pack_' + creditStatus.autoTopupPack;

									// populate the card entry form details in case the payment fails
									var autoTopupPackDetails = null;

									for (var i = 0; i < creditStatus.creditPacks.length; i++) {
										if (creditStatus.creditPacks[i].id == creditStatus.autoTopupPack) {
											autoTopupPackDetails = creditStatus.creditPacks[i];
											break;
										}
									}

									if (autoTopupPackDetails) {
										var nCredits = autoTopupPackDetails.credits;
										var ppc =
											project.price_per_credit *
											((100 - autoTopupPackDetails.discount) / 100);
										var totalpack = (nCredits * ppc).toFixed(2);

										$('.response-modal-credits-for-pack').text(nCredits);
										$('.response-modal-price-for-pack').text(totalpack);
										$('.response-modal-price-per-credit-payment').text(ppc.toFixed(2));
										tracking('seller-leads-dashboard', 'Auto-topup-credit-response', {
											project_id: project.project_id,
											lead_type: 'project',
										});
									}

									if (isUSTaxComplianceActive() && !Bark.seller.isUSTaxCompliant) {
										Bark.seller.lastPackModalSelector =
											'#' + $(this).closest('.modal').attr('id');
										$(Bark.seller.lastPackModalSelector).modal('hide');
										let $callback = function () {
											Bark.seller.submitYourDetailsCall(project.id, isOneClick);
										};
										Bark.seller.openUSTaxCompliantModal(true);
										Bark.seller.bindUSTaxComplianceModalNextAction($callback, true);
										Bark.seller.bindUSTaxComplianceConfirmCancelModal();
									} else {
										Bark.seller.submitYourDetailsCall(project.id, isOneClick);
									}
								} else {
									if (isOneClick) {
										Bark.seller.setLastAttemptedResponseType('oneClickResponse');
									} else {
										Bark.seller.setLastAttemptedResponseType('submitYourDetails');
									}

									var modalId = '#sellerResponseCreditPackUpsell1';

									Bark.seller.hideProjectInteractionLoader();

									// Show or hide the VAT text
									for (var i = 0; i < creditStatus.creditPacks.length; i++) {
										var pack = creditStatus.creditPacks[i];
										$(modalId + ' #credit-pack-row-' + pack.id + ' .js-vat-toggle')[
											pack.vat_multiplier > 1 || $('#js-show-tax-disclaimers-us').length
												? 'removeClass'
												: 'addClass'
										]('d-none');
									}

									$('.credit-pack-selection .credit-upsell-topup-submit')
										.removeClass('loading')
										.prop('disabled', false);
									Bark.seller.logAbandonedResponse(project);
									$(modalId).modal('show');
									creditUpsellModalBindTrackingListeners();
									Bark.seller.welcomeShown = true;
									Bark.seller.currentModalSelector = modalId;
									$.post('/api/event-log/', {
										event_category: 'seller_credit_pack_upsell',
										event_name: 'view',
										event_id: 'credit_modal',
									});
									window.ga?.(
										'set',
										'page',
										window.location.pathname + 'credit-packs-upsell/',
									);
									window.ga?.('send', 'pageview');
									tracking('seller-leads-dashboard', 'Purchase credits - view', {
										pagename: window.location.pathname,
										lead_type: 'project',
										seller_profile_id: Bark.seller.id,
										is_one_click: Number(Boolean(isOneClick)),
										is_starter_pack: 0,
									});
									tracking(
										'seller-leads-dashboard-&-responses',
										'credit_pack_modal_open',
										{
											seller_profile_id: Bark?.seller?.id,
											country_id: window?.Bark?.ENV?.ccid,
											project_id: Bark?.seller?.currentlyShownProjectId,
										},
									);
								}
							}

							// display a starter pack
							else {
								Bark.seller.hideProjectInteractionLoader();
								Bark.seller.logAbandonedResponse(project);
								$(
									`${Bark.seller.starterPackModalSelector} .purchase-upsell-welcome-offer`,
								).prop('disabled', false);
								Bark.seller.bindStarterPackModalEvents &&
									Bark.seller.bindStarterPackModalEvents();
								$(Bark.seller.starterPackModalSelector).modal('show');
								Bark.seller.welcomeShown = true;
								Bark.seller.currentModalSelector = Bark.seller.starterPackModalSelector;
								window.ga?.('set', 'page', window.location.pathname + 'credits-upsell/');
								window.ga?.('send', 'pageview');
								tracking('seller-leads-dashboard', 'Purchase credits - view', {
									pagename: window.location.pathname,
									lead_type: 'project',
									seller_profile_id: Bark.seller.id,
									is_one_click: Number(Boolean(isOneClick)),
									is_starter_pack: 1,
								});
							}
							window.scrollTo(0, 0);
						});
					})
					.fail(function () {
						Bark.seller.hideProjectInteractionLoader();
						Bark.alertModal(
							_t('seller_dashboard:project-interaction.error-message'),
							null,
							null,
						);
					});
			},

			showSessionExpiredModal: function () {
				Bark.seller.hideListLoader();
				Bark.alertModal(
					_t('common:expired-session-login'),
					_t('common:login'),
					function () {
						Bark.seller.showListLoader();
						location.href = _l('/login');
					},
				);
				return false;
			},

			updateCreditBalanceDom: function (creditStatus) {
				if (typeof creditStatus.creditsBalance !== 'undefined') {
					Bark.seller.creditStatus = creditStatus;

					// Update the #header credit balance only if it has changed
					var currentDomBalance = $('#header span.header-credit-balance')
						.first()
						.text()
						.slice(1, -1);
					if (currentDomBalance != creditStatus.creditsBalance) {
						$('#header span.header-credit-balance').animate(
							{ opacity: 0.1 },
							{
								duration: 200,
								complete: function () {
									$('#header span.header-credit-balance').text(
										'(' + creditStatus.creditsBalance + ')',
									);
									$('#header span.header-credit-balance').animate(
										{ opacity: 1 },
										{ duration: 200 },
									);
								},
							},
						);
					}

					// Modal updates
					$('.response-modal-credit-balance-text').text(
						Bark.sprintf(
							'%d credit%s',
							creditStatus.creditsBalance,
							+creditStatus.creditsBalance === 1 ? '' : 's',
						),
					);
				}
			},

			logAbandonedResponse: function (project) {
				var payload = {
					category_id: project.category_id,
					expected_credits_required: project.expected_credits_required,
					expecting_free_responses: project.expecting_free_responses ? 1 : 0,
					is_free: project.is_free,
					project_id: project.id,
					quote_later: true,
					seller_name: Bark.ProjectsData.sellerName,
				};

				var shouldLogAbandonedResponse = $('#log-abandoned-response');
				if (shouldLogAbandonedResponse.length > 0 && shouldLogAbandonedResponse.val()) {
					Bark.json({
						url: '/sellers/abandoned-response/',
						data: {
							payload: JSON.stringify({
								payload: JSON.stringify(payload),
								project_id: project.id,
							}),
						},
					});
				}
			},

			capitalize: function (string) {
				return string.substr(0, 1).toUpperCase() + string.substr(1);
			},

			sellerRetryAttempts: 0,
			hasCard: false,
			cardId: null,
			cardLast4: '',

			handleCardDecline: function (error) {
				let response = error.responseJSON;
				Bark.seller.sellerRetryAttempts += 1;
				Bark.seller.hideProjectInteractionLoader();

				Bark.seller.isMultiPaymentModalAvailable() &&
					creditCardTracking.logAction('payment failure', {
						payment_method:
							$('#payment-accordion .collapse.open').data('payment-method') ||
							'saved-card',
					});

				var errorReason;
				if (typeof response.decline_reason !== 'undefined') {
					errorReason = response.decline_reason;
				} else {
					var project = Bark.seller.getProjectDataById(
						Bark.seller.currentlyShownProjectId,
					);
					var buyerName = cleanse(project.buyer_name);

					if (
						response?.error?.message ===
						'Cannot charge a customer that has no active card'
					) {
						errorReason = {
							conversational_error_message: _t(
								'seller_dashboard:card-decline.inactive-card.conversational-error-message',
							),
							clean_error_message: _t(
								'seller_dashboard:card-decline.inactive-card.clean-error-message',
							),
							user_action_message_retried: _t(
								'seller_dashboard:card-decline.inactive-card.user-action-message-retried',
							),
							primary_cta_text: _t(
								'seller_dashboard:card-decline.inactive-card.call-to-action',
							),
							user_can_try_again: 0,
							recoverable_decline: 1,
						};
						if (buyerName) {
							errorReason.user_action_message = _t(
								'seller_dashboard:card-decline.inactive-card.user-action-message-buyer',
								{ buyer_public_name: buyerName },
							);
						} else {
							errorReason.user_action_message = _t(
								'seller_dashboard:card-decline.inactive-card.user-action-message',
							);
						}
					} else {
						errorReason = {
							conversational_error_message: _t(
								'seller_dashboard:card-decline.current-card.conversational-error-message',
							),
							clean_error_message: _t(
								'seller_dashboard:card-decline.current-card.clean-error-message',
							),
							user_action_message_retried: _t(
								'seller_dashboard:card-decline.current-card.user-action-message-retried',
							),
							primary_cta_text: _t(
								'seller_dashboard:card-decline.current-card.call-to-action',
							),
							user_can_try_again: 0,
							recoverable_decline: 1,
						};
						if (buyerName) {
							errorReason.user_action_message = _t(
								'seller_dashboard:card-decline.current-card.user-action-message-buyer',
								{ buyer_public_name: buyerName },
							);
						} else {
							errorReason.user_action_message = _t(
								'seller_dashboard:card-decline.current-card.user-action-message',
							);
						}
					}
				}

				if (Bark.seller.hasCard && Bark.seller.cardLast4) {
					errorReason.conversational_error_message =
						errorReason.conversational_error_message.replace(
							'[[card_last_4]]',
							' ending ' + Bark.seller.cardLast4,
						);
					errorReason.clean_error_message = errorReason.clean_error_message.replace(
						'[[card_last_4]]',
						' ending ' + Bark.seller.cardLast4,
					);
				} else {
					errorReason.conversational_error_message =
						errorReason.conversational_error_message.replace('[[card_last_4]]', '');
					errorReason.clean_error_message = errorReason.clean_error_message.replace(
						'[[card_last_4]]',
						'',
					);
				}

				// if the seller has already tried to update their card details
				if (Bark.seller.sellerRetryAttempts > 1) {
					var errorText =
						Bark.seller.capitalize(
							errorReason.clean_error_message.replace(
								'[[card_last_4]]',
								Bark.seller.cardLast4,
							),
						) + '. ';
					errorText += errorReason.user_action_message_retried;
					$('#purchase-credit-stripe-error').text(errorText).show();
					// send them back to the card update screen with an error message

					if (Bark.seller.isMultiPaymentModalAvailable()) {
						Bark.seller.showMultiplePaymentEntryForm({
							...Bark.seller.generateCardModalOptions(false),
							enableWallets: true,
							paymentOptions: Bark.seller.currentPaymentRequest,
						});
						window.BarkPaymentsCardModal.showModalErrorText(
							Bark.seller.$cardModal,
							errorText,
						);
					} else {
						Bark.seller.showCardEntryForm({
							...Bark.seller.generateCardModalOptions(false),
							enableWallets: true,
							paymentOptions: Bark.seller.currentPaymentRequest,
						});
						window.BarkPaymentsCardModal.showModalErrorText(
							Bark.seller.$cardModal,
							errorText,
						);
					}
				} else {
					// show the card error page with relevant error message
					if (Bark.seller.paymentType.indexOf('autotopup_') === 0) {
						$('.js-purchase-credits-error-heading').text('Auto Topup Failed');
					} else {
						$('.js-purchase-credits-error-heading').text('Credit Purchase Failed');
					}

					const modal = $(Bark.seller.purchaseCreditsErrorModalSelector);

					$('.js-card-decline-reason', modal)
						.text(errorReason.conversational_error_message)
						.removeClass('d-none');
					$('.js-card-decline-user-action', modal).text(errorReason.user_action_message);
					$('.js-reenter-card-details', modal).text(errorReason.primary_cta_text);

					modal.modal('show');
				}

				return false;
			},
			onPaymentIntentSuccessRedirect: async function () {
				const paymentIntentId = QS.getValue('payment_intent', null);
				const statusSuccess = QS.getValue('redirect_status', null);
				const sourceType = QS.getValue('source_type', null);
				const method = QS.getValue('pm_type', 'sofort');
				const isRedirectStatusSuccess =
					QS.getValue('redirect_status', null) === 'succeeded';
				await Bark.seller.getSellerCurrentCardInfo();
				Bark.seller.initPaymentModals();

				if (Bark.seller.cardLast4) {
					$('.response-modal-card-number-suffix').text(Bark.seller.cardLast4);

					if (Bark.seller.isMultiPaymentModalAvailable()) {
						$('.response-modal-card-expiry-date').text(Bark.seller.cardExpiry);
						$('.response-modal-card-brand-name').text(Bark.seller.cardBrand);
						$('.response-modal-card-brand')
							.empty()
							.append(
								$('<img>', {
									class: 'img-fluid',
									src: Bark.seller.cardBrandImgSrc,
								}),
							);
						$('.accordion > div:not(.d-none):first').addClass('round-card');
						$('.js-saved-card').removeClass('d-none');
						Bark.seller.$multiPaymentModal
							.find('.card-header[data-payment-method="new-card"] label')
							.text(_t('seller_common:card-form.new-card'));
						$('.js-card-details-row').removeClass('d-none');
					}
				}

				if (paymentIntentId) {
					Bark.showLoading();

					window.BarkPaymentsCardModal.confirmPaymentIntent(
						method,
						paymentIntentId,
						Bark.USER.userId,
						Bark.seller.showSofortSuccessModal,
					)
						.then((data) => {
							Bark.hideLoading();
							console.log(`${method} payment confirmed`, data?.is_paid);
							QS.deleteValue('payment_intent_client_secret');
							QS.deleteValue('redirect_status');
							if (data.is_paid) {
								QS.deleteValue('payment_intent');
								QS.deleteValue('pm_type');
								Bark.hideLoading();
								QS.updateBrowserQuerystring();
								return;
							} else {
								creditCardTracking.logAction('payment failure', {
									payment_method: method,
								});
							}
							QS.updateBrowserQuerystring();

							if (!isRedirectStatusSuccess) {
								let totalCredits = data?.credits;
								let ppc = null;
								let totalPrice = null;
								Bark.seller.$multiPaymentModal.data({
									planId: data?.cpId == 1 ? 'spack' : data?.cpId,
									credits: totalCredits,
									price: data?.price,
									n_responses: data?.n_responses,
									upsell: false,
								});
								Bark.hideLoading();
								const starterPack = $('.purchase-upsell-welcome-offer');
								const creditPack = $(`.js-credit-pack-row[data-plan-id=${data.cpId}]`);

								if (starterPack.length && data?.cpId == 1) {
									ppc = starterPack.data('pricePerCredit');
									totalPrice = starterPack.data('discount');
								} else if (creditPack.length > 0) {
									ppc = creditPack.data('pricePerCredit');
									totalPrice = creditPack.data('packPrice');
									Bark.seller.paymentType = 'credit';
								}

								const options = Bark.seller.generateMultiPaymentModalOptions(
									true,
									totalCredits,
									ppc,
									totalPrice,
								);

								if (method === 'sofort') {
									Bark.seller.showMultiplePaymentEntryForm(options);
									window.BarkPaymentsCardModal.showModalErrorText(
										Bark.seller.$multiPaymentModal,
										_t('seller_common:sofort-payment.error.failed-request'),
									);
								} else {
									Bark.seller.showCardEntryForm(options);
									window.BarkPaymentsCardModal.showModalErrorText(
										Bark.seller.$multiPaymentModal,
										_t('seller_common:sofort-payment.error.failed-request'),
									);
								}

								Bark.hideLoading();
							} else {
								throw new Error('problem-confirming-payment');
							}
						})
						.catch((err) => {
							//basic error
							console.log(err);
							let message = _t('seller_common:sofort-payment.error.problem-with-payment');
							switch (err.message) {
								case 'problem-with-payment':
									message = _t('seller_common:sofort-payment.error.problem-with-payment');
									break;
								case 'problem-confirming-payment':
									message = _t(
										'seller_common:sofort-payment.error.problem-confirming-payment',
									);
									break;
								default:
									break;
							}

							window.Bark.alertModal(message);
							window.bugsnagClient.notify(
								new Error('Error: Failed to verify payment Intent'),
								{
									metaData: {
										original: err,
										payment_intent_id: paymentIntentId,
										redirect_status: statusSuccess,
										source_type: sourceType,
									},
								},
							);
							Bark.hideLoading();
							return false;
						});
				}
				QS.updateBrowserQuerystring();
			},

			closeCreditModal: async () => {
				var project = Bark.seller.getProjectDataById(Bark.seller.currentlyShownProjectId);
				$('.credit-pack-selection .credit-upsell-topup-submit')
					.removeClass('loading')
					.prop('disabled', false);

				await Bark.seller.getSellerCurrentCardInfo();

				if (!project) {
					return;
				}

				if (Bark.seller.lastPackModalSelector) {
					if (Bark.seller.lastPackModalSelector !== '#purchaseCreditsError') {
						$(Bark.seller.lastPackModalSelector).modal('show');
					}
				}
			},
			closeMultiPaymentModal: () => {
				QS.deleteValue('payment_intent');
				QS.updateBrowserQuerystring();
				var project = Bark.seller.getProjectDataById(Bark.seller.currentlyShownProjectId);
				$('#payment-accordion .payment-body ').collapse('hide');
				$('.credit-pack-selection .credit-upsell-topup-submit')
					.removeClass('loading')
					.prop('disabled', false);

				if (!project) {
					return;
				}

				if (Bark.seller.lastPackModalSelector) {
					if (Bark.seller.lastPackModalSelector !== '#purchaseCreditsError') {
						$(Bark.seller.lastPackModalSelector).modal('show');
					}
				}
			},
			// call api to purchase credit pack via using existing card
			savedCardCallback: ($modal, successCallback, $selector) => (event, $modal) => {
				event.preventDefault();

				let button = $(event.currentTarget);
				button.addClass('loading').prop('disabled', true);
				let $container = button.closest('.bark-modal');
				let modalId = '#' + $container.prop('id');
				let packDiscount =
					$container.data('discountprice') ?? $container.data('packDiscount');
				let nCredits = $container.data('credits');
				let planId = $container.data('plan-id');
				let salesTeamQuoteId = $container.data('sales-team-quote-id');
				Bark.seller.setAutoTopup = $('#autotop-enabler-starter-pack').is(':checked')
					? 1
					: 0;
				Bark.seller.salesTeamQuoteId = salesTeamQuoteId;
				$container.modal('hide');
				Bark.seller.creditPackUpsellModalSubmit(
					modalId,
					planId,
					packDiscount,
					nCredits,
					salesTeamQuoteId,
				);
			},
			initPaymentModals: () => {
				if (!Bark.seller.$cardModal) {
					Bark.seller.$cardModal = window.BarkPaymentsCardModal.initCardModal(
						Bark.seller.cardModalSelector,
						Bark.seller.purchaseCreditResponseHandler,
						Bark.seller.closeCreditModal,
						Bark.seller.closeCreditModal,
					);
				}

				if (
					!Bark.seller.$multiPaymentModal &&
					Bark.seller.isMultiPaymentModalAvailable()
				) {
					Bark.seller.$multiPaymentModal =
						window.BarkPaymentsCardModal.initMultiPaymentCardModal(
							Bark.seller.multiPaymentModalSelector,
							Bark.seller.purchaseCreditResponseHandler,
							Bark.seller.closeMultiPaymentModal,
							Bark.seller.closeMultiPaymentModal,
							Bark.seller.savedCardCallback,
						).data({
							country: Bark.LOCALE.iso2c,
						});
				}
			},

			generateCardModalOptions: (
				showPurchaseDetails,
				totalCredits,
				ppc,
				totalPack,
				hideProjectPrice,
			) => {
				const modalOptions = {};
				if (showPurchaseDetails) {
					modalOptions['headerText'] = _t(
						'seller_dashboard:card-modal.purchase-details.header',
						{ total_credits: totalCredits },
					);
					let subHeader = _t('seller_dashboard:card-modal.amount-summary.title', {
						each_amount: _t('seller_dashboard:card-modal.amount-summary.each-amount', {
							price_per_credit: currency(ppc),
						}),
						total_amount: _t('seller_dashboard:card-modal.amount-summary.total-amount', {
							total_price: currency(totalPack),
						}),
					});
					if (
						window.BarkPaymentsCardModal.hasTax(Bark.seller.$cardModal) ||
						$('#js-show-tax-disclaimers-us').length
					) {
						subHeader += ` ${_t('common:excluding-vat')}`;
					}
					const project = Bark.seller.getProjectDataById(
						Bark.seller.currentlyShownProjectId,
					);

					if (project && !hideProjectPrice) {
						const creditDifference =
							project.credits_required - Bark.seller.creditStatus.creditsBalance;

						if ($('.card-modal-apm-design').length) {
							subHeader += `<p class="mb-0 mt-2">
                                ${_t(
																	'seller_dashboard:card-modal.purchase-details-alt.subheader_with_credits',
																	{
																		count: creditDifference,
																		total_credits: creditDifference,
																		buyer_public_name: cleanse(project.buyer_name),
																	},
																)}
                            </p>`;

							modalOptions.confirmText = _t('seller_common:card-form.pay-with-card');
						} else {
							subHeader +=
								'<br/><br/> ' +
								_t('seller_dashboard:card-modal.purchase-details-alt.subheader', {
									total_credits: creditDifference,
									buyer_public_name: cleanse(project.buyer_name),
								});
						}
					}

					modalOptions['subheaderHtml'] = subHeader;
				} else {
					modalOptions['headerText'] = _t('seller_dashboard:card-modal.header');
					modalOptions['subheaderHtml'] = '';
				}
				return modalOptions;
			},
			generateMultiPaymentModalOptions: (
				showPurchaseDetails,
				totalCredits,
				ppc,
				totalPack,
			) => {
				const modalOptions = {};
				modalOptions['headerText'] = _t('seller_dashboard:card-modal.header');
				modalOptions['subheaderHtml'] = '';

				if (showPurchaseDetails) {
					if (totalCredits) {
						modalOptions['headerText'] = _t(
							'seller_dashboard:card-modal.purchase-details-alt.header',
							{ total_credits: totalCredits },
						);
					}
					if (ppc && totalPack) {
						modalOptions['subheaderHtml'] = _t(
							'seller_dashboard:card-modal.amount-summary-alt.title',
							{
								each_amount: _t(
									'seller_dashboard:card-modal.amount-summary-alt.each-amount',
									{ price_per_credit: currency(ppc) },
								),
								total_amount: _t(
									'seller_dashboard:card-modal.amount-summary-alt.total-amount',
									{ total_price: currency(totalPack) },
								),
								excluding_vat: window.BarkPaymentsCardModal.hasTax(Bark.seller.$cardModal)
									? _t('common:excluding-vat-text')
									: '',
							},
						);
					}

					const project = Bark.seller.getProjectDataById(
						Bark.seller.currentlyShownProjectId,
					);
					const creditDifference =
						project.credits_required - Bark.seller.creditStatus?.creditsBalance;
					if (creditDifference > 0) {
						modalOptions['formFooterHtml'] = _t(
							'seller_dashboard:card-modal.purchase-details-alt.subheader',
							{
								total_credits: _t('seller_dashboard:starter-pack-content.total-credits', {
									total_credits: _n(creditDifference),
								}),
								buyer_public_name: cleanse(project.buyer_name),
							},
						);
					}
				}
				return modalOptions;
			},

			/**
			 * @param {object} modalOptions
			 * @param {string?} modalOptions.headerText
			 * @param {string?} modalOptions.subheaderHtml
			 * @param {string?} modalOptions.formFooterHtml
			 * @param {string?} modalOptions.confirmText
			 * @param {string?} modalOptions.cancelText
			 * @param {boolean?} modalOptions.clearModal
			 * @param {boolean?} modalOptions.isOnlySavingCard
			 * @param {boolean?} modalOptions.enableWallets True to enable ApplePay or GooglePay. description is also required to be set
			 * @param {PaymentRequestOptions?} modalOptions.paymentOptions Required if enabling wallets.
			 */
			showCardEntryForm: (modalOptions) => {
				// we are going to show the card modal, we need to make double sure it's loaded
				Bark.seller.initPaymentModals();

				// always hide it before opening, we might add one after calling this function
				window.BarkPaymentsCardModal.clearModal(Bark.seller.$cardModal);
				window.BarkPaymentsCardModal.hideModalErrorText(Bark.seller.$cardModal);
				window.BarkPaymentsCardModal.showCardModal(Bark.seller.$cardModal, modalOptions);

				Bark.seller.currentModalSelector = Bark.seller.cardModalSelector;
				$(Bark.seller.lastPackModalSelector).modal('hide');
			},

			/**
			 * @param {object} modalOptions
			 * @param {string?} modalOptions.headerText
			 * @param {string?} modalOptions.subheaderHtml
			 * @param {string?} modalOptions.formFooterHtml
			 * @param {string?} modalOptions.confirmText
			 * @param {string?} modalOptions.cancelText
			 * @param {boolean?} modalOptions.clearModal
			 * @param {boolean?} modalOptions.enableWallets True to enable ApplePay or GooglePay. description is also required to be set
			 * @param {PaymentRequestOptions?} modalOptions.paymentOptions
			 */
			showMultiplePaymentEntryForm: (modalOptions) => {
				// we are going to show the card modal, we need to make double sure it's loaded
				Bark.seller.initPaymentModals();
				let multiPaymentConfirmSelector = '.js-payment-modal-confirm';

				// always hide it before opening, we might add one after calling this function
				window.BarkPaymentsCardModal.hideModalErrorText(Bark.seller.$multiPaymentModal);
				window.BarkPaymentsCardModal.showMultiPaymentModal(
					Bark.seller.$multiPaymentModal,
					modalOptions,
				);
				Bark.seller.currentModalSelector = Bark.seller.cardModalSelector;
				$(Bark.seller.lastPackModalSelector).modal('hide');

				Bark.seller.$multiPaymentModal
					.find("#payment-accordion .card-header [for='new-card']")
					.text(_t('seller_common:card-form.debit-credit-card'));
				if (Bark.seller.cardLast4) {
					Bark.seller.$multiPaymentModal
						.find("#payment-accordion .card-header [for='new-card']")
						.text(_t('seller_common:card-form.new-card'));
				}
				window.BarkPaymentsCardModal.confirmButtonSetLoadingState(
					Bark.seller.$multiPaymentModal,
					false,
					multiPaymentConfirmSelector,
				);
			},

			showBundleCardEntryForm: function (errorMessage) {
				throw 'Bundles is not supported';
			},

			cardUpdatedToast: null,

			submitYourDetailsCall: function (projectId, isOneClick) {
				var project = Bark.seller.getProjectDataById(projectId);
				isOneClick = isOneClick ? 1 : 0;
				if (!project) {
					return false;
				}

				$('.js-dashboard-right-container').animate({ scrollTop: 0 });

				Bark.seller.lastAttemptedResponseType = 'submitYourDetails';

				// get me a campaign please
				var campaign = $('#session-campaign').val();
				if (!campaign) {
					campaign = Bark.seller.getUrlParameter('campaign');
				}

				Bark.seller.showProjectInteractionLoader();

				// Call
				// hide success message by default then we'll slide it down
				$('.js-respond-message-success-message').hide();
				Bark.SMSTemplates.refresh();
				Bark.seller.submitYourDetailsApi(
					projectId,
					campaign,
					isOneClick,
					Bark.seller.stripeToken,
					Bark.seller.paymentType,
					Bark.seller.planId,
				);
			},

			handleScaAuthenticationRequest: function (
				client_secret,
				successCallback,
				failureCallback,
			) {
				window.BarkPaymentsCardModal.promptUserAction(
					client_secret,
					(result) => {
						if (result?.paymentIntent) {
							successCallback(result?.paymentIntent);
						} else {
							purchasing = false;
							if (failureCallback) {
								failureCallback();
							} else {
								Bark.seller.hideProjectInteractionLoader();
								if (Bark.seller.isMultiPaymentModalAvailable()) {
									Bark.seller.showMultiplePaymentEntryForm({
										resetFormAfterFailedSCA: true,
										...Bark.seller.generateCardModalOptions(false),
										enableWallets: true,
										paymentOptions: Bark.seller.currentPaymentRequest,
									});
									window.BarkPaymentsCardModal.showModalErrorText(
										Bark.seller.$multiPaymentModal,
										_t('seller_dashboard:card-modal.authenticate-error-message'),
									);
								} else {
									Bark.seller.showCardEntryForm({
										...Bark.seller.generateCardModalOptions(false),
										enableWallets: true,
										paymentOptions: Bark.seller.currentPaymentRequest,
									});
									window.BarkPaymentsCardModal.showModalErrorText(
										Bark.seller.$cardModal,
										_t('seller_dashboard:card-modal.authenticate-error-message'),
									);
								}
							}
						}
					},
					() => {
						// It is not clear if this is ever likely to happen, but we log it to bugsnag, so if it does a lot
						// then we can improve this error messaging.
						purchasing = false;
						Bark.seller.hideProjectInteractionLoader();
						window.Bark.alertModal(
							_t('seller_dashboard:card-modal.generic-error-message'),
						);
					},
				);
			},

			submitYourDetailsApi: async function (
				projectId,
				campaign,
				isOneClick,
				stripeToken,
				paymentType,
				planId,
				paymentIntentId,
			) {
				const project = Bark.seller.getProjectDataById(projectId);
				let payload = {
					bark_id: projectId,
					project_id: projectId,
					campaign: campaign || undefined,
					quick_response: isOneClick,
					supports_payment_intents: true,
					tracking: window.Bark.getTrackableParameters(),
					expecting_free_with_subscription: Number(project.is_free_with_subscription),
				};

				if (stripeToken != '') {
					payload.stripe_token = stripeToken;
				}

				if (paymentType != '') {
					payload.payment_type = paymentType;
				}

				if (planId != '') {
					payload.plan_id = planId;
				}

				if (paymentIntentId) {
					payload.payment_intent_id = paymentIntentId;
				}

				if (!stripeToken) {
					await Bark.seller.getSellerCurrentCardInfo();
				}

				if (Bark.seller.cardId) {
					payload.paymentMethodId = Bark.seller.cardId;
				}
				const hasFreeResponsesBeforeSubmittingDetails =
					Bark?.seller?.creditStatus?.hasFreeResponses;
				let result;

				await Bark.api(
					'response/submitd/',
					payload,
					function (data) {
						if (data.forcelogin) {
							location.replace(_l('/login/?dest_url=' + data.redirect_url));
							// location.replace('/logout/');
							return false;
						}

						const respondingToCurrentProject =
							Number(payload.project_id) === Number(Bark.seller.currentlyShownProjectId);

						Bark.seller.paymentType = '';
						Bark.seller.hideProjectInteractionLoader();

						if (payload.payment_type && payload.payment_type.match(/^pack_/)) {
							window.ga?.(
								'send',
								'event',
								'button',
								'buyUpsellCreditPack',
								'packId:' + parseInt(payload.payment_type.replace(/pack_/, '')),
							);
						}

						Bark.seller.stripeToken = '';
						Bark.seller.updateCreditStatus('check-upgrades');
						$('.seller-dash-v2 .details-submitted').show();

						if (respondingToCurrentProject) {
							Bark.seller.showSellerResponseWorkflow(data);
						}

						if (!shouldShowLeadsListRct()) {
							Bark.seller.hideProjectFromTheList(payload.project_id);
						}

						if (Bark.sellerResponseFlow) {
							let project = Bark.seller.getProjectDataById(payload.project_id);
							if (project) {
								Bark.sellerResponseFlow.projectCreated = project?.created;
								Bark.sellerResponseFlow.categoryName = project?.category_name;
							}
						}

						if (!shouldShowLeadsListRct()) {
							Bark.seller.removeProject(payload.project_id);
						}

						Bark.seller.sellerRetryAttempts = 0;

						if (Bark.sellerResponseFlow && respondingToCurrentProject) {
							Bark.sellerResponseFlow.showResponseFlow(data, !!isOneClick);
						}

						if (
							parseInt(payload.project_id) ===
							parseInt(Bark.ProjectsData.projectToRespond)
						) {
							Bark.seller.removeProjectToRespond();
						}

						if (data.show_success_message) {
							$('.js-respond-message-success-message').slideDown({ duration: 100 });
						}

						Bark.seller.getLeadStatus(true);
						Bark.seller.sendResponseAnalytics({
							seller_profile_id: Bark.seller.id,
							response_id: data.response.id,
						});

						tracking('seller-leads-dashboard', 'Response success', {
							project_id: payload.project_id,
							lead_type: 'project',
						});

						if (showQuickFilters()) {
							tracking('quick-filters-leads-list', 'lead-purchased', {
								quick_filter_selected: Bark.seller.quickFiltersApplied.length > 0,
								quick_filter_value:
									Bark.seller.quickFiltersApplied.length > 0
										? Bark.seller.quickFiltersApplied.join()
										: '',
							});
							//Updates quick filters counts
							setTimeout(function () {
								Bark.seller.fetchFilteredRecords();
							}, 1000);
						}

						if (shouldShowLeadsListRct()) {
							leadsListRctRemoveProjectFromList(payload.project_id);
						}

						result = true;
						return true;
					},
					function (error) {
						const errorResponse = error.responseJSON;
						let sanitisedErrorType;
						let errorMessage = '';

						if (errorResponse.error === 'subscription_free_error') {
							location.replace(`/sellers/dashboard/?prx=${projectId}`);
							return;
						}

						if (errorResponse?.error?.type === 'action_required') {
							sanitisedErrorType = 'sca';
							Bark.seller.handleScaAuthenticationRequest(
								errorResponse?.error?.error_data?.client_secret,
								(paymentIntent) => {
									Bark.seller.submitYourDetailsApi(
										projectId,
										campaign,
										isOneClick,
										stripeToken,
										paymentType,
										planId,
										paymentIntent.id,
									);
								},
								Bark.seller.isMultiPaymentModalAvailable()
									? (err) => {
											Bark.seller.purchaseUpsellHandleFailed(
												_t(
													'seller_dashboard:credit-upsell.error-message.authorize-payment',
												),
											);
											Bark.seller.hideProjectInteractionLoader();
									  }
									: null,
							);
						} else {
							if (errorResponse?.error?.status_code === 402) {
								sanitisedErrorType = 'card_declined';
								Bark.seller.handleCardDecline(error);
							} else {
								sanitisedErrorType = 'other';
								Bark.seller.hideProjectInteractionLoader();
								if (errorResponse) {
									errorMessage = errorResponse?.error?.message;
									$('.error-general span').text(errorMessage);
								}

								$('.error-general').show();
							}
						}

						tracking('seller-leads-dashboard', 'Response failure', {
							project_id: payload.project_id,
							lead_type: 'project',
							seller_profile_id: Bark.seller.id,
							error_type: sanitisedErrorType,
							error_message: errorMessage,
							seller_was_prospect: Boolean(Bark?.USER?.sellerWasProspect),
							payment_type: payload.payment_type,
						});

						result = false;
					},
					'POST',
					Bark.apiVersionHeader('v3'),
				);

				if (project.is_free_with_subscription) {
					await Bark.seller.SubscriptionFreeResponse.handleFreeWithSubscription();
				}

				openCompleteProfileModal?.();

				return result;
			},

			sendFilterHeapEvents(filters) {
				let reducedFilters = Object.keys(filters).reduce(
					(appliedFilters, currentFilterKey) => {
						if (
							currentFilterKey !== 'show_filter_counts' &&
							currentFilterKey !== 'next_page' &&
							filters[currentFilterKey]
						) {
							appliedFilters.filter_names.push(currentFilterKey);
							appliedFilters.filter_values.push(
								`${currentFilterKey}: ${filters[currentFilterKey]}`,
							);
						}
						return appliedFilters;
					},
					{ filter_names: [], filter_values: [] },
				);
				tracking('seller-leads-dashboard-&-responses', 'Filters - apply', {
					filter_names: reducedFilters.filter_names.toString(),
					filter_values: reducedFilters.filter_values.toString(),
				});
			},

			trackResponse(projectResponseId) {
				$.post(
					'/api/track-response/',
					{ project_response_id: projectResponseId },
					function () {
						return true;
					},
				);
			},

			sendResponseAnalytics(data) {
				try {
					window.ga?.('ecommerce:addTransaction', {
						id: 'r' + data.response_id,
						affiliation: data.seller_profile_id,
						revenue: '0',
						shipping: '0',
						tax: '0',
					});

					window.ga?.('ecommerce:addItem', {
						id: 'u' + data.response_id,
						name: 'Reply',
						sku: 'reply',
						category: 'Reply',
						price: '0',
						quantity: '1',
					});

					window.ga?.('ecommerce:send');

					window.ga?.('send', 'event', 'backend', 'seller_fresh_response', '');
				} catch (ignore) {}
			},

			hideProjectFromTheList: function (projectId) {
				// Remove the project from DOM but dont remove from the local db / object as we will still need the project details when they send a message in a few seconds
				var $el = $('.dashboard-projects div[data-project-id="' + projectId + '"]');
				if ($el.closest('#sticky-project').length > 0) {
					$el = $el.closest('#sticky-project');
				}
				$($el).slideUp({
					duration: 200,
					complete: function () {
						$(this).remove();

						// Update total
						$('.seller-dash-v2 .results-count span').text(
							parseInt($('.seller-dash-v2 .results-count span').text(), 10) - 1,
						);
						Bark.ProjectsData.projectsTotal--;
						Bark.seller.unfilteredTotal--;
						Bark.seller.updateNewHeaderLeadCounts(
							Bark.ProjectsData.projectsTotal,
							Bark.seller.unfilteredTotal,
						);
						Bark.seller.processNoRecords();

						// If nothing left and there are more, load more
						if (
							Bark.seller.getNumberOfVisibleProjects() == 0 &&
							Bark.seller.moreRecords
						) {
							Bark.seller.fetchRecords();
						} else {
							Bark.seller.updateRecordCount();
						}
					},
				});
			},

			showSellerResponseWorkflow: function (data, type) {
				var buyerTelephone = data.buyerInfo ? data.buyerInfo.tel : data.buyer_telephone;
				var buyerEmail = data.buyerInfo ? data.buyerInfo.email : data.buyer_email;

				Bark.seller.sendMessageFormShown = true;

				$('.seller-dash-v2 .interaction-info').slideUp({ duration: 100 });
				$('.seller-dash-v2 .highlights-interaction-info').slideUp({ duration: 100 });

				// Update data
				$('#barkSendEstimateModal .respond-message-estimate').attr(
					'data-buyer-id',
					data.buyerInfo.id,
				);
				$('#barkSendEstimateModal .respond-message-estimate').attr(
					'data-project-response-id',
					data.project_response_id,
				);
				$('.seller-dash-v2 .dashboard-project-details .buyer-telephone-display').html(
					$('<a>', {
						class: 'text-dark-blue',
						href: 'tel:' + buyerTelephone,
						target: '_blank',
					}).text(buyerTelephone),
				);
				$('.seller-dash-v2 .dashboard-project-details .buyer-email-display').html(
					$('<a>', {
						class: 'text-dark-blue',
						href: 'mailto:' + buyerEmail,
						target: '_blank',
					}).text(buyerEmail),
				);

				// Reset values
				$(
					'#barkSendEstimateModal .respond-message-estimate .respond-textarea-field',
				).removeClass('is-error');
				$(
					'#barkSendEstimateModal .respond-message-estimate .respond-textarea-field, .seller-dash-v2 .respond-message-estimate .estimate-amount',
				).val('');
				$('#barkSendEstimateModal .respond-message-estimate .estimate').removeClass(
					'disabled',
				);
				$('#barkSendEstimateModal .respond-message-estimate .estimate-type')
					.val('')
					.prop('disabled', false);
				$('#barkSendEstimateModal .respond-message-estimate .estimate-amount')
					.val('')
					.prop('disabled', false)
					.removeClass('is-error');

				// Show the form
				$('.seller-dash-v2 .dashboard-project-details .project-top-header').addClass(
					'just-responded',
				);
				$('.seller-dash-v2 .dashboard-project-details .project-promo').hide();

				// d-flex is !important, so it overrides the display:none, so we can't fade it out
				$('.seller-dash-v2 .response-cap-and-count-container')
					.removeClass('d-flex d-lg-inline-flex')
					.hide();
				$(
					'.seller-dash-v2 .calls-to-action, .seller-dash-v2 .project-credits-to-respond, .seller-dash-v2 .response-cap-and-count-response-requested',
				).fadeOut({
					duration: 200,
					complete: function () {
						if (type === 'template-editor' && typeof window.Bract !== 'undefined') {
							$(
								'.seller-dash-v2 .respond-message-estimate.respond-success-message',
							).slideDown({ duration: 300 });
							Bark.seller.showCampaignEditor();
						}
						$('.seller-dash-v2 .dashboard-project-details .buyer-telephone')
							.slideDown({ duration: 200 })
							.css({ display: 'block' });
						$('.seller-dash-v2 .dashboard-project-details .buyer-email')
							.slideDown({ duration: 200 })
							.css({ display: 'block' });
					},
				});

				if (shouldShowMobileStickyHeaderRct()) {
					setMobileStickyHeaderRctShowPassBtn(false);
				}
			},

			updateCreditStatus: function (action) {
				Bark.json({
					url: '/sellers/credit-status/',
					data: { prevCpid: Bark.seller.lastPurchasedPackId },
				}).done(function (response) {
					Bark.seller.creditStatus = response;

					// Always update credits balance view
					Bark.seller.updateCreditBalanceDom(response);
					Bark.seller.updateProjectPricing();

					// Do other things
					if (action == 'check-upgrades') {
						if (response.upgradePack != null || response.proPlanUpsell) {
							Bark.seller.updatePackUpgradeModal(response);
							//This is used by credit-status endpoint to determine if they should get an upsell
							// because we have processed the upsell we want to set it back to false.
							Bark.seller.lastPurchasedPackId = false;
						}
					}
				});
			},

			updateProjectPricing: function () {
				// Check if all free responses have been used and if they have
				// update preloaded project pricing as they're not free anymore.
				if (Bark.seller.creditStatus.hasFreeResponses == false) {
					for (const project of Bark.ProjectsData.projects) {
						if (project.is_free_with_subscription) {
							continue;
						}

						/** @var { ProjectObject } project */
						const selector = `.seller-dash-v2 .dashboard-projects div[data-project-id="${project.project_id}"] .project-credits span.num-credits-resp`;
						let isFree = false;

						if (project?.is_project_alert_free !== 'undefined') {
							isFree = project.is_project_alert_free;
						}

						project.is_free = isFree;
						project.expecting_free_responses = false;

						$(selector).html(Bark.seller.calculateProjectCredits(project));
					}
				}
			},

			updatePackUpgradeModal: function (data) {
				const largerCreditPackDiscountFeatureEnabled = Boolean(
					$('.credit-pack-row .credits-pill span.pill-elite-pro-benefit').length,
				);

				if (largerCreditPackDiscountFeatureEnabled) {
					return;
				}
				// Credits upgrade

				if (Bark.seller.isMultiPaymentModalAvailable()) {
					Bark.seller.showPurchaseSuccessModal(Bark.seller.mostRecentCreditsPurchased);
					return;
				}
				if (data.upgradePack != null) {
					$('.modal').modal('hide');
					$('#creditPurchaseSuccessNew').modal('show');
					$('#creditPurchaseSuccessNew .pack-upgrade').show();
					$(
						'#creditPurchaseSuccessNew .pack-upgrade.elite-pro, #creditPurchaseSuccessNew .js-credits-added, #creditPurchaseSuccessNew .inline-bark-border',
					).hide();
					$('#creditPurchaseSuccessNew .js-credits').text(data.upgradePack.credits);
					$('#creditPurchaseSuccessNew .js-discount').text(data.upgradePack.discount);
					$('#creditPurchaseSuccessNew .js-price').html(
						data.upgradePack.currency_symbol + data.upgradePack.price.toFixed(2),
					);
					$('#creditPurchaseSuccessNew .js-discounted-price').text(
						data.upgradePack.currency_symbol +
							data.upgradePack.discounted_price.toFixed(2),
					);
					if (
						data.upgradePack.vat_multiplier != 1 ||
						$('#js-show-tax-disclaimers-us').length
					) {
						$('#creditPurchaseSuccessNew .js-discounted-price').append(
							` <small>${_t('common:excluding-vat')}</small>`,
						);
					}

					// Show 'Great - you've purchased 160 credits'
					if (
						data.previousPackId &&
						data.upgradePack &&
						Bark.seller.mostRecentCreditsPurchased > 0
					) {
						$('#creditPurchaseSuccessNew .inline-success-message').show();
						$('#creditPurchaseSuccessNew .js-credits-just-purchased').text(
							Bark.seller.mostRecentCreditsPurchased,
						);
					} else {
						$('#creditPurchaseSuccessNew .inline-success-message').hide();
					}

					$('#creditPurchaseSuccessNew .purchase-credit-pack')
						.attr('data-discountprice', data.upgradePack.discounted_price)
						.attr('data-ncredits', data.upgradePack.credits)
						.attr('data-currency', data.upgradePack.currency_symbol)
						.attr(
							'data-plan-co',
							Number(
								data.upgradePack.discounted_price * data.upgradePack.vat_multiplier,
							).toFixed(2),
						)
						.attr('data-plan-id', data.previousPackId)
						.attr('data-price-per-credit', data.upgradePack.price_per_credit)
						.attr('data-plan-upgrade-id', data.upgradePack.id)
						.data('upgradePack', data.upgradePack);

					// Update the success modal upfront
					$('#creditPurchaseSuccessNew .js-credits-purchased').text(
						data.upgradePack.credits,
					);
				}
			},

			/**
			 * @deprecated Appears unused
			 */
			multiPaymentUpsell: async function (packId, ncredits = null, packDiscount = null) {
				await Bark.seller.getSellerCurrentCardInfo();

				if (Bark.seller.cardLast4) {
					$('.response-modal-card-number-suffix').text(Bark.seller.cardLast4);
					$('.response-modal-card-expiry-date').text(Bark.seller.cardExpiry);
					$('.response-modal-card-brand-name').text(Bark.seller.cardBrand);
					$('.response-modal-card-brand')
						.empty()
						.append(
							$('<img>', {
								class: 'img-fluid',
								src: Bark.seller.cardBrandImgSrc,
							}),
						);
					$('.js-saved-card').removeClass('d-none');
					$('.js-card-details-row').removeClass('d-none');
				}
				Bark.seller.initPaymentModals();
				Bark.seller.$multiPaymentModal.data({
					planId: packId,
					credits: ncredits,
					packDiscount: packDiscount,
					defaultPaymentMethod: Bark.seller.cardLast4 ? SAVED_CARD : NEW_CARD,
					upsell: true,
					'pack-type': 'credit-pack-upgrade',
				});
				Bark.seller.lastPackModalSelector = '#sellerResponseCreditPackUpsell1';
				Bark.seller.showMultiplePaymentEntryForm(
					Bark.seller.generateMultiPaymentModalOptions(false),
				);
				Bark.seller.paymentType = 'upsell';
				Bark.hideLoading();
				$('#creditPurchaseSuccessNew .purchase-credit-pack').prop('disabled', false);
			},
			processBuyUpgradeCredits: async function () {
				Bark.showLoading();
				const packId = $('#creditPurchaseSuccessNew .purchase-credit-pack').attr(
					'data-plan-id',
				);
				const packDiscount = $('#creditPurchaseSuccessNew .purchase-credit-pack').attr(
					'data-discountprice',
				);
				const ncredits = $('#creditPurchaseSuccessNew .purchase-credit-pack').attr(
					'data-ncredits',
				);

				// no upsells for multi payment modal
				if (!Bark.seller.isMultiPaymentModalAvailable()) {
					Bark.seller.processBuyUpgradeCreditsApi(packId);
				}
			},
			/**
			 * Register a new card on Stripe
			 * @param {string} tokenId
			 */
			registerNewCard: async function (tokenId, { hideLoading }) {
				!hideLoading && Bark.showLoading();

				try {
					await window.BarkPaymentsCardModal.confirmSetupIntent(
						Bark.seller.$cardModal.data('user-id'),
						tokenId,
					);
					!hideLoading && Bark.hideLoading();
					await Bark.seller.getSellerCurrentCardInfo();
					return true;
				} catch (e) {
					console.log(e);
					Bark.hideLoading();
					window.BarkPaymentsCardModal.showModalErrorText(
						Bark.seller.$cardModal,
						_t('seller_dashboard:card-modal.authenticate-error-message'),
					);
					window.BarkPaymentsCardModal.showCardModal(Bark.seller.$cardModal);
					return false;
				}
			},

			processBuyUpgradeCreditsNoPaymentMethod: () => {
				Bark.seller.paymentType = 'upsell';
				const btn = $('#creditPurchaseSuccessNew .purchase-credit-pack');

				const data = btn.data('upgradePack');
				const cpId = btn.data('planId');

				Bark.seller.currentPaymentRequest = {
					label: _t('seller_dashboard:card-modal.purchase-details-alt.header', {
						total_credits: data.credits,
					}),
					amount: data.discounted_price * 100,
					cpId,
					userId: getUserIdFromModal(),
					isUpsell: true,
					projectId: null,
					packType: 'credit-pack',
					onWalletPaymentComplete: async () => {
						Bark.seller.purchaseUpsellHandleComplete({ status: true });
						Bark.hideLoading();
					},
				};

				Bark.seller.showCardEntryForm({
					...Bark.seller.generateCardModalOptions(
						true,
						data.credits,
						data.price_per_credit,
						data.discounted_price,
						true,
					),
					enableWallets: true,
					paymentOptions: Bark.seller.currentPaymentRequest,
				});
			},

			processBuyUpgradeCreditsApi: async function (packId, paymentIntentId) {
				const payload = {
					cpid: packId,
					supports_payment_intents: true,
					payment_intent_id: paymentIntentId || null,
					tracking: Bark.getTrackableParameters(),
				};

				await Bark.seller.getSellerCurrentCardInfo();

				if (Bark.seller.cardId) {
					payload.paymentMethodId = Bark.seller.cardId;
				} else {
					Bark.hideLoading();
					Bark.seller.processBuyUpgradeCreditsNoPaymentMethod();
					return;
				}

				Bark.api(
					'creditpack/purchase-upsell',
					payload,
					Bark.seller.purchaseUpsellHandleComplete,
					(error) => {
						const errorResponse = error.responseJSON;
						if (errorResponse?.error?.type === 'action_required') {
							Bark.seller.handleScaAuthenticationRequest(
								errorResponse?.error?.error_data?.client_secret,
								(paymentIntent) => {
									Bark.seller.processBuyUpgradeCreditsApi(packId, paymentIntent.id);
								},
								() => {
									Bark.seller.purchaseUpsellHandleFailed(
										_t('seller_dashboard:credit-upsell.error-message.authorize-payment'),
									);
									Bark.seller.hideProjectInteractionLoader();
								},
							);
						} else {
							Bark.seller.purchaseUpsellHandleFailed(
								_t('seller_dashboard:credit-upsell.error-message.process-payment'),
							);
						}
					},
					'POST',
				);
			},

			purchaseUpsellHandleComplete: (response) => {
				$('#creditPurchaseSuccessNew .purchase-credit-pack').prop('disabled', false);
				Bark.hideLoading();
				if (response.status) {
					Bark.seller.hideProjectInteractionLoader();
					Bark.seller.updateCreditStatus('update-balance');
					$(
						'#creditPurchaseSuccessNew .pack-upgrade, #creditPurchaseSuccessNew .pack-upgrade.elite-pro, #creditPurchaseSuccessNew .inline-success-message',
					).hide();
					$(
						'#creditPurchaseSuccessNew .js-credits-added, #creditPurchaseSuccessNew .inline-bark-border',
					).show();
					$('#creditPurchaseSuccessNew').modal('show');
				} else {
					Bark.seller.purchaseUpsellHandleFailed(
						_t('seller_dashboard:credit-upsell.error-message.process-payment'),
					);
				}
			},

			purchaseUpsellHandleFailed: (alertText) => {
				$('#creditPurchaseSuccessNew .purchase-credit-pack').prop('disabled', false);
				Bark.seller.hideProjectInteractionLoader();
				Bark.hideLoading();
				Bark.alertModal(alertText);
				creditCardTracking.logAction('payment failure', {
					payment_method: window.BarkPaymentsCardModal.getActiveAccordionSection(),
				});
			},

			updateSearchFormValues: function (data) {
				var option;

				// Categories
				$('.seller-dash-v2 .dashboard-projects-filters .field-categories').html('');
				var categoriesPresent = false;
				var cids = Bark.seller.getUrlParameter('cids');
				if (typeof data.filter_categories !== 'undefined') {
					for (option in data.filter_categories) {
						if (typeof data.filter_categories[option] !== 'function') {
							categoriesPresent = true;
							var categorySelected = false;

							if (
								cids == '' ||
								$.inArray(data.filter_categories[option].hashed_id, cids.split(',')) > -1
							) {
								categorySelected = true;
							}

							$('.seller-dash-v2 .dashboard-projects-filters .field-categories').append(
								'<option value="' +
									data.filter_categories[option].hashed_id +
									'"' +
									(categorySelected ? ' selected="selected"' : '') +
									'>' +
									data.filter_categories[option].name +
									'</option>',
							);
						}
					}
				}

				// Radius / r
				var radius = Bark.seller.getUrlParameter('r');
				if (radius != '') {
					$('.seller-dash-v2 .dashboard-projects-filters .field-radius').val(radius);
				}

				// Keyword
				$('.seller-dash-v2 .dashboard-projects-filters .field-keyword').val(
					Bark.seller.getUrlParameter('kw'),
				);
			},

			fetchRecords: function (reset, renderView) {
				Bark.seller.debug('- fetchRecords()');
				// if true - clear the list and load new records (e.g. new search or refresh)
				reset = typeof reset === 'undefined' ? false : true;
				renderView = typeof renderView === 'undefined' ? false : true;
				if (reset) {
					// new records
					Bark.seller.showFirstItem = true;
					Bark.seller.moreRecords = true;
					Bark.ProjectsData.projectsTotal = 0;
					Bark.ProjectsData.projects = [];
					Bark.seller.nextPage = '';
					Bark.seller.lastRefresh = new Date();

					$('.seller-dash-v2 .dashboard-projects #sticky-project').empty();
					$('.seller-dash-v2 .dashboard-projects .items').empty();
					$('.seller-dash-v2 .dashboard-project-details').hide();
				} else {
					if (shouldShowLeadsListRct()) {
						Bark.seller.showFirstItem =
							Bark.ProjectsData.projects.length > 0 ||
							$('.respond-success-message').is(':visible')
								? false
								: true;
					} else {
						// load more records
						if (
							Bark.seller.getNumberOfVisibleProjects() > 0 ||
							$('.respond-success-message').is(':visible')
						) {
							Bark.seller.showFirstItem = false;
						} else {
							Bark.seller.showFirstItem = true;
						}
					}
				}

				// Don't load if there are no more to load
				if (!Bark.seller.moreRecords) {
					return false;
				}
				// Show loader
				Bark.seller.showListLoader(reset);

				var payload = Bark.seller.buildFilterQueryString();

				Bark.seller.initialQueryPayload = payload;

				if (Bark.ProjectsData.projectToRespond) {
					payload.project_id = Bark.ProjectsData.projectToRespond;
				}

				if (Bark.ProjectsData.show_unread) {
					payload.project = ['unread'];

					Bark.seller.dataFiltered = true;
				}

				var fail = function (error, exception) {
					if (exception === 'abort') {
						return false;
					}
					Bark.seller.fetchingRecords = null;
					Bark.seller.showGeneralError(error);
					Bark.seller.hideListLoader();
				};

				var headers = {
					Accept: 'application/vnd.bark.v2+json',
				};

				if (Bark.seller.fetchingRecords) {
					Bark.seller.fetchingRecords.abort();
					Bark.seller.fetchingRecords = null;
				}

				// Load data
				Bark.seller.fetchingRecords = Bark.api(
					Bark.seller.getDataUrl(),
					payload,
					function (data) {
						Bark.seller.fetchingRecords = null;

						if (data.data.unfiltered_total) {
							Bark.seller.unfilteredTotal = data.data.unfiltered_total;
						}

						if (!shouldShowLeadsListRct()) {
							try {
								var visibleProjects =
									Bark.seller.getNumberOfVisibleProjects() + data.data.items?.length;
								if (visibleProjects >= data.data.total) {
									Bark.seller.moreRecords = false;
								}
							} catch (e) {
								window.bugsnagClient.notify(e, {
									metaData: { payload: payload, data: data },
								});
								Bark.seller.moreRecords = false;
							}
						}

						if (typeof data.data.checked_projects !== 'undefined') {
							for (var projectId in data.data.checked_projects) {
								if (!data.data.checked_projects.hasOwnProperty(projectId)) {
									continue;
								}
								var errorDetails = data.data.checked_projects[projectId];
								var errorType = null;
								var errorData = null;
								if (errorDetails.substring(0, 'capped'.length) === 'capped') {
									errorType = 'capped';
									errorData = errorDetails.substring('capped_'.length);
								} else {
									errorType = errorDetails;
								}
								var errorMessage = false;
								switch (errorType) {
									case 'found':
										errorMessage = false;
										break;
									case 'responded':
										//check if this is and upsell first before showing error message
										errorMessage = _t(
											'seller_dashboard:leads-page.error-message.already-responded',
										);
										break;
									case 'capped':
										var cap = 5;
										if (errorData) {
											cap = errorData;
										}
										errorMessage =
											'<p>' +
											_t('seller_dashboard:leads-page.error-message.response-limit', {
												count: cap,
											}) +
											'</p>' +
											'<p>' +
											_t(
												'seller_dashboard:leads-page.error-message.response-limit-continued',
											) +
											'</p>';
										break;
									default:
										errorMessage = _t(
											'seller_dashboard:leads-page.error-message.unavailable',
										);
								}
								if (errorMessage) {
									Bark.seller.projectErrors[projectId] = errorMessage;
								}
							}
						}

						for (var item in data.data.items) {
							if (typeof data.data.items[item] !== 'function') {
								Bark.seller.addDataRecord(data.data.items[item]);
							}
						}

						Bark.seller.nextPage = data.data.next_page;
						Bark.ProjectsData.projectsTotal = data.data.total;

						Bark.seller.debug('- render fetched records');

						Bark.seller.determineServiceLocationCountsFromFilters(data.data.filters); // leads_filter_header_exp
						Bark.seller.updateLatestRefresh();
						Bark.seller.renderRecords();
						Bark.seller.renderFilters(data.data.filters, true);
						Bark.seller.updateNewHeader(data);
						Bark.seller.checkOneClickViaEmail();
						Bark.seller.setupEntityTracking();
					},
					fail,
					'GET',
					headers,
				);
			},

			fetchFilteredRecords: function () {
				var payload = Bark.seller.buildFilterQueryString();
				Bark.seller.newQueryPayload = Bark.seller.buildFilterQueryString();

				Object.assign(payload, { results_per_request: 0 });

				var fail = function (error, exception) {
					if (exception === 'abort') {
						return false;
					}
					Bark.seller.fetchingFilterCounts = null;
					Bark.seller.showGeneralError(error);
					Bark.seller.hideFilterLoader();
				};

				var headers = {
					Accept: 'application/vnd.bark.v2+json',
				};

				if (Bark.seller.fetchingFilterCounts) {
					Bark.seller.fetchingFilterCounts.abort();
					Bark.seller.fetchingFilterCounts = null;
				}

				// Load data
				Bark.seller.fetchingFilterCounts = Bark.api(
					Bark.seller.getDataUrl(),
					payload,
					function (data) {
						Bark.seller.debug('- updatingFilterCounts');
						Bark.seller.renderFilters(data.data.filters, true);
						Bark.seller.hideFilterLoader();

						if (
							!Bark.seller.payloadsAreEqual(
								Bark.seller.newQueryPayload,
								Bark.seller.initialQueryPayload,
							)
						) {
							// This is not currently used, but I am leaving it in here as it will allow us to only show the apply
							// button once the filters have changed, and I think that will be requested once people play with it
							// Bark.seller.showApplyFiltersPanel();
						}
					},
					fail,
					'GET',
					headers,
				);
			},

			determineServiceLocationCountsFromFilters: (filteredData) => {
				// Whole function is related to leads_filter_header_exp
				try {
					const categories =
						filteredData.find((item) => item.id === 'categories')?.children || [];
					const locations =
						filteredData.find((item) => item.id === 'locations')?.children || [];

					Bark.seller.servicesCount = categories.filter((item) =>
						Boolean(Number(item.filter_value)),
					).length;
					Bark.seller.locationsCount = locations.filter((item) =>
						Boolean(Number(item.filter_value)),
					).length;
				} catch (e) {
					Bark.seller.servicesCount = 1;
					Bark.seller.locationsCount = 1;
				}
			},

			fetchBundle: function () {
				Bark.seller.debug('- fetchBundle()');
				Bark.seller.showFirstItem = true;
				Bark.seller.moreRecords = false;
				Bark.ProjectsData.projectsTotal = 0;
				Bark.ProjectsData.projects = [];
				Bark.seller.nextPage = '';
				Bark.seller.lastRefresh = new Date();

				$('.seller-dash-v2 .dashboard-projects #sticky-project').empty();
				$('.seller-dash-v2 .dashboard-projects .items').empty();
				$('.seller-dash-v2 .dashboard-project-details').hide();

				// Show loader
				Bark.seller.showListLoader(true);

				var fail = function (error, exception) {
					if (exception === 'abort') {
						return false;
					}
					location.href = '/sellers/dashboard/?berr=invalid';
				};

				var headers = {
					Accept: 'application/vnd.bark.v2+json',
				};

				if (Bark.seller.fetchingRecords) {
					Bark.seller.fetchingRecords.abort();
					Bark.seller.fetchingRecords = null;
				}

				var payload = { pbs: Bark.seller.projectBundle.slug };
				if (Bark.seller.testing) {
					payload.testing = 1;
				}

				// Load data
				Bark.seller.fetchingRecords = Bark.api(
					Bark.seller.getDataUrl(),
					payload,
					function (data) {
						Bark.seller.fetchingRecords = null;

						var project_data = data.data.es_data;
						var bundle_data = data.data.bundle;

						Bark.seller.projectBundle.credits = bundle_data.expected_credits;
						Bark.seller.projectBundle.leadCount = bundle_data.expected_projects_count;
						Bark.seller.projectBundle.id = bundle_data.id;

						if (bundle_data.expires_at) {
							Bark.seller.projectBundle.expiresAt = moment.utc(
								bundle_data.expires_at,
								'YYYY-MM-DD HH:mm:ss',
							);
						} else {
							Bark.seller.projectBundle.expiresAt = null;
						}

						if (project_data.unfiltered_total) {
							Bark.seller.unfilteredTotal = project_data.unfiltered_total;
						}

						Bark.seller.moreRecords = false;

						for (var item in project_data.items) {
							if (typeof project_data.items[item] !== 'function') {
								Bark.seller.addDataRecord(project_data.items[item]);
							}
						}

						Bark.seller.nextPage = '';
						Bark.ProjectsData.projectsTotal = project_data.total;

						Bark.seller.debug('- render bundle records');
						Bark.seller.renderBundleData();
						Bark.seller.updateLatestRefresh();
						Bark.seller.renderRecords();
					},
					fail,
					'GET',
					headers,
				);
			},

			renderBundleData: function () {
				var detailsContainer = $('.project-bundle-offer-details');
				var leadCount = Bark.seller.projectBundle.leadCount;
				var credits = Bark.seller.projectBundle.credits;
				detailsContainer
					.find('.lead-count')
					.text(_t('seller_dashboard:bundle.all-leads', { count: leadCount }));
				detailsContainer
					.find('.credit-count')
					.text(_t('seller_dashboard:bundle.credit-count', { count: credits }));
				Bark.seller.updateBundleExpiryTimer();
				detailsContainer.removeClass('waiting');
				$('.bundlebox-container').removeClass('waiting');
			},

			updateBundleExpiryTimer: function () {
				var expiresContainer = $('.project-bundle-offer-details').find(
					'.expiry-time-span',
				);
				var expiresAt = Bark.seller.projectBundle.expiresAt;
				var expired = false;
				if (expiresAt) {
					var seconds = Math.floor(expiresAt.diff(moment.utc()) / 1000);
					var fromNowString = '';
					if (seconds < 0) {
						expired = true;
						fromNowString = _t('seller_dashboard:bundle.offer-expired');
					} else {
						if (seconds < 10) {
							fromNowString = _t('seller_dashboard:bundle.offer-expires-few-seconds');
						} else if (seconds < 60) {
							fromNowString = _t('seller_dashboard:bundle.offer-expires-seconds', {
								count: seconds,
							});
						} else {
							var minutes = Math.floor(seconds / 60);
							seconds = seconds % 60;
							if (minutes < 60) {
								fromNowString = _t(
									'seller_dashboard:bundle.offer-expires-minutes-seconds',
									{ minutes: minutes, seconds: seconds },
								);
							} else {
								var hours = Math.floor(minutes / 60);
								minutes = minutes % 60;
								fromNowString = _t(
									'seller_dashboard:bundle.offer-expires-hours-minutes',
									{ hours: hours, minutes: minutes },
								);
							}
						}
					}
					expiresContainer.find('.expiry-time').text(fromNowString);
					expiresContainer.show();
					if (expired) {
						$('.bundle-action-buttons').removeClass('d-flex').addClass('d-none');
						Bark.alertModal(
							_t('seller_dashboard:bundle.modal-title-offer-expired'),
							_t('seller_dashboard:bundle.modal-title-leads'),
							function () {
								location.href = _l('/sellers/dashboard/');
							},
						);
					} else {
						setTimeout(function () {
							Bark.seller.updateBundleExpiryTimer();
						}, 1000);
					}
				} else {
					expiresContainer.hide();
				}
			},

			payloadsAreEqual: function (oldPayload, newPayload) {
				return false;

				// var a = window._omit(oldPayload, ['next_page', 'project_id', '__proto__']);
				// var b = window._omit(newPayload, ['next_page', 'project_id', '__proto__']);
				//
				// return window._isEqual(a, b);
			},

			showApplyFiltersPanel: function () {
				$('#dashboard-sidebar-modal .modal-footer').fadeIn(750);
			},

			hideApplyFiltersPanel: function () {
				$('#dashboard-sidebar-modal .modal-footer').fadeOut(100);
			},

			hideNoResultsPanel: function () {
				Bark.seller.debug('- hideNoResultsPanel()');
				$('.seller-dash-v2').removeClass('no-results-returned');
				$('.seller-dash-v2 .no-results').fadeOut(200);

				$('.desktop-no-results').removeClass('d-lg-flex');
				$('.tablet-no-results').removeClass('d-md-block');
				$('.mobile-no-results').removeClass('d-block');
			},

			updateLatestRefresh: function () {
				if (Bark.seller.lastRefresh) {
					const $lastUpdated = Bark.seller.shouldSeeNewHeader
						? $('#js-last-updated-parsed-time span')
						: $('.last-updated span');
					$lastUpdated.text(
						Bark.seller.getTimeSinceForRefresh(Bark.seller.lastRefresh.getTime() / 1000),
					);
				}
			},

			checkOneClickViaEmail: function () {
				if (
					typeof Bark.ProjectsData.one_click_to_respond === 'undefined' ||
					!Bark.seller.isRespondingToAProject()
				)
					return false;

				if (Bark.ProjectsData.one_click_to_respond != Bark.seller.currentlyShownProjectId)
					return false;

				if ($('.seller-dash-v2 .one-click-response').length > 0) {
					$('.seller-dash-v2 .one-click-response').trigger('click');
				} else {
					Bark.seller.processSubmitYourDetails(true);
				}

				QS.deleteValue('emailact');
				Bark.ProjectsData.one_click_to_respond = false;
			},

			renderRecords: function () {
				Bark.seller.debug('- renderRecords()');
				var projects = Bark.seller.getAllProjectsData();

				// Update total
				$('.seller-dash-v2 .results-count').show();
				$('.seller-dash-v2 .results-count span').text(
					Bark.seller.getProjectsTotalCount(),
				);

				Bark.seller.updateRecordCount();
				Bark.seller.updateFilterIconView();
				this.utcTime = moment.utc();

				if (shouldShowLeadsListRct()) {
					Bark.seller.debug('- loadLeadsListRct()');
					loadLeadsListRct(entityTracking);
				} else {
					for (var i = 0; i < projects.length; i++) {
						var newProjectObject = Bark.seller.transformApiProjectResponseToOldFormat(
							projects[i],
						);
						this.utcTime = moment.utc();
						Bark.seller.renderRecord(newProjectObject);
					}
				}

				// Hide loader
				Bark.seller.hideListLoader();

				$('.all-projects-header').removeClass('d-none');

				if (!shouldShowLeadsListRct()) {
					// Bark.seller.moreRecords = (Bark.seller.getProjectsTotalCount() > Bark.seller.getNumberOfVisibleProjects()) ? true : false;

					// Show / hide 'Load more' button
					if (Bark.seller.moreRecords) {
						Bark.seller.showLoadMoreButton();
					} else {
						Bark.seller.hideLoadMoreButton();
					}
				}

				const listIsEmpty = shouldShowLeadsListRct()
					? !Bark.ProjectsData.projects.length
					: !$('.dashboard-projects .items div').length;
				// Show first project on the list
				if (
					(projects.length || Bark.seller.isRespondingToAProject()) && // if there are projects to show (including the one theyre replying to)
					(Bark.seller.showFirstItem || listIsEmpty) && // And filling an empty list (on load or new search)
					(!Bark.seller.isMobile ||
						(Bark.seller.isMobile && Bark.seller.isRespondingToAProject())) // and not on mobile or on mobile and is responding to a project
				) {
					Bark.seller.showFirstProject();
				}

				Bark.seller.processNoRecords();
			},

			updateRecordCount: function () {
				Bark.seller.page === 'leads'
					? $('.total-leads-text').html(
							_t('seller_dashboard:leads-page.update-record-count.total-leads.text', {
								count: Bark.seller.unfilteredTotal,
								number_of_leads: Bark.seller.formatNumber(Bark.seller.unfilteredTotal),
								lead_preference_link:
									'<a data-cy="dashboard-filters-edit" href="' +
									_l('/settings/leads/my_services/') +
									'">' +
									_t('seller_dashboard:leads-page.update-record-count.total-leads.link') +
									'</a>',
								interpolation: { escapeValue: false },
							}),
					  )
					: $('.total-leads-text').html(
							_t('seller_dashboard:leads-page.update-record-count.total-responses.text', {
								count: Bark.seller.unfilteredTotal,
								number_of_responses: Bark.seller.formatNumber(
									Bark.seller.unfilteredTotal,
								),
								lead_preference_link:
									'<a data-cy="dashboard-filters-edit" href="' +
									_l('/settings/leads/my_services/') +
									'">' +
									_t('seller_dashboard:leads-page.update-record-count.total-leads.link') +
									'</a>',
								interpolation: { escapeValue: false },
							}),
					  );

				if (Bark.seller.dataFiltered) {
					if (Bark.ProjectsData.show_unread) {
						if (Bark.seller.page === 'leads') {
							var results_count_filter = _t(
								'seller_dashboard:leads-page.update-record-count.showing-unread-leads-filter',
								{
									count: Bark.seller.getProjectsTotalCount(),
									number_of_leads: Bark.seller.formatNumber(
										Bark.seller.getProjectsTotalCount(),
									),
								},
							);
						} else {
							var results_count_filter = _t(
								'seller_dashboard:leads-page.update-record-count.unread-responses-filter',
								{
									count: Bark.seller.getProjectsTotalCount(),
									number_of_leads: Bark.seller.formatNumber(
										Bark.seller.getProjectsTotalCount(),
									),
								},
							);
						}
					} else {
						if (Bark.seller.page === 'leads') {
							var results_count_filter = _t(
								'seller_dashboard:leads-page.update-record-count.showing-filtered-leads-filter',
								{
									count: Bark.seller.getProjectsTotalCount(),
									number_of_leads: Bark.seller.formatNumber(
										Bark.seller.getProjectsTotalCount(),
									),
								},
							);
						} else {
							var results_count_filter = _t(
								'seller_dashboard:leads-page.update-record-count.filtered-responses-filter',
								{
									count: Bark.seller.getProjectsTotalCount(),
									number_of_leads: Bark.seller.formatNumber(
										Bark.seller.getProjectsTotalCount(),
									),
								},
							);
						}
					}

					$('.results-count-filter').text(results_count_filter);
				} else {
					if (Bark.seller.page === 'leads') {
						$('.results-count-filter').text(
							_t(
								'seller_dashboard:leads-page.update-record-count.showing-all-leads-filter',
								{
									count: Bark.seller.getProjectsTotalCount(),
									number_of_leads: Bark.seller.formatNumber(
										Bark.seller.getProjectsTotalCount(),
									),
								},
							),
						);
					} else {
						$('.results-count-filter').text(
							_t('seller_dashboard:leads-page.update-record-count.all-responses-filter', {
								count: Bark.seller.getProjectsTotalCount(),
								number_of_leads: Bark.seller.formatNumber(
									Bark.seller.getProjectsTotalCount(),
								),
							}),
						);
					}
				}

				Bark.seller.page === 'leads' &&
					Bark.seller.updateNewHeaderLeadCountsReact(
						Bark.seller.dataFiltered,
						Bark.ProjectsData.projectsTotal,
						Bark.seller.unfilteredTotal,
					);
			},

			updateFilterIconView: function () {
				if (Bark.seller.dataFiltered) {
					$('.fresh-filter-row').removeClass('d-flex').addClass('d-none');
					$('.edit-filters-row').removeClass('d-none');
				} else {
					$('.edit-filters-row').addClass('d-none');
					$('.fresh-filter-row').removeClass('d-none').addClass('d-flex');
				}
			},
			showContactPreferences: function (
				record,
				isUrgent,
				isContactPrefTypePhone = null,
				isContactPrefTypeSms = null,
				isContactPrefTypeEmail = null,
			) {
				let contactPreferenceHtml = getContactPreferencesText(
					'lead-list',
					isUrgent,
					isContactPrefTypePhone,
					isContactPrefTypeSms,
					isContactPrefTypeEmail,
				);
				if (contactPreferenceHtml) {
					if (isUrgent) {
						record.find('.urgent .contact-text').html(contactPreferenceHtml);
						record.find('.urgent').removeClass('d-none').addClass('d-flex');
					} else {
						record.find('.contact .contact-text').html(contactPreferenceHtml);
						record.find('.contact').removeClass('d-none').addClass('d-flex');
					}
				}
			},
			showProjectDetailContactPreferences: function (
				projectDetailsContactPreferences,
				isUrgent,
				isContactPrefTypePhone = null,
				isContactPrefTypeSms = null,
				isContactPrefTypeEmail = null,
			) {
				let contactPreferenceHtml = getContactPreferencesText(
					'lead-details',
					isUrgent,
					isContactPrefTypePhone,
					isContactPrefTypeSms,
					isContactPrefTypeEmail,
				);
				if (contactPreferenceHtml) {
					if (isUrgent) {
						projectDetailsContactPreferences
							.find('.urgent .contact-text')
							.html(contactPreferenceHtml);
						projectDetailsContactPreferences
							.find('.urgent')
							.removeClass('d-none')
							.addClass('d-flex');
					} else {
						projectDetailsContactPreferences
							.find('.contact .contact-text')
							.html(contactPreferenceHtml);
						projectDetailsContactPreferences
							.find('.contact')
							.removeClass('d-none')
							.addClass('d-flex');
					}
				}
			},
			renderRecord: function (item) {
				const {
					summary,
					project_title,
					project_id,
					buyer_share_name,
					highlights,
					response_count,
					last_message,
					has_images,
					has_files,
					project_detail,
					credits_price,
					credits_price_after_discount,
				} = item;

				// Only render those records that don't exist in DOM
				if ($('.dashboard-projects div[data-project-id="' + project_id + '"]').length) {
					return false;
				}

				// Add the basics: like the project ID and the buyers name
				const record = $('#js-record-template .dashboard-projects-item').clone();
				record.attr('data-project-id', project_id);
				record.data('project-id', project_id);
				record.find('.project-title-name').text(buyer_share_name);
				const recordParentSelector = Bark.seller.isInNewUIExperiment
					? '.js-with-new-ui'
					: '.with-highlights';
				record
					.find(`${recordParentSelector} .project-item-highlights-title .title`)
					.html(project_title);
				let hasAdditionalDetails = false;

				// Does the project have additional details (or files, or images)?
				if (has_images || has_files || project_detail?.length > 0) {
					hasAdditionalDetails = true;
				}
				// Add a truncated project summary to the additional details section.
				const maxDisplayLengthForSummary = hasAdditionalDetails ? 60 : 75;
				if (hasAdditionalDetails) {
					if (Bark.seller.isInNewUIExperiment) {
						// We deal with this further on, in the truncated summary parsing
					} else {
						record
							.find(
								`${recordParentSelector} .project-item-highlights-title .additional-details-label`,
							)
							.removeClass('d-none');
					}
				}

				if (summary.length) {
					let summaryWithStartDivider = hasAdditionalDetails ? '/ ' + summary : summary;
					let truncatedSummary =
						summaryWithStartDivider.length > maxDisplayLengthForSummary
							? summaryWithStartDivider.substring(0, maxDisplayLengthForSummary) +
							  '&hellip;'
							: summaryWithStartDivider;
					truncatedSummary = truncatedSummary
						.split('/')
						.join("<span class='divider'>/</span>");
					if (Bark.seller.isInNewUIExperiment) {
						if (hasAdditionalDetails) {
							truncatedSummary =
								'<span class="text-success font-weight-medium">' +
								_t('seller_leads-dashboard:highlights.additional-details') +
								' </span>' +
								truncatedSummary;
						}
						record
							.find(`${recordParentSelector} .additional-details .summary`)
							.html(truncatedSummary);
					} else {
						record
							.find(`${recordParentSelector} .project-item-highlights-title .summary`)
							.html(truncatedSummary);
					}
				} else {
					// No summary, so bye bye summary block 👋
					if (Bark.seller.isInNewUIExperiment) {
						record.find(`${recordParentSelector} .additional-details .summary`).empty();
					} else {
						record
							.find(`${recordParentSelector} .additional-detail-title .summary`)
							.empty();
					}
				}

				if (credits_price_after_discount) {
					let formattedCreditsPriceAfterDiscount = currency(credits_price_after_discount);
					record
						.find(
							`${recordParentSelector} .price-on-leads-details .leads-details-discounted-price`,
						)
						.html(formattedCreditsPriceAfterDiscount);
				}
				if (credits_price) {
					let formattedCreditsPrice = currency(credits_price);
					record
						.find(
							`${recordParentSelector} .price-on-leads-details .leads-details-original-price`,
						)
						.html(formattedCreditsPrice);
				}

				// Add the badge
				if (highlights?.length > 0) {
					// Remove the project-suggested section
					record.find('.project-suggested').remove();
					const messageCodeToStringMap = {
						'profile-view': _t('seller_dashboard:record.profile-view.text', {
							profile_view_span:
								'<span class="font-weight-medium">' +
								_t('seller_dashboard:record.profile-view.profile-view-span') +
								'</span>',
							interpolation: { escapeValue: false },
						}),
						'request-quote': _t('seller_dashboard:record.request-quote.text', {
							request_quote_span:
								'<span class="font-weight-medium">' +
								_t('seller_dashboard:record.request-quote.request-quote-span') +
								'</span>',
							interpolation: { escapeValue: false },
						}),
						message: _t('seller_dashboard:record.message.text', {
							message_span:
								'<span class="font-weight-medium">' +
								_t('seller_dashboard:record.message.message-span') +
								'</span>',
							interpolation: { escapeValue: false },
						}),
						'resubmitted-project': _t(
							'seller_dashboard:record.resubmitted-project.text',
							{
								resubmitted_project_span:
									'<span class="font-weight-medium">' +
									_t(
										'seller_dashboard:record.resubmitted-project.resubmitted-project-span',
									) +
									'</span>',
								interpolation: { escapeValue: false },
							},
						),
						'call-me': _t('seller_dashboard:record.call-me.text', {
							call_me_span:
								'<span class="font-weight-bold">' +
								_t('seller_dashboard:record.call-me.call-me-span') +
								'</span>',
							interpolation: { escapeValue: false },
						}),
					};
					// Get only the newest highlight
					let highlight = highlights.hasMax('timestamp');
					let messageHTML = messageCodeToStringMap[highlight.action];
					if (messageHTML) {
						if (Bark.seller.isInNewUIExperiment) {
							record
								.find(`${recordParentSelector} .js-badge-dynamic`)
								.removeClass('d-none')
								.addClass('d-flex');
							record
								.find(`${recordParentSelector} .js-badge-dynamic-message-container`)
								.html(messageHTML);
							if (summary.length) {
								record
									.find(`${recordParentSelector} .js-badges-container`)
									.addClass('mt-3');
							}
						} else {
							record
								.find(
									`${recordParentSelector} .project-item-highlights-badge .lead-highlights-badge-message`,
								)
								.html(messageHTML);
							record
								.find(`${recordParentSelector} .project-item-highlights-badge`)
								.removeClass('d-none')
								.addClass('d-inline-flex')
								.addClass(highlight.action.replace('-', '_'));
						}
					}
				}

				let hasContactPreferences =
					item?.is_contact_pref_type_phone ||
					item?.is_contact_pref_type_sms ||
					item?.is_contact_pref_type_email;
				if (Bark.seller.isInNewUIExperiment && hasContactPreferences) {
					record
						.find('.js-contact-preferences-row')
						.removeClass('d-none')
						.addClass('d-flex');
					record
						.find(`${recordParentSelector} .js-badge-urgent`)
						.removeClass('d-flex')
						.addClass('d-none');
					Bark.seller.showContactPreferences(
						record,
						item.is_urgent,
						item.is_contact_pref_type_phone,
						item.is_contact_pref_type_sms,
						item.is_contact_pref_type_email,
					);
				} else if (item.is_urgent && Bark.seller.isInNewUIExperiment) {
					record
						.find(`${recordParentSelector} .js-badge-urgent`)
						.removeClass('d-none')
						.addClass('d-inline-flex');
				} else if (item.is_urgent) {
					record
						.find('.project-item-urgent-badge')
						.removeClass('d-none')
						.addClass('d-inline-flex');
				}

				if (item.is_top_opportunity) {
					record
						.find(`${recordParentSelector} .js-top-opportunity-badge`)
						.removeClass('d-none')
						.addClass('d-inline-flex');
				}

				// Show "Be the first to respond" fake link to get sellers to click
				if (!response_count) {
					if (Bark.seller.isInNewUIExperiment) {
						record
							.find(`${recordParentSelector} .first-to-respond`)
							.removeClass('d-none');
					} else {
						if (record.find('.project-item-highlights-badge.d-none').length > 0) {
							record
								.find(`${recordParentSelector} .first-to-respond`)
								.addClass('mt-0')
								.removeClass('d-none');
						} else {
							record
								.find(`${recordParentSelector} .first-to-respond`)
								.addClass('mt-2')
								.removeClass('d-none');
						}
					}
				}

				// Show how recently the lead was updated -- NB: not how long ago it was posted.
				if (!last_message) {
					const minutesUntilDayThreshold = 1290;
					const timeSinceLeadUpdated = moment
						.utc(item.since)
						.locale('dashboard')
						.from(this.utcTime);
					if (
						typeof item.since !== 'undefined' &&
						this.getDuration(item.since) < minutesUntilDayThreshold
					) {
						record
							.find('.highlights.project-posted-ago')
							.removeClass('d-none')
							.find('.posted_ago')
							.text(timeSinceLeadUpdated);
					} else {
						record
							.find('.project-posted-ago:not(.highlights)')
							.removeClass('d-none')
							.find('.posted_ago')
							.text(timeSinceLeadUpdated);
					}
				}

				Bark.seller.renderRecordAdditionalGenericActions(item, record);
			},

			// This is the majority of the original `renderRecord` function
			// Contains record rendering functionality that is generic: not changed by any experiment.
			// If you add a new experiment to the lead list and you're gonna change how things are being displayed; pull stuff out of here as needed.
			renderRecordAdditionalGenericActions: function (item, record) {
				// Sort out the location string for display

				const location_val = $('<div>');
				location_val.append($('<div>').text(item.city_string).html());
				if (!item.is_local) {
					location_val.append(` (${item.national_label})`);
				}
				record.find('.project-name-location .location').html(location_val.html());

				if (!item.last_message) {
					record
						.find('.project-posted-ago .posted_ago')
						.text(moment.utc(item.since).locale('dashboard').from(this.utcTime));
				}

				record
					.find('.project-credits span.num-credits-resp')
					.html(Bark.seller.calculateProjectCredits(item));
				if (!item.categ_promo) {
					record.find('.project-promo').remove();
				}
				if (item.resubmitted_project) {
					record.find('.js-project-resubmitted').show();
					$(document)
						.off('click.jsProjectResubmittedExplainer')
						.on(
							'click.jsProjectResubmittedExplainer',
							'.js-project-resubmitted-explainer',
							() => {
								$('#whatIsRelistedExplain').modal('show');
								Bark.logEvent('what-is-relisted', 'click', 'bark-resubmitted');
								return false;
							},
						);
				} else {
					record.find('.js-project-resubmitted').hide();
				}

				if ($('.js-remote-explainer').length) {
					$(document)
						.off('click.jsRemoteExplainer')
						.on('click.jsRemoteExplainer', '.js-remote-explainer', () => {
							$('#whatIsRemoteExplain').modal('show');
							Bark.logEvent('what-is-remote', 'click', 'bark-seller');
							return false;
						});
				}

				if (item.is_shortlisted === true && item.buyer_interaction_top_text) {
					record
						.find('.project-suggested .suggestions_text')
						.text(item.buyer_name + ' has ' + item.buyer_interaction_top_text);
				}
				if (!item.viewed) {
					record.find('.unread-notification').addClass('show');
					record.find('.project-item-title').addClass('bold');
					record.data('unread', true);
				}

				if (item.is_shortlisted != true || item.last_message) {
					record.find('.project-suggested').remove();
				}

				var message = record.find('.latest-action .message');

				if (item.last_message) {
					message.text(Bark.seller.truncateMessage(item.last_message.label, 70));

					if (!item.last_message.is_read && item.last_message.sender != 'seller') {
						message.addClass('text-primary strong');
					}

					record
						.find('.latest-action .time-posted')
						.text(Bark.seller.getTimeSince(item.last_message.time));

					message.closest('.latest-action').removeClass('d-none');
				} else {
					message.closest('.latest-action').addClass('d-none');
				}
				if (
					Bark.seller.isRespondingViaPaymentRedirect() &&
					parseInt(Bark.ProjectsData.projectToRespond) === parseInt(item.project_id)
				) {
					let stickyText = _t('seller_dashboard:leads-page.your-lead-from-last-visit');
					let stickyHeader = $('<div>', {
						class: 'justify-content-between align-items-center text-xs d-flex px-4 py-3',
					}).append($('<div>').text(stickyText));
					$('.dashboard-projects #sticky-project').append(stickyHeader).append(record);
				} else if (
					parseInt(Bark.ProjectsData.projectToRespond) === parseInt(item.project_id)
				) {
					var stickyText = '';
					switch (Bark.ProjectsData.projectToRespondType) {
						case 'web-notification':
						case 'webpush':
							stickyText = _t('seller_dashboard:leads-page.your-lead-from-notification');
							break;
						default:
							stickyText = _t('seller_dashboard:leads-page.your-lead-from-email');
							break;
					}
					var stickyHeader = $('<div>', {
						class: 'justify-content-between align-items-center text-xs d-flex px-4 py-3',
					}).append($('<div>').text(stickyText));
					$('.dashboard-projects #sticky-project').append(stickyHeader).append(record);
				} else {
					$('.dashboard-projects .items').append(record);
				}

				// currently used for relisted leads
				$('[data-toggle="popover"]').popover();
			},

			truncateMessage: function (string, length) {
				if (string.length > length) return string.substring(0, length) + '...';
				else return string;
			},

			getTimeSince: function (timestamp) {
				return moment(timestamp * 1000)
					.locale('dashboard')
					.fromNow();
			},

			getTimeSinceForRefresh: function (timestamp) {
				return moment(timestamp * 1000)
					.locale('dashboard')
					.fromNowOrNow();
			},

			displayProjectError: function (projectId, defaultMessage) {
				var message = defaultMessage;
				if (typeof Bark.seller.projectErrors[projectId] !== 'undefined') {
					message = Bark.seller.projectErrors[projectId];
				}
				Bark.alertModal(message);
			},

			showProject: function (projectId) {
				Bark.seller.debug('- showProject()');
				var project = Bark.seller.getProjectDataById(projectId);

				if (!project) {
					tracking('seller-leads-dashboard', 'Project to respond to not found', {
						project_id: projectId,
						seller_profile_id: Bark.seller.id,
						is_responding_to_prx: projectId === Bark.ProjectsData.projectToRespond,
						is_responding_to_prospect_lead:
							Bark.ProjectsData.projectToRespondType === 'prospect',
						seller_was_prospect: Number(Boolean(Bark?.USER?.sellerWasProspect)),
					});

					Bark.api(
						`seller/bark/${projectId}`,
						{},
						function (data) {
							if (data.data.is_low_quality) {
								setTimeout(function () {
									Bark.seller.showLowQualityWarningModal(projectId);
								}, 200);

								Bark.seller.removeProjectToRespond();
								Bark.seller.showFirstProject(true);

								return false;
							}

							if (projectId === Bark.ProjectsData.projectToRespond) {
								if (Bark.ProjectsData.projectToRespondType === 'prospect') {
									Bark.seller.initProspectResponseFlow(projectId);
								} else {
									setTimeout(function () {
										Bark.seller.displayProjectError(
											projectId,
											_t('seller_dashboard:leads-page.error-message.unavailable'),
										);
									}, 500);
								}

								Bark.seller.removeProjectToRespond();
								Bark.seller.showFirstProject();
							} else {
								Bark.seller.displayProjectError(
									projectId,
									_t('seller_dashboard:leads-page.error-message.unavailable'),
								);
								return false;
							}
						},
						function (err) {
							bugsnagClient.notify(
								new Error(`Could not refetch requested project by specific request.`),
							);
							tracking(
								'seller-leads-dashboard',
								'Project data by id is not found during retry',
								{
									project_id: projectId,
									seller_profile_id: Bark.seller.id,
									is_responding_to_prx: projectId === Bark.ProjectsData.projectToRespond,
								},
							);
							return false;
						},
						'GET',
						Bark.apiVersionHeader('v2'),
					);

					return false;
				} else {
					if (!Bark.seller.isMobile) {
						window.scrollTo(0, 0);
					}

					Bark.seller.sendMessageFormShown = false;

					// Render project data
					Bark.seller.renderProjectDetails(projectId);

					$('.dashboard-projects-item').removeClass('selected');
					$('.dashboard-projects div[data-project-id="' + projectId + '"]').addClass(
						'selected',
					);

					if (Bark.seller.isMobile) {
						setTimeout(function () {
							Bark.seller.currentScrollTop = $(document).scrollTop();

							// hide the header and stuff
							$('body').css({ 'padding-top': 0 });
							$('#header').hide({ duration: 0 });

							$('.seller-dash-v2 .dashboard-side-menu').hide({ duration: 0 });
							$('.seller-dash-v2 .dashboard-side-menu ul').removeClass('open');
							$('.seller-dash-v2 .dashboard-projects').hide({ duration: 0 });
							window.scrollTo(0, 0);
							$('.seller-dash-v2 .dashboard-project-details').fadeIn({ duration: 150 });
						}, 90);

						// Set the page state, which will allow the user to go back to the lead list if they click the back button
						Bark.setPageState({ projectId: projectId });
					} else {
						$('.seller-dash-v2 .dashboard-project-details').fadeIn({ duration: 100 });
					}
					$('.seller-dash-v2 .dashboard-project-details').removeClass('d-none');

					if (shouldShowLeadsListRct() && LeadsListRctDispatcher) {
						LeadsListRctDispatcher({ type: 'setSelectedProjectId', value: projectId });
					}
				}
			},

			showCampaignEditor: function () {
				if (typeof window.Bract === 'undefined') {
					return false;
				}

				$(
					'.seller-dash-v2 .calls-to-action.for-leads, .seller-dash-v2 .project-credits-to-respond',
				).addClass('d-none');
				$('.template-editor-wrapper').removeClass('d-none');
				window.Bract.loadDefault();
			},

			hideCampaignEditor: function () {
				$('.template-editor-wrapper').addClass('d-none');
				$(
					'.seller-dash-v2 .calls-to-action.for-leads, .seller-dash-v2 .project-credits-to-respond',
				).removeClass('d-none');
			},

			hideProject: function () {
				Bark.seller.debug('- hideProject()');
				$('.seller-dash-v2 .dashboard-side-menu').show({ duration: 0 });
				$('.seller-dash-v2 .dashboard-projects').show({ duration: 0 });
				$('.seller-dash-v2 .dashboard-project-details').hide({ duration: 0 });

				if (Bark.seller.isMobile) {
					// show the header
					$('#header').show({ duration: 0 });

					window.scrollTo(0, Bark.seller.currentScrollTop);

					// Bark.seller.currentlyShownProjectId = null;
				}
			},
			getDuration: function (dateAsString) {
				return moment.utc().diff(moment.utc(dateAsString), 'minutes');
			},

			renderProjectDetails: function (projectId) {
				Bark.seller.debug('- renderProjectDetails()');
				var project = Bark.seller.getProjectDataById(projectId);
				if (!project) {
					return false;
				}

				var opacityDuration = Bark.seller.isMobile ? 0 : 50;

				// Show / hide page specific buttons / content
				$('.seller-dash-v2 .dashboard-project-details .calls-to-action.for-leads').hide();
				$(
					'.seller-dash-v2 .dashboard-project-details .calls-to-action.for-my-responses',
				).hide();
				$(
					'.seller-dash-v2 .dashboard-project-details .project-credits-to-respond',
				).hide();
				$('.seller-dash-v2 .dashboard-project-details .project-promo').hide();

				$('.seller-dash-v2 .dashboard-project-details .info-message').hide();
				$('.seller-dash-v2 .dashboard-project-details .view-messages').off();

				$('.seller-dash-v2 .dashboard-project-details .have-been-hired').hide();
				$('.dashboard-project-details .error-message').hide();

				if (Bark.seller.page == 'leads') {
					$(
						'.seller-dash-v2 .dashboard-project-details .project-credits-to-respond',
					).show();
					if (project.customer_order === null) {
						$(
							'.seller-dash-v2 .dashboard-project-details .calls-to-action.for-leads',
						).show();
						$(
							'.seller-dash-v2 .dashboard-project-details .submit-your-details, .seller-dash-v2 .dashboard-project-details .not-interested',
						).show();

						// //
						// $(
						//     '.seller-dash-v2 .response-cap-and-count-container',
						// ).show();

						// $(
						//     '.seller-dash-v2 .dashboard-project-details .calls-to-action.for-leads',
						// )

						// $(
						//     '.seller-dash-v2 .dashboard-project-details .calls-to-action.for-leads',
						// )
					}

					if (shouldShowMobileStickyHeaderRct()) {
						setMobileStickyHeaderRctShowPassBtn(true);
					}

					$('.seller-dash-v2 .dashboard-project-details .buyer-telephone').hide();
					$('.seller-dash-v2 .dashboard-project-details .seller-quote').hide();
					$('.seller-dash-v2 .dashboard-project-details .buyer-email').hide();
					$('.seller-dash-v2 .dashboard-project-details .project-top-header').removeClass(
						'just-responded',
					);
				}
				if (Bark.seller.page == 'my-responses') {
					$(
						'.seller-dash-v2 .dashboard-project-details .calls-to-action.for-my-responses',
					).show();
					$('.seller-dash-v2 .dashboard-project-details .project-top-header').removeClass(
						'just-hired',
					);
					$('.seller-dash-v2 .dashboard-project-details .have-been-hired').show();
				}
				if (Bark.seller.page == 'bundle') {
					$('.seller-dash-v2 .dashboard-project-details .view-messages').hide();
				}
				if (Bark.seller.page == 'hired') {
					$('.seller-dash-v2 .dashboard-project-details .view-messages').show();
					$(
						'.seller-dash-v2 .dashboard-project-details .calls-to-action.for-hired',
					).show();
				}
				if (Bark.seller.page == 'not-interested') {
					$(
						'.seller-dash-v2 .dashboard-project-details .calls-to-action.for-not-interested',
					).show();
					$(
						'.seller-dash-v2 .dashboard-project-details .project-credits-to-respond',
					).show();
					$('.seller-dash-v2 .dashboard-project-details .buyer-telephone').hide();
					$('.seller-dash-v2 .dashboard-project-details .buyer-email').hide();
					$('.seller-dash-v2 .dashboard-project-details .seller-quote').hide();
					$('.seller-dash-v2 .dashboard-project-details .project-top-header').removeClass(
						'just-responded',
					);
				}

				// blink and update data
				Bark.seller.currentlyShownProjectId = projectId;
				project.customer_order === null && Bark.seller.GHGCreditStatus.render(projectId);

				if (shouldShowBookingDetailsRct()) {
					loadProjectDetailsBookingDetails(project, entityTracking);
				}

				if (shouldShowLeadsListRct()) {
					loadValueBadgesLeadDetailsRct(project);
				}

				if (shouldShowProjectDetailsLeadDetailsRct()) {
					loadProjectDetailsLeadDetailsRct(project);
				}

				if (shouldShowResponsesLeadDetailsRct()) {
					loadResponsesLeadDetailsRct(project);
				}

				if (shouldShowBuyerInteractionLeadDetailsRct()) {
					loadBuyerInteractionLeadDetailsRct(project);
				}

				if (shouldShowMobileStickyHeaderRct()) {
					loadMobileStickyHeaderRct(project);
				}

				if ($('#download-app-modal').length) {
					if (window.SellerAppDashboard) {
						window.SellerAppDashboard.launchModule(
							'download-app-modal',
							'download-app-modal',
							{
								isOpen: true,
								sellerProfileId: Bark.seller.id,
							},
						);
					} else {
						tracking('Download App Modal', 'Seller dashboard react app not loaded');
					}
				}

				if ($(`#complete-profile-highlight`).length) {
					let elementBuilt = false;
					if (
						CompleteProfileHighlightDispatcher &&
						$(`#complete-profile-highlight`).children().length
					) {
						try {
							CompleteProfileHighlightDispatcher({
								type: 'setBuyerName',
								value: project.buyer_share_name,
							});
							elementBuilt = true;
						} catch (e) {}
					}

					if (!elementBuilt) {
						if (window.SellerAppDashboard) {
							window.SellerAppDashboard.launchModule(
								'complete-profile-highlight',
								'complete-profile-highlight',
								{
									sellerProfileId: Bark.seller.id,
									sellerName: Bark.seller.company_name,
									buyerName: project.buyer_share_name,
									shouldDisplayHighlight: Bark?.USER?.shouldShowCompleteProfileHighlight,
									onMount: (dispatcher, openModal, showHighlight) => {
										CompleteProfileHighlightDispatcher = dispatcher;
										openCompleteProfileModal = openModal;
										showCompleteProfileHighlight = showHighlight;
									},
									onMadeChanges: () => {
										// Some changes made, we no longer need this flow so remove its references
										$(`#complete-profile-highlight`).hide();
										CompleteProfileHighlightDispatcher = null;
										openCompleteProfileModal = null;
										showCompleteProfileHighlight = null;
									},
								},
							);
						} else {
							tracking('seller-leads-dashboard', 'Seller dashboard react app not loaded');
						}
					}
				}

				// modals
				$('.response-modal-name').text(cleanse(project.buyer_name));
				$('.response-modal-current-required').text(project.credits_required);
				$('#sellerHiredModal .buyer-name').html(cleanse(project.buyer_name));

				// project details
				$('#barkSendEstimateModal .respond-message-estimate')
					.attr('data-project-response-id', '')
					.attr('data-buyer-id', '');
				$(
					'.seller-dash-v2 .respond-message-estimate, .seller-dash-v2 .have-been-hired-form',
				).hide();
				$(
					'.seller-dash-v2 .info-message-sent, .seller-dash-v2 .info-message-later',
				).hide();
				$(
					'.seller-dash-v2 .dashboard-project-details .project-credits-to-respond span.num-credits-resp',
				).html(Bark.seller.calculateProjectCredits(project));
				$('.seller-dash-v2 .dashboard-project-details').attr(
					'data-project-id',
					project.id,
				);
				$('.seller-dash-v2 .dashboard-project-details').data('project-id', project.id);

				const projectHighlightsElement =
					'.seller-dash-v2 .dashboard-project-details .project-details-highlights';
				const updatedRequestsModalElement = '#updated-requests-modal';
				const isUrgentHighlightElement = $(
					'.seller-dash-v2 .dashboard-project-details .project-details-urgent',
				);
				const topOpportunityHighlightElement = $(
					'.seller-dash-v2 .dashboard-project-details .project-details-top-opportunity',
				);

				if (project.resubmitted_project && $(projectHighlightsElement).length > 0) {
					if (project.since && typeof this.utcTime !== 'undefined') {
						let timeBasis = moment.unix(project.created);
						const timeStringEl = $(projectHighlightsElement).find('.time-string');
						if (project?.highlights.length > 0) {
							const resubmittedProjectHighlight = project?.highlights.filter(function (
								value,
							) {
								return value?.action === 'resubmitted-project';
							});
							if (resubmittedProjectHighlight.length > 0) {
								const resubmittedTimestamp =
									resubmittedProjectHighlight.hasMax('timestamp');
								if (
									typeof resubmittedTimestamp !== 'undefined' &&
									typeof resubmittedTimestamp['timestamp'] !== 'undefined'
								) {
									timeBasis = moment.utc(resubmittedTimestamp['timestamp']);
								}
							}
						}
						if (!timeBasis || moment.utc(project.since).isSameOrBefore(timeBasis)) {
							timeBasis = moment.utc(project.since);
						}
						timeStringEl.text(timeBasis.locale('dashboard').from(this.utcTime));
						$(projectHighlightsElement).find('.highlights-interaction-info').show();
					}
					$(projectHighlightsElement).removeClass('d-none').show();
					$('.project-details-highlights .infobox-icon')
						.off('click.modal')
						.on('click.modal', function () {
							$(`${updatedRequestsModalElement}`).modal('show');
						});
				} else {
					$(projectHighlightsElement).hide();
				}

				let hasContactPreferences =
					project?.is_contact_pref_type_phone ||
					project?.is_contact_pref_type_sms ||
					project?.is_contact_pref_type_email;

				let projectDetailsContactPreferences = $(
					sellerDashboardProjectDetailsSelector,
				).find('.project-details-contact-preferences');
				projectDetailsContactPreferences.addClass('d-none');
				isUrgentHighlightElement.addClass('d-none');

				if (Bark.seller.isInNewUIExperiment && hasContactPreferences) {
					projectDetailsContactPreferences
						.find('.contact-preferences')
						.removeClass('d-flex')
						.addClass('d-none');
					projectDetailsContactPreferences.removeClass('d-none');
					Bark.seller.showProjectDetailContactPreferences(
						projectDetailsContactPreferences,
						project.is_urgent,
						project.is_contact_pref_type_phone,
						project.is_contact_pref_type_sms,
						project.is_contact_pref_type_email,
					);
				} else if (project.is_urgent) {
					isUrgentHighlightElement[project.is_urgent ? 'removeClass' : 'addClass'](
						'd-none',
					);
				}

				if (project.is_top_opportunity) {
					topOpportunityHighlightElement.removeClass('d-none');
				} else {
					topOpportunityHighlightElement.addClass('d-none');
				}

				$('.seller-dash-v2 .dashboard-project-details .project-promo').hide();
				$('.seller-dash-v2 .info-bark-closed')[
					project.project_status == 'closed' ? 'show' : 'hide'
				]();

				if (typeof project.categ_promo !== 'undefined' && project.categ_promo) {
					$('.seller-dash-v2 .dashboard-project-details .project-promo').show();
				}

				// Estimate
				if (typeof project.estimate !== 'undefined' && project.estimate) {
					$(
						'.seller-dash-v2 .dashboard-project-details .project-estimate-text span',
					).html(project.estimate);
					$('.seller-dash-v2 .dashboard-project-details .project-estimate').show();
				} else {
					$('.seller-dash-v2 .dashboard-project-details .project-estimate').hide();
					$(
						'.seller-dash-v2 .dashboard-project-details .project-estimate-text span',
					).html('');
				}

				const timeSinceLeadUpdated = moment
					.utc(project.since)
					.locale('dashboard')
					.from(this.utcTime);
				let notResubmittedProjectHighlight = project?.highlights.filter(function (value) {
					return value?.action !== 'resubmitted-project';
				});

				let postedOrResponded = '';
				if (!(Bark.seller.page == 'leads' || Bark.seller.page == 'bundle')) {
					postedOrResponded = _t(
						'seller_dashboard:project-details.posted-ago-responded',
						{ time_ago: timeSinceLeadUpdated },
					);
				}

				$('.seller-dash-v2 .dashboard-project-details .posted-ago').addClass('d-none');

				if (!postedOrResponded) {
					if (
						Bark.seller.page == 'leads' &&
						notResubmittedProjectHighlight &&
						notResubmittedProjectHighlight.length > 0
					) {
						postedOrResponded = _t(
							'seller_dashboard:project-details.posted-ago-updated',
							{ time_ago: timeSinceLeadUpdated },
						);
					} else {
						postedOrResponded = timeSinceLeadUpdated;
					}
				}

				$('.seller-dash-v2 .dashboard-project-details .posted-ago')
					.removeClass('d-none')
					.text(postedOrResponded);

				var projectTitle = String(
					project.category_name + (project.need_list ? ' | ' : '') + project.need_list,
				);
				projectTitle =
					projectTitle.length > 75
						? projectTitle.substring(0, 75) + '&hellip;'
						: projectTitle;
				// make the part before | bold
				if (projectTitle.indexOf('|') > -1) {
					projectTitle = '<span>' + projectTitle.replace('|', '|</span>');
				}
				// Category too long, | was removed, make the whole thing bold
				else {
					projectTitle = '<span>' + projectTitle + '</span>';
				}

				$('.seller-dash-v2 .dashboard-project-details [data-project-id]').data(
					'project-id',
					project.id,
				);
				$('.seller-dash-v2 .dashboard-project-details .project-title').text(
					project.category_name,
				);
				$('.seller-dash-v2 .dashboard-project-details span.buyer_name').text(
					cleanse(project.buyer_name),
				);
				$('.seller-dash-v2 .dashboard-project-details .project-details').data(
					'buyer-name',
					cleanse(project.buyer_name),
				);
				$('.seller-dash-v2 .dashboard-project-details span.buyer-telephone-display').text(
					project.buyer_telephone,
				);
				$('.seller-dash-v2 .dashboard-project-details span.buyer-email-display').text(
					project.buyer_email,
				);
				$(
					'.seller-dash-v2 .dashboard-project-details .project-name-location .location',
				).text(
					project.is_local
						? project.city_string
						: project.city_string + ' (' + project.national_label + ')',
				);

				if (project.is_local) {
					$(
						'.seller-dash-v2 .dashboard-project-details .project-name-location-notes .location-notes',
					).empty();
					$(
						'.seller-dash-v2 .dashboard-project-details .project-name-location-notes',
					).addClass('hidden');
					$(
						'.seller-dash-v2 .dashboard-project-details .project-name-location-notes',
					).hide();
				} else {
					$(
						'.seller-dash-v2 .dashboard-project-details .project-name-location-notes .location-notes',
					).text(_t('seller_dashboard:project-details.online-or-remote'));
					$(
						'.seller-dash-v2 .dashboard-project-details .project-name-location-notes',
					).removeClass('hidden');
					$(
						'.seller-dash-v2 .dashboard-project-details .project-name-location-notes',
					).show();
				}

				$('.seller-dash-v2 .dashboard-project-details .currency-symbol').html(
					Bark.seller.currency_symbol,
				);

				// Seller quote
				$('.seller-dash-v2 .dashboard-project-details .seller-quote span')
					.html('')
					.parent()
					.hide();
				if (typeof project.quote !== 'undefined' && project.quote) {
					project.quote_type =
						project.quote_type == 'onceoff'
							? _t('seller_dashboard:project-details.quote-type-oneoff')
							: project.quote_type;
					$('.seller-dash-v2 .dashboard-project-details .seller-quote span.quote-value')
						.html(
							Bark.seller.currency_symbol +
								format_currency(project.quote) +
								' / ' +
								project.quote_type,
						)
						.parent()
						.show();
					$('.seller-dash-v2 .dashboard-project-details button.send-estimate').text(
						_t('seller_dashboard:project-details.update-estimate-button'),
					);
				} else {
					$('.seller-dash-v2 .dashboard-project-details button.send-estimate').text(
						_t('seller_dashboard:project-details.send-estimate-button'),
					);
				}

				$('.dashboard-project-details .project-details-action-info').slideUp({
					duration: 100,
				});

				if (typeof project.slug !== 'undefined') {
					$('.seller-dash-v2 .dashboard-project-details .view-messages')
						.off()
						.click(function () {
							var qs = '';
							if ($(this).hasClass('send-estimate')) {
								qs = '?send-estimate=1';
							}

							// Open the messenger in a new tab/window
							if (Bark.isMobile()) {
								window.open('/sellers/messenger/' + project.slug + '/' + qs, '_self');
							} else {
								window.open('/sellers/messenger/' + project.slug + '/' + qs, '_blank');
							}
						});
				}

				if (Bark.seller.page == 'leads') {
					// Not Interested or Decline Request
					if (project.is_seller_shortlisted) {
						$('.dashboard-project-details .calls-to-action .not-interested')
							.addClass('shortlisted')
							.find('span')
							.text(_t('seller_dashboard:project-details.decline-request'));
					} else {
						$('.dashboard-project-details .calls-to-action .not-interested')
							.removeClass('shortlisted')
							.find('span')
							.text(_t('seller_dashboard:project-details.not-interested'));
					}

					// One click response?
					if (
						(typeof Bark.seller.creditStatus.oneClickEnabled !== 'undefined' &&
							Bark.seller.creditStatus.oneClickEnabled) ||
						(typeof Bark.seller.creditStatus.oneClickEnabled === 'undefined' &&
							project.one_click_enabled)
					) {
						$(
							'.dashboard-project-details .calls-to-action .one-click-response, .dashboard-project-details .calls-to-action .send-custom-message',
						).show();
					}
					// Submit my details
					else {
						$('.dashboard-project-details .calls-to-action .send-custom-message').hide();
					}

					// Already responded
					if (Bark.seller.alreadyRespondedToProject(project.id)) {
						if (Bark.seller.wasSellerDismissed(project.id)) {
							// Responded but dismissed
							$('.seller-dash-v2 .dashboard-project-details span.provider_noun').text(
								project.provider_noun,
							);
							$('.seller-dash-v2 .info-dismissed').show();
							$(
								'.seller-dash-v2 .dashboard-project-details .project-top-header',
							).addClass('just-responded');
						} else {
							// Already responded
							$('.seller-dash-v2 .info-already-responded-to-buyer').show();
							$(
								'.seller-dash-v2 .dashboard-project-details .project-top-header',
							).addClass('just-responded');
						}

						$(
							'.seller-dash-v2 .dashboard-project-details .submit-your-details, .seller-dash-v2 .dashboard-project-details .one-click-response, .seller-dash-v2 .dashboard-project-details .send-custom-message, .seller-dash-v2 .dashboard-project-details .not-interested, .seller-dash-v2 .have-been-hired',
						).hide();

						if (shouldShowMobileStickyHeaderRct()) {
							setMobileStickyHeaderRctShowPassBtn(false);
						}

						$('.calls-to-action.for-my-responses').show();
					}

					// Response Count and Cap Experiment: show the relevant information
					if (
						$('.response-cap-and-count-container').length &&
						project.customer_order === null
					) {
						// Reset everything
						$('.response-cap-and-count-container')
							.addClass('d-none')
							.removeClass('d-flex d-lg-inline-flex');
						$('.response-cap-and-count-container .responses-text span').text(null);
						$('.response-cap-and-count-container .response-icon').show();
						$('.response-cap-and-count-container .count-meter').hide();
						$('.response-cap-and-count-container .count-meter .meter-bar').removeClass(
							'on',
						);
						$('.response-cap-and-count-container .infobox-icon')
							.hide()
							.off('click.modal');
						$('.response-cap-and-count-response-requested')
							.addClass('d-none')
							.removeClass('d-flex');
						var showCapInfoIcon = false;

						if (project.response_cap > 0) {
							$('.response-cap-and-count-container .response-cap-text')
								.html(
									_t('seller_dashboard:project-details.response-cap.text', {
										count: project.response_cap,
										strong_span:
											'<strong>' +
											_t('seller_dashboard:project-details.response-cap.strong-span') +
											'</strong>',
										interpolation: { escapeValue: false },
									}),
								)
								.removeClass('d-none');
							if ($('.response-cap-and-count-container .count-meter').length) {
								// Only present for variant 3
								$('.response-cap-and-count-container .count-meter').show();
								$('.response-cap-and-count-container .response-icon').hide();
							}
							showCapInfoIcon = true;
						} else {
							$('.response-cap-and-count-container .response-cap-text').addClass(
								'd-none',
							);
						}
						if (project.response_count > 0) {
							// Response count (variant 2) text
							$('.response-cap-and-count-container .response-count-text').html(
								_t('seller_dashboard:project-details.responded-pros', {
									count: project.response_count,
									strong_span: '<strong>' + project.response_count + '</strong>',
									interpolation: { escapeValue: false },
								}),
							);

							// Response count and cap (variant 3) text
							if (project.response_cap > 0) {
								if (project.response_count > project.response_cap) {
									$(
										'.response-cap-and-count-container .response-cap-and-count-text',
									).html(
										_t('seller_dashboard:project-details.responded-pros-cap', {
											count: project.response_cap,
											strong_span: '<strong>' + project.response_cap + '</strong>',
											interpolation: { escapeValue: false },
										}) +
											'<br><span class="text-grey-200 lh-xl">+' +
											_t('seller_dashboard:project-details.responded-customers-diff', {
												count: project.response_count - project.response_cap,
											}) +
											'</span>',
									);
								} else {
									$(
										'.response-cap-and-count-container .response-cap-and-count-text',
									).html(
										_t('seller_dashboard:project-details.responded-pros-cap', {
											count: project.response_cap,
											strong_span: '<strong>' + project.response_count + '</strong>',
											interpolation: { escapeValue: false },
										}),
									);
								}

								// Turn on the meter bars for the number of responses
								if ($('.response-cap-and-count-container .count-meter').length) {
									$('.response-cap-and-count-container .count-meter .meter-bar')
										.slice(0, project.response_count)
										.addClass('on');
								}

								// Show why this lead is still available, if responses are over the cap (variant 3)
								if (project.response_count >= project.response_cap) {
									var responseInteractionText =
										project.buyer_interaction_text || 'expressed interest in you';
									if (project.buyer_interaction_text) {
										$('.response-cap-and-count-response-requested .text-response').text(
											_t(
												'seller_dashboard:project-details.response-requested-buyer-text',
												{
													buyer_public_name: project.buyer_name,
													response_interaction_text: project.buyer_interaction_text,
												},
											),
										);
									} else {
										$('.response-cap-and-count-response-requested .text-response').text(
											_t(
												'seller_dashboard:project-details.response-requested-auto-text',
												{
													buyer_public_name: project.buyer_name,
												},
											),
										);
									}
									$('.response-cap-and-count-response-requested')
										.removeClass('d-none')
										.addClass('d-flex');
								}
								showCapInfoIcon = true;
							} else {
								// No cap, so just show number of responses in variant 3
								$('.response-cap-and-count-container .response-cap-and-count-text').html(
									_t('seller_dashboard:project-details.responded-pros', {
										count: project.response_count,
										strong_span: '<strong>' + project.response_count + '</strong>',
										interpolation: { escapeValue: false },
									}),
								);
							}
						} else {
							$('.response-count-text', '.response-cap-and-count-container').html(
								_t('seller_dashboard:project-details.first-respond.text', {
									strong_span:
										'<strong>' +
										_t('seller_dashboard:project-details.first-respond.strong-span') +
										'</strong>',
									interpolation: { escapeValue: false },
								}),
							);
							if (project.response_cap > 0) {
								$(
									'.response-cap-and-count-text',
									'.response-cap-and-count-container',
								).html(
									_t('seller_dashboard:project-details.responded-pros-cap', {
										count: project.response_cap,
										strong_span: '<strong>0</strong>',
										interpolation: { escapeValue: false },
									}),
								);
							} else {
								$(
									'.response-cap-and-count-text',
									'.response-cap-and-count-container',
								).html(
									_t('seller_dashboard:project-details.first-respond.text', {
										strong_span:
											'<strong>' +
											_t('seller_dashboard:project-details.first-respond.strong-span') +
											'</strong>',
										interpolation: { escapeValue: false },
									}),
								);
							}
						}
						// Show the cap info icon
						if ($('.response-cap-and-count-container .response-count-text').length) {
							// Only show for variants 1 and 3
							showCapInfoIcon = false;
						}
						if (showCapInfoIcon) {
							$('.response-cap-and-count-container .infobox-icon')
								.show()
								.on('click.modal', function () {
									modalManager.quickModal(
										_t('seller_dashboard:project-details.limited-responses'),
										'',
										'<p class="text-center">' +
											_t('seller_dashboard:project-details.max-pros-for-lead', {
												count: project.response_cap,
											}) +
											'</p>',
										[
											'<span></span>', // This forces the ok-button to be on the right, as the style is justify:space-between
											modalManager.basicOkayButtonHTML(),
										],
										['modal-sm', 'response-cap-info-modal'],
									);
								});
						}
						// Show the response section if we set some text
						if (
							$('.response-cap-and-count-container .responses-text span').text().length >
							0
						) {
							$('.response-cap-and-count-container')
								.removeClass('d-none')
								.addClass('d-flex d-lg-inline-flex');
						}
					} else {
						if (project.customer_order !== null) {
							$('.response-cap-and-count-container')
								.addClass('d-none')
								.removeClass('d-flex d-lg-inline-flex');
						}

						$('.response-cap-and-count-response-requested')
							.addClass('d-none')
							.removeClass('d-flex');
					}
				}

				// my responses
				if (Bark.seller.page == 'my-responses') {
					if (project.buyer_telephone) {
						$('.seller-dash-v2 .dashboard-project-details .buyer-telephone').css({
							display: 'block',
						});
						// $('.seller-dash-v2 .dashboard-project-details .buyer-telephone a').attr('href', 'tel:' + (project.buyer_telephone || '')).text(project.buyer_telephone || '');
						$(
							'.seller-dash-v2 .dashboard-project-details .buyer-telephone a.js-bark-connect',
						)
							.text(project.buyer_telephone || '')
							.data({
								calleeUserType: 'buyer_user',
								calleeUserTypeId: project.buyer_user_id,
								calleeName: project.buyer_name,
								callerNumber: Bark.seller.telephone,
								calleeAvatar: project.buyer_avatar,
								forType: 'project',
								forTypeId: project.id,
							});
						$('.seller-dash-v2 .dashboard-project-details .buyer-telephone-display').html(
							'<a class="text-dark-blue" href="tel:' +
								(project.buyer_telephone || '') +
								'">' +
								(project.buyer_telephone || '') +
								'</a>',
						);
						Bark.BarkConnect.bind();
					} else {
						$('.seller-dash-v2 .dashboard-project-details .buyer-telephone').hide();
					}

					if (project.buyer_email) {
						$('.seller-dash-v2 .dashboard-project-details .buyer-email').css({
							display: 'block',
						});
						$('.seller-dash-v2 .dashboard-project-details .buyer-email a')
							.attr('href', 'mailto:' + (project.buyer_email || ''))
							.text(project.buyer_email || '');
						$('.seller-dash-v2 .dashboard-project-details .buyer-email-display').html(
							'<a class="text-dark-blue" href="mailto:' +
								(project.buyer_email || '') +
								'">' +
								(project.buyer_email || '') +
								'</a>',
						);
					} else {
						$('.seller-dash-v2 .dashboard-project-details .buyer-email').hide();
						$('.seller-dash-v2 .dashboard-project-details .buyer-email a')
							.attr('href', '')
							.text('');
					}

					if (
						typeof project.project_status !== 'undefined' &&
						project.project_status == 2
					) {
						$('.seller-dash-v2 .dashboard-project-details .bark-is-closed').show();
					} else {
						$('.seller-dash-v2 .dashboard-project-details .bark-is-closed').hide();
					}

					var message = $('.dashboard-project-details .latest-action .message');
					if (project.last_message) {
						message.text(Bark.seller.truncateMessage(project.last_message.label, 70));

						if (
							!project.last_message.is_read &&
							project.last_message.sender != 'seller'
						) {
							message.addClass('text-primary strong');
						} else {
							message.removeClass('text-primary strong');
						}

						$('.dashboard-project-details .time-posted').text(
							Bark.seller.getTimeSince(project.last_message.time),
						);
						message.closest('.latest-action').css('opacity', 1);
					} else {
						message.text('');
						$('.dashboard-project-details .time-posted').text('');
						message.closest('.latest-action').css('opacity', 0);
					}
					$('.seller-dash-v2 .dashboard-project-details [data-response-id]')
						.data('response-id', project.resp_id)
						.attr('data-response-id', project.resp_id);
					var projectStatusId = project.response_status_id;
					projectStatusId =
						projectStatusId !== null || projectStatusId > 3 ? projectStatusId : 1;
					$('#project-status-select')
						.val(projectStatusId)
						.data('has-hired-data', project.has_hired_seller_data || false);
					Bark.seller.updateStatusSelectColour();
					$('.notes-input').val(project.note ? project.note : '');
					if (project.has_phoned) {
						$(
							'.seller-dash-v2 .dashboard-project-details input[data-response-contact-type="phone"]',
						).prop('checked', true);
					} else {
						$(
							'.seller-dash-v2 .dashboard-project-details input[data-response-contact-type="phone"]',
						).prop('checked', false);
					}
					if (project.sent_sms) {
						$(
							'.seller-dash-v2 .dashboard-project-details input[data-response-contact-type="sms"]',
						).prop('checked', true);
					} else {
						$(
							'.seller-dash-v2 .dashboard-project-details input[data-response-contact-type="sms"]',
						).prop('checked', false);
					}
					if (project.sent_email) {
						$(
							'.seller-dash-v2 .dashboard-project-details input[data-response-contact-type="email"]',
						).prop('checked', true);
					} else {
						$(
							'.seller-dash-v2 .dashboard-project-details input[data-response-contact-type="email"]',
						).prop('checked', false);
					}
				}

				// hired
				if (Bark.seller.page == 'hired') {
					if (project.buyer_telephone) {
						$('.seller-dash-v2 .dashboard-project-details .buyer-telephone').css({
							display: 'block',
						});
						$('.seller-dash-v2 .dashboard-project-details .buyer-telephone a')
							.attr('href', 'tel:' + (project.buyer_telephone || ''))
							.text(project.buyer_telephone || '');
					} else {
						$('.seller-dash-v2 .dashboard-project-details .buyer-telephone').hide();
					}

					if (project.buyer_email) {
						$('.seller-dash-v2 .dashboard-project-details .buyer-email').css({
							display: 'block',
						});
						$('.seller-dash-v2 .dashboard-project-details .buyer-email a')
							.attr('href', 'mailto:' + (project.buyer_email || ''))
							.text(project.buyer_email || '');
					} else {
						$('.seller-dash-v2 .dashboard-project-details .buyer-email').hide();
						$('.seller-dash-v2 .dashboard-project-details .buyer-email a')
							.attr('href', '')
							.text('');
					}

					if (
						typeof project.project_status !== 'undefined' &&
						project.project_status == 2
					) {
						$('.seller-dash-v2 .dashboard-project-details .bark-is-closed').show();
					} else {
						$('.seller-dash-v2 .dashboard-project-details .bark-is-closed').hide();
					}
				}

				// SellerViewBuyerPhoneVerificationExperiment: show/hide the verified badge
				$('.verified-phone-container')[
					project.is_buyer_phone_verified ? 'show' : 'hide'
				]();

				// Interactions & Messages
				$(
					'.seller-dash-v2 .dashboard-project-details .project-details-insights .interaction-info',
				).hide();
				$(
					'.seller-dash-v2 .dashboard-project-details .project-details-insights .interaction-info .interaction-info-sentence span:not(.bark-svg-icon)',
				).html('');
				$('.seller-dash-v2 .dashboard-project-details .messages-items').html('').hide();
				var i = 0;
				if (project.is_seller_shortlisted === true) {
					let buyerInteractionCalloutText = project.buyer_interaction_callout_text;
					let interactions = $('.project-details-insights .interaction-info-sentence');
					for (let i = 0; i < interactions.length; i++) {
						let interactionSentenceClone = $(interactions[i]).first();
						let parent = $(interactions[i]).parent();
						if (!parent.length) {
							continue;
						}
						let _interactionHtmlMessages = [];
						parent.empty();

						if (
							project.buyer_interaction_callout_text &&
							!$('.response-cap-and-count-response-requested.d-flex').length &&
							project.customer_order === null
						) {
							// Note: don't display if .response-cap-and-count-response-requested is set to show, as set above
							interactionSentenceClone
								.find('span.response:not(.bark-svg-icon)')
								.html(cleanse(project.buyer_name) + ' ' + buyerInteractionCalloutText);
							parent.html(interactionSentenceClone.prop('outerHTML'));
							$(
								'.seller-dash-v2 .dashboard-project-details .project-details-insights .interaction-info',
							).show();
						}

						if (project.buyer_interaction_messages.length > 0) {
							_interactionHtmlMessages = project.buyer_interaction_messages
								.reverse()
								.map(function (value, key) {
									let _callout_text =
										key > 0
											? _t('seller_dashboard:project-details.buyer-interaction-message', {
													buyer_public_name: cleanse(project.buyer_name),
											  })
											: cleanse(project.buyer_name) +
											  ' ' +
											  buyerInteractionCalloutText.substring(
													0,
													buyerInteractionCalloutText.length - 1,
											  );

									interactionSentenceClone
										.find('span.response:not(.bark-svg-icon)')
										.html(
											` ${_callout_text}: <div class="font-weight-bold">&ldquo;${value}&rdquo;</div>`,
										);
									return interactionSentenceClone.prop('outerHTML');
								});
							parent.html(_interactionHtmlMessages.join(''));
						}
						$('.seller-dash-v2 .dashboard-project-details .interaction-info').show();
					}
				}
				// Answers & questions

				$('.dashboard-project-details .project-questions-answers').text('');
				for (i = 0; i < project.answers.length; i++) {
					$('.dashboard-project-details .project-questions-answers').append(
						'<div class="project-details-question text-xs text-light-grey pb-2 text-regular">' +
							project.answers[i].question +
							'</div>',
					);
					$('.dashboard-project-details .project-questions-answers').append(
						'<div class="project-details-answer text-xs pb-4">' +
							project.answers[i].answer +
							'</div>',
					);
				}

				// Additional details
				if (typeof project.detail !== 'undefined' && project.detail) {
					$('.dashboard-project-details .project-questions-answers').append(
						'<div class="project-details-question text-xs text-light-grey pb-2 text-regular">Additional details</div>',
					);
					$('.dashboard-project-details .project-questions-answers').append(
						'<div class="project-details-answer text-xs pb-4">' +
							nl2br(project.detail) +
							'</div>',
					);
				}

				// TODO implement files big boy

				var files = '';

				// Images
				if (typeof project.images !== 'undefined' && project.images) {
					for (var j = 0; j < project.images.length; j++) {
						files +=
							'<a href="' +
							project.images[j].image +
							'" target="_blank" title="' +
							_t('seller_dashboard:project-details.see-image-new-window') +
							'">' +
							'<div class="d-inline-flex flex-column overflow-hidden text-center border rounded p-3 mt-2 mr-2 align-items-center">' +
							'<img class="filetype-asset" src="' +
							Bark.seller.getAttachmentAsset(project.images[j].extension) +
							'" alt="" />' +
							'<p class="text-light-grey text-xxs pt-3">' +
							Bark.seller.truncateFileName(
								project.images[j].image,
								project.images[j].extension,
							) +
							'</p>' +
							'</div>' +
							'</a>';
					}
				}

				// Files
				if (typeof project.files !== 'undefined' && project.files) {
					for (var j = 0; j < project.files.length; j++) {
						files +=
							'<a href="' +
							project.files[j].path +
							'" target="_blank" title="' +
							_t('seller_dashboard:project-details.see-image-new-window') +
							'">' +
							'<div class="d-inline-flex flex-column overflow-hidden text-center border rounded p-3 mt-2 mr-2 align-items-center">' +
							'<img class="filetype-asset" src="' +
							Bark.seller.getAttachmentAsset(project.files[j].extension) +
							'" alt="" />' +
							'<p class="text-light-grey text-xxs pt-3">' +
							Bark.seller.truncateFileName(
								project.files[j].filename,
								project.files[j].extension,
							) +
							'</p>' +
							'</div>' +
							'</a>';
					}
				}

				if (files) {
					$('.dashboard-project-details .project-questions-answers').append(
						'<div class="project-details-question text-xs text-light-grey pb-2 text-regular">' +
							_t('seller_dashboard:project-details.attachments') +
							'</div>' +
							'<div class="project-details-answer text-xs project-details-files-content pb-4 text-regular">' +
							files +
							'</div>',
					);
				}

				$('.project-details-map img').attr(
					'src',
					String(
						'https://maps.googleapis.com/maps/api/staticmap?center=[lat],[lng]&zoom=11&size=625x235&maptype=roadmap&key=AIzaSyD3p2CemxOtPGf4igCbaMqo4BrNEF8Wktc',
					)
						.replace('[lat]', project.lat)
						.replace('[lng]', project.lng),
				);

				if (shouldShowLeadDetailsNewDesign()) {
					applyLeadDetailsNewDesignQnAChanges();
				}

				// Google map
				// $('.project-details-map').css({
				//     backgroundImage: String('url(https://maps.googleapis.com/maps/api/staticmap?center=[lat],[lng]&zoom=11&size=640x640&maptype=roadmap&key=AIzaSyD3p2CemxOtPGf4igCbaMqo4BrNEF8Wktc').replace('[lat]', project.lat).replace('[lng]', project.lng)
				// });

				// Show it

				$('.dashboard-project-details').animate({ opacity: 1 }, { duration: 150 });
				$('.dashboard-project-details .project-details-project-container')
					.css({ display: 'block' })
					.animate(
						{ opacity: 1 },
						{
							duration: opacityDuration,
							complete: function () {
								if (
									!Bark.seller.alreadyRespondedToProject(project.id) &&
									Bark.seller.isRespondingToAProject()
								) {
									if (project.id == parseInt(Bark.ProjectsData.projectToRespond)) {
										// Decline url request for suggested bark (prx and decline in the url)
										if (
											Bark.seller.isRespondingToAProject() &&
											Bark.seller.isDecliningProjectRequest() &&
											project.is_seller_shortlisted
										) {
											$('#confirmNotInterested').modal('show');
										}

										// Coming from an email to submit details and partof the auto submit experiment
										else if (Bark.seller.isRespondingViaEmail()) {
											if (Bark.ProjectsData.projectToRespondType === 'prospect') {
												$('.infobox-container').attr('data-responded', 1);
											}
											Bark.seller.processSubmitYourDetails();
										}
									}
								}
							},
						},
					);
			},

			getAttachmentAsset: function (fileExtension) {
				var basePath =
					'https://d18jakcjgoan9.cloudfront.net/img/barkv2/material-icons/filetype_';

				if (['doc', 'docx', 'pages'].indexOf(fileExtension) !== -1) {
					return basePath + 'doc.svg';
				} else if (fileExtension === 'gif') {
					return basePath + 'gif.svg';
				} else if (['jpg', 'jpeg'].indexOf(fileExtension) !== -1) {
					return basePath + 'jpg.svg';
				} else if (fileExtension === 'png') {
					return basePath + 'png.svg';
				} else if (fileExtension === 'pdf') {
					return basePath + 'PDF.svg';
				} else if (fileExtension === 'zip') {
					return basePath + 'zip.svg';
				} else {
					return basePath + 'other.svg';
				}
			},

			truncateFileName: function (filename, extension) {
				var maxLength = 6;

				var shortenedFilename =
					filename.length > maxLength
						? filename.substr(0, maxLength - 1) + '&hellip;'
						: filename;
				var processedExtension = extension ? extension.toLowerCase() : '';

				return shortenedFilename + processedExtension;
			},

			alreadyRespondedToProject: function (projectId) {
				var project = Bark.seller.getProjectDataById(projectId);
				if (!project) {
					return false;
				}

				if (typeof project.is_visible_to_seller !== 'undefined') {
					if (project.is_visible_to_seller == '1') {
						return true;
					}
				}

				return false;
			},

			wasSellerDismissed: function (projectId) {
				var project = Bark.seller.getProjectDataById(projectId);
				if (!project) {
					return false;
				}

				if (typeof project.is_dismissed !== 'undefined') {
					if (project.is_dismissed == '1') {
						return true;
					}
				}

				return false;
			},

			purchaseCreditResponseHandler: async function (response) {
				tracking('starter_pack_modal', 'starter_pack_modal_confirm_payment', {
					project_id: Bark.seller.currentlyShownProjectId,
					seller_profile_id: Bark.seller.id,
				});

				$('.bark-icon-cross-o').fadeIn();
				Bark.recordHotjarEvent('starter-pack-payment-success');
				$('#purchase-credit-stripe-error').hide();
				Bark.seller.stripeToken = response.id;
				Bark.seller.setAutoTopup = $('#autotop-enabler-starter-pack').is(':checked')
					? 1
					: 0;

				if (
					Bark.seller.isMultiPaymentModalAvailable() &&
					Bark.seller?.paymentType !== 'add-card'
				) {
					if (
						Bark.seller.$multiPaymentModal
							.find('.js-payment-modal-confirm')
							.attr('data-payment-method') === NEW_CARD
					) {
						Bark.showLoading();
						try {
							await window.BarkPaymentsCardModal.confirmSetupIntent(
								Bark.seller.$cardModal.data('user-id'),
								Bark.seller.stripeToken,
							);
							Bark.seller.stripeToken = null;
						} catch (e) {
							console.error(e);
							window.bugsnagClient && window.bugsnagClient.notify(e);
							Bark.seller.purchaseUpsellHandleFailed(
								_t('seller_dashboard:credit-upsell.error-message.authorize-payment'),
							);
							Bark.hideLoading();
							return;
						}
						Bark.hideLoading();
					}
					Bark.seller.stripeToken = null;
				}

				if (Bark.seller.paymentType === 'add-card') {
					$('.modal-backdrop').addClass('modal-backdrop-blue');
				}

				await Bark.seller.getSellerCurrentCardInfo();
				$('.response-modal-card-number-suffix').text(response.card.last4);

				if (Bark.seller.isMultiPaymentModalAvailable()) {
					$('.response-modal-card-expiry-date').text(Bark.seller.cardExpiry);
					$('.response-modal-card-brand-name').text(Bark.seller.cardBrand);
					$('.response-modal-card-brand')
						.empty()
						.append(
							$('<img>', {
								class: 'img-fluid',
								src: Bark.seller.cardBrandImgSrc,
							}),
						);
					$('.js-saved-card').removeClass('d-none');
					$('.js-card-details-row').removeClass('d-none');
				}
				Bark.seller.cardLast4 = response.card.last4;
				$('.response-modal-card-number-suffix').text(response.card.last4);

				window.BarkPaymentsCardModal.hideModalErrorText(Bark.seller.$cardModal);

				if (Bark.seller.page === 'bundle') {
					window.bugsnagClient.notify(
						new Error('Unsupported response type: Attempt to run bundles'),
					);
					console.error('Unsupported response type');
				} else if (
					typeof Bark.seller.paymentType === 'string' &&
					Bark.seller.paymentType.startsWith('pack_') &&
					Bark.seller.paymentType !== 'pack_upsell_welcome'
				) {
					const packId = Bark.seller.paymentType.substr(5);
					Bark.seller.purchaseCreditsThroughApi(
						packId,
						Bark.seller.setAutoTopup,
						Bark.seller.stripeToken,
						Bark.seller.currentlyShownProjectId,
					);
				} else if (
					typeof Bark.seller.paymentType === 'string' &&
					Bark.seller.paymentType.startsWith('autotopup_pack_')
				) {
					const packId = Bark.seller.paymentType.substr(15);
					Bark.seller.purchaseCreditsThroughApi(
						packId,
						Bark.seller.setAutoTopup,
						Bark.seller.stripeToken,
						Bark.seller.currentlyShownProjectId,
					);
				} else if (
					typeof Bark.seller.paymentType === 'string' &&
					Bark.seller.paymentType.startsWith('upsell')
				) {
					Bark.seller.processBuyUpgradeCreditsApi(
						$('.js-multiple-payment-modal').data('planId'),
					);
				} else if (
					typeof Bark.seller.paymentType === 'string' &&
					Bark.seller.paymentType.startsWith('credit')
				) {
					Bark.seller.purchaseCreditsThroughApi(
						$('.js-multiple-payment-modal').data('planId'),
						Bark.seller.setAutoTopup,
						Bark.seller.stripeToken,
						Bark.seller.currentlyShownProjectId,
					);
				} else {
					Bark.seller.purchaseStarterPack();
				}
			},

			purchaseStarterPack: () => {
				Bark.seller.purchaseCreditsThroughApi(
					'spack',
					Bark.seller.setAutoTopup,
					Bark.seller.stripeToken,
					Bark.seller.currentlyShownProjectId,
					null,
					() => {
						Bark.seller.trackStarterPackPurchase();
						Bark.seller.is_starter_pack_eligible = false;
						$('.price-on-leads-details').removeClass('d-flex').addClass('d-none');
						$(`#ghg-on-leads-details`).hide();
						if (LeadsListRctDispatcher) {
							LeadsListRctDispatcher({
								type: 'setLeadsListBanner',
								value: Bark.sellerSelfObject.leads_list_banner ?? undefined,
							});
						}
					},
				);
			},

			setPurchasing: (isPurchasing) => {
				purchasing = isPurchasing;
			},

			getIsPurchasing: () => purchasing,

			setViewport: function () {
				Bark.seller.debug('- setViewport()');

				var logoLeft;
				var dashboardTop;

				// Mobile
				if ($(window).width() < Bark.seller.mobileThreshold) {
					Bark.seller.debug('Mobile');
					$('.seller-dash-v2').addClass('mobile').removeClass('desktop tablet');

					if (!Bark.seller.isMobile) {
						// Show the list of projects and project details
						if ($('#sidebar').is(':visible')) {
							$('.leads-and-responses').hide();
						} else {
							if (Bark.seller.getProjectsTotalCount() > 0) {
								$('.leads-and-responses').show();
								$('.seller-dash-v2 .dashboard-projects').show();
								$('.seller-dash-v2 .dashboard-project-details').hide();
							}
						}
					}

					$('#dashboard-sidebar-modal .modal-dialog').addClass('modal-fullscreen');

					Bark.seller.isMobile = true;
					Bark.seller.isTablet = false;
					Bark.seller.isDesktop = false;
				}

				// Tablet
				else if (
					$(window).width() >= Bark.seller.mobileThreshold &&
					$(window).width() < Bark.seller.tabletThreshold
				) {
					Bark.seller.debug('Tablet');

					$('.seller-dash-v2').addClass('tablet').removeClass('desktop mobile');

					if ($('#sidebar').is(':visible')) {
						$('.leads-and-responses').hide();
					} else {
						$('.leads-and-responses').show();
						$('.seller-dash-v2 .dashboard-projects').fadeIn();
					}

					if (
						Bark.seller.getProjectsTotalCount() > 0 &&
						!$('#sidebar').hasClass('active')
					) {
						$('.seller-dash-v2 .dashboard-project-details').fadeIn();
					}

					if (Bark.seller.isMobile) {
						setTimeout(function () {
							Bark.seller.showFirstProject();
						}, 200);
					}

					$('#dashboard-sidebar-modal .modal-dialog').addClass('modal-fullscreen');

					Bark.seller.isMobile = false;
					Bark.seller.isTablet = true;
					Bark.seller.isDesktop = false;
				}

				// Desktop
				else {
					Bark.seller.debug('Desktop');

					$('#dashboard-sidebar-modal .modal-dialog').removeClass('modal-fullscreen');

					$('.seller-dash-v2').addClass('desktop').removeClass('mobile tablet');
					$('#sidebar').addClass('active');
					$('.sidebar-content-wrapper').removeClass('d-none');
					$(
						'.seller-dash-v2 .dashboard-side-menu, .seller-dash-v2 .dashboard-projects',
					).fadeIn();

					if (Bark.seller.getProjectsTotalCount() > 0) {
						$('.leads-and-responses').show();
						$('.seller-dash-v2 .dashboard-project-details').fadeIn();
					}

					// Set desktop layout
					$('#header').show({ duration: 0 });
					dashboardTop = $('.seller-dash-v2').offset().top;
					$('.seller-dash-v2').css({ marginRight: logoLeft });
					$('.seller-dash-v2 .dashboard-side-menu').css({
						left: logoLeft,
						top: dashboardTop,
						width: 150,
					});
					$('.seller-dash-v2 .dashboard-project-details').css({
						margin: '0 0 0 auto',
						width:
							$(window).width() -
							$('.seller-dash-v2 .dashboard-side-menu').width() -
							$('.seller-dash-v2 .dashboard-projects').width() -
							38 -
							logoLeft * 2,
					});

					Bark.seller.showHideBanners();

					if (Bark.seller.isMobile) {
						setTimeout(function () {
							Bark.seller.showFirstProject();
						}, 200);
					}

					Bark.seller.isMobile = false;
					Bark.seller.isTablet = false;
					Bark.seller.isDesktop = true;
				}

				Bark.seller.setPanelHeights();
			},

			showHideBanners: function () {
				$('.banners .banner').each(function () {
					if (Bark.seller.isElementInViewport($(this))) {
						$(this).css('visibility', 'visible');
					} else {
						$(this).css('visibility', 'hidden');
					}
				});
			},

			sendBannerDismiss: function (type) {
				Bark.seller.debug('- sendBannerDismiss()');

				if (type) {
					$.post('/sellers/api/banner-dismiss/', { type: type });
				}
			},

			isElementInViewport: function (el) {
				if (typeof jQuery === 'function' && el instanceof jQuery) {
					el = el[0];
				}

				var rect = el.getBoundingClientRect();

				return (
					rect.top >= 0 &&
					rect.bottom <= (window.innerHeight || document.documentElement.clientHeight)
				);
			},

			showGeneralError: function (error) {
				var errorText = null;
				if (
					typeof error !== 'undefined' &&
					typeof error.responseJSON !== 'undefined' &&
					typeof error.responseJSON.error !== 'undefined' &&
					typeof error.responseJSON.error.code !== 'undefined'
				) {
					if (error.responseJSON.error.code === 408) {
						errorText = error.responseJSON.error.message;
					}
				}
				if (!errorText) {
					errorText = _t(
						'seller_dashboard:leads-page.leads-list.generic-error.error-message',
					);
				}
				window.scrollTo(0, 0);
				$('.seller-dash-v2 .dashboard-projects').show();
				$('.seller-dash-v2 .dashboard-projects #sticky-project').empty();
				$('.seller-dash-v2 .dashboard-projects .items').empty();
				$('.seller-dash-v2 .dashboard-project-details').hide();
				$('.seller-dash-v2 .dashboard-projects-filters').hide();
				$('.seller-dash-v2 .general-error .js-general-error-text').text(errorText);
				$('.seller-dash-v2 .general-error').fadeIn({ duration: 200 });
			},

			processNotInterested: function (projectId) {
				Bark.seller.showProjectInteractionLoader();

				Bark.json({
					url: '/response_declined/',
					data: {
						recordType: 'seller',
						recordId: Bark.seller.id,
						projectId: projectId,
						declineType: 99,
					},
				})
					.done(function (result) {
						if (result.status) {
							Bark.seller.hideProjectInteractionLoader();
							if (parseInt(projectId) === parseInt(Bark.ProjectsData.projectToRespond)) {
								Bark.seller.removeProjectToRespond();
							}
							if ($('#notInterestedSurvey').length > 0) {
								$('#notInterestedSurvey').modal();
							} else {
								if (shouldShowLeadsListRct()) {
									leadsListRctNotInterested(projectId);
								} else {
									Bark.seller.removeProject(projectId);
								}

								if (showQuickFilters()) {
									//Updates quick filters counts
									setTimeout(function () {
										Bark.seller.fetchFilteredRecords();
									}, 1000);
								}
							}
							tracking('seller-leads-dashboard', 'Not interested', {
								project_id: projectId,
								lead_type: 'project',
							});
						} else {
							Bark.seller.hideProjectInteractionLoader();
						}
					})
					.fail(function () {
						Bark.seller.hideProjectInteractionLoader();
					});
			},

			processUndoNotInterested: function (projectId) {
				Bark.seller.showProjectInteractionLoader();

				Bark.json({
					url: '/response_declined_delete/',
					data: {
						recordType: 'seller',
						recordId: Bark.seller.id,
						projectId: projectId,
						declineType: 99,
					},
				})
					.done(function (result) {
						Bark.seller.hideProjectInteractionLoader();
						if (result.status) {
							if (shouldShowLeadsListRct()) {
								leadsListRctNotInterested(projectId);
							} else {
								Bark.seller.removeProject(projectId);
							}
						}
					})
					.fail(function () {
						Bark.seller.hideProjectInteractionLoader();
					});
			},

			processHiredModal: function () {
				var modal = $('#sellerHiredModal'),
					projectId = $('.dashboard-project-details').attr('data-project-id'),
					noDisclosure = $('#hired-no-disclose', modal).prop('checked');

				// Validate the modal contents
				$('.error', modal).addClass('d-none');
				if (!noDisclosure) {
					if (!$('#hired-amount', modal).val()) {
						$('#hired-modal-amount-error').removeClass('d-none');
						return false;
					} else if (!$('#hired-type', modal).val()) {
						$('#hired-modal-type-error').removeClass('d-none');
						return false;
					}
				}

				// hide the modal, show the loader
				// Note: If "nodisc" is set, the API will automatically override the values for quote and qtype, but still requires valid values to be posted
				var payload = {
					bid: projectId,
					quote: noDisclosure ? 0 : $('#hired-amount', modal).val(),
					qtype: noDisclosure ? 'onceoff' : $('#hired-type', modal).val(),
					review: $('#hired-review', modal).prop('checked') ? 1 : 0,
					nodisc: noDisclosure ? 1 : 0,
				};
				modal.modal('hide');
				Bark.showLoading();

				// Post to the API
				Bark.api(
					'seller/project-hired/',
					payload,
					function (data) {
						Bark.hideLoading();
						// Prevent re-showing this modal
						$('#project-status-select').data('has-hired-data', true);
						Bark.seller.updateLocalProject(projectId, 'has_hired_data', true);
					},
					function (error) {
						Bark.hideLoading();
						if (error.status === 403) {
							// Means the project has already been marked as hired. Fail silently.
							// Prevent re-showing this modal
							$('#project-status-select').data('has-hired-data', true);
							Bark.seller.updateLocalProject(projectId, 'has_hired_data', true);
							return;
						}
						$('#hired-modal-save-error', modal).removeClass('d-none');
						window.setTimeout(function () {
							modal.modal('show');
						}, 500);
					},
					'POST',
					Bark.apiVersionHeader('v2'),
				);
			},

			resetHiredModal: function () {
				$('#sellerHiredModal input#hired-no-disclose').prop('checked', false);
				$('#sellerHiredModal input#hired-review').prop('checked', true);
				$('#sellerHiredModal input[type=text],#sellerHiredModal input[type=number]').val(
					null,
				);
				$('#sellerHiredModal select').prop('selectedIndex', 0);
				$('#sellerHiredModal #hired-amount, #sellerHiredModal #hired-type').prop(
					'disabled',
					false,
				);
				$('#sellerHiredModal .hired-amount').removeClass('disabled');
				// To trigger the currency symbol coloring
				$('#sellerHiredModal input[type=number]').trigger('blur');
			},

			removeProject: function (projectId) {
				var nextToShowProjectId = $(
					'.dashboard-projects div[data-project-id="' + projectId + '"]',
				)
					.prev('div.dashboard-projects-item')
					.attr('data-project-id');
				if (!nextToShowProjectId) {
					nextToShowProjectId = $(
						'.dashboard-projects div[data-project-id="' + projectId + '"]',
					)
						.next('div.dashboard-projects-item')
						.attr('data-project-id');
				}
				if (!nextToShowProjectId) {
					nextToShowProjectId = $('div.dashboard-projects-item')
						.not('div[data-project-id="' + projectId + '"]')
						.not('div[data-project-id=""]')
						.first()
						.attr('data-project-id');
				}
				Bark.seller.removeProjectFromLocalData(projectId);
				var $el = $('.dashboard-projects div[data-project-id="' + projectId + '"]');
				if ($el.closest('#sticky-project').length > 0) {
					$el = $el.closest('#sticky-project');
				}
				$($el).slideUp({
					duration: 200,
					complete: function () {
						// Remove item from the DOM project list
						$(this).remove();

						// Hide the project details panel
						if (Bark.seller.getNumberOfVisibleProjects() == 0) {
							$('.seller-dash-v2 .dashboard-project-details').fadeOut({ duration: 100 });
						}

						Bark.seller.hideProjectInteractionLoader();

						// On mobile just hide the project
						if (Bark.seller.isMobile) {
							Bark.seller.hideProject();
						}
						// On desktop / tablet show the next project on the list, if there is
						else if (nextToShowProjectId) {
							Bark.seller.showProject(nextToShowProjectId);
						} else if (Bark.seller.getNumberOfVisibleProjects() > 0) {
						}

						// Update total
						$('.seller-dash-v2 .results-count span').text(
							parseInt($('.seller-dash-v2 .results-count span').text(), 10) - 1,
						);
						Bark.ProjectsData.projectsTotal--;
						Bark.seller.unfilteredTotal--;
						Bark.seller.updateNewHeaderLeadCounts(
							Bark.ProjectsData.projectsTotal,
							Bark.seller.unfilteredTotal,
						);
						Bark.seller.processNoRecords();

						// If nothing left and there are more, load more
						if (
							Bark.seller.getNumberOfVisibleProjects() == 0 &&
							Bark.seller.moreRecords
						) {
							Bark.seller.fetchRecords();
						} else {
							Bark.seller.updateRecordCount();
						}
					},
				});
			},

			processNoRecords: function () {
				Bark.seller.debug('- processNoRecords()');

				$(
					'.seller-dash-v2 .dashboard-projects .no-records, .seller-dash-v2 .dashboard-projects .no-records-search',
				).hide();

				$('.seller-dash-v2 .no-results').hide();

				if (
					Bark.seller.getProjectsTotalCount() == 0 &&
					!$('.response-flow-container,.response-flow-later').filter(':visible').length
				) {
					$('.seller-dash-v2').addClass('no-results-returned');

					var noResultsBlock = $('.no-results-template').clone();
					noResultsBlock.removeClass('d-none no-results-template');
					noResultsBlock.addClass('no-results-container');

					if (Bark.seller.dataFiltered) {
						Bark.seller.page === 'leads'
							? noResultsBlock
									.find('.no-results-title')
									.text(_t('seller_dashboard:leads-page.leads-list.leads-none'))
							: noResultsBlock
									.find('.no-results-title')
									.text(_t('seller_dashboard:leads-page.leads-list.responses-none'));
						noResultsBlock
							.find('.no-results-text')
							.text(
								_t('seller_dashboard:leads-page.leads-list.results-not-found-message'),
							);
						noResultsBlock
							.find('.no-results-link')
							.text(_t('seller_dashboard:leads-page.leads-list.clear-filters'))
							.addClass('clear-all-filters');
					} else {
						$('.seller-dash-v2 .dashboard-projects .no-records').fadeIn({
							duration: 200,
						});

						if (Bark.seller.page === 'leads') {
							noResultsBlock
								.find('.no-results-title')
								.text(_t('seller_dashboard:leads-page.leads-list.no-leads-title'));
							noResultsBlock.find('.no-results-text').html(
								_t('seller_dashboard:leads-page.leads-list.none-found.text', {
									lead_preferences_link:
										' <a href="' +
										_l('/settings/leads/my_services/') +
										'">' +
										_t(
											'seller_dashboard:leads-page.leads-list.none-found.lead-preferences-link',
										) +
										'</a>',
									interpolation: { escapeValue: false },
								}),
							);
						} else if (Bark.seller.page === 'my-responses') {
							noResultsBlock
								.find('.no-results-title')
								.text(_t('seller_dashboard:leads-page.leads-list.responses-none'));
							noResultsBlock
								.find('.no-results-text')
								.text(
									_t('seller_dashboard:leads-page.leads-list.you-have-not-responded'),
								);
							noResultsBlock
								.find('.no-results-link')
								.text('View leads')
								.attr('href', _l('/sellers/dashboard/'));
						}
					}

					if ($('.no-results-container').length == 0) {
						$('.no-results').append(noResultsBlock);
					}

					$('.seller-dash-v2 .no-results').fadeIn();
					$('.seller-dash-v2 .dashboard-project-details').addClass('d-none');
					$('.desktop-no-results').addClass('d-lg-flex');
					$('.tablet-no-results').addClass('d-md-block');
					$('.mobile-no-results').addClass('d-block');

					$('.filter-icon-wrapper').addClass('d-none').removeClass('d-flex');
				} else {
					Bark.seller.hideNoResultsPanel();
				}
			},

			getDataUrl: function () {
				if (Bark.seller.page == 'leads') {
					return 'seller/barks/';
				}

				if (Bark.seller.page == 'my-responses') {
					return 'seller/responses/';
				}

				if (Bark.seller.page === 'bundle') {
					return 'seller/bundle/';
				}
			},

			openSearchFormExtras: function () {
				$('.seller-dash-v2 .search-extras, .seller-dash-v2 .search-buttons').slideDown({
					duration: 200,
				});
				$('.seller-dash-v2 .search-keyword .form-search-submit').hide({ duration: 200 });
			},

			closeSearchFormExtras: function () {
				Bark.seller.debug('- closeSearchFormExtras()');
				$('.seller-dash-v2 .search-extras, .seller-dash-v2 .search-buttons').slideUp({
					duration: 200,
				});
				$('.seller-dash-v2 .search-keyword .form-search-submit').show({ duration: 200 });
			},

			getRequestQueryString: function () {
				return window.location.search.replace('?', '');
			},

			buildFormDataQueryString: function () {
				var query = '';
				var args = {};

				args.pn = $('.dashboard-projects-filters .field-location').val()
					? $('.dashboard-projects-filters .field-location').val().trim()
					: '';

				var latlng = args.pn
					? $('.dashboard-projects-filters .field-location').data('value').split('_')
					: [];

				args.pn = latlng.length === 2 ? args.pn : '';
				args.lat = latlng.length === 2 ? latlng[0] : '';
				args.lng = latlng.length === 2 ? latlng[1] : '';

				args.r =
					args.pn && latlng.length === 2
						? $('.dashboard-projects-filters .field-radius').val()
						: '';

				args.cids = $('.dashboard-projects-filters .field-categories').val() || '';
				args.crs = $('.dashboard-projects-filters .field-credits').val() || '';
				args.p = $('.dashboard-projects-filters .field-submitted').val() || '';
				args.kw = $('.dashboard-projects-filters .field-keyword').val()
					? encodeURIComponent($('.dashboard-projects-filters .field-keyword').val())
							.toLowerCase()
							.replace(/%20/g, '+')
					: '';

				for (var arg in args) {
					if (args[arg] !== '') {
						query += arg + '=' + args[arg] + '&';
					}
				}

				return query;
			},

			logViewProject: function (projectId) {
				const project = Bark.seller.getProjectDataById(projectId);

				let additionalProps = {
					project_id: projectId,
					seller_profile_id: Number(Bark.seller.id),
					pagename: window.location.pathname,
					auto_selected: false,
					highlights: [],
					be_the_first_to_respond: false,
					is_urgent: project?.is_urgent,
				};
				let _lead_item_project = $(
					'.dashboard-projects-item[data-project-id="' + projectId + '"]',
				);
				let firstToRespond = _lead_item_project.find(
					'.with-highlights .first-to-respond:not(.d-none)',
				);
				if (firstToRespond.length > 0) {
					additionalProps['be_the_first_to_respond'] = firstToRespond.length > 0;
				}

				let badgeSelector =
					'.with-highlights .project-item-highlights-badge:not(.d-none)';
				let leadHighlightsBadgeMessage = _lead_item_project.find(
					badgeSelector + ' .lead-highlights-badge-message',
				);
				if (
					leadHighlightsBadgeMessage.length > 0 &&
					leadHighlightsBadgeMessage.first().text() !== '' &&
					!_lead_item_project.find(badgeSelector).hasClass('resubmitted_project')
				) {
					additionalProps.highlights.push(
						'project_lead_highlights_' +
							leadHighlightsBadgeMessage.first().text().toLowerCase().replace(/ /g, '_'),
					);
				}
				let leadHighlightsBadge = _lead_item_project.find(
					'.with-highlights .highlights:not(.d-none) .posted_ago.badge.badge-success',
				);
				if (leadHighlightsBadge.length > 0) {
					additionalProps.highlights.push('project_lead_highlights_badge');
				}
				let leadHighlightsAdditionalDetails = _lead_item_project.find(
					'.with-highlights .additional-details-label:not(.d-none)',
				);
				if (leadHighlightsAdditionalDetails.length > 0) {
					additionalProps.highlights.push('project_lead_highlights_additional_details');
				}

				if (project.is_urgent) {
					additionalProps.highlights.push('project_lead_highlights_urgent_request');
				}

				if (project.is_top_opportunity) {
					additionalProps.highlights.push('project_lead_is_top_opportunity');
				}

				additionalProps.highlights = additionalProps.highlights.join(', ');

				tracking('seller-leads-dashboard-&-responses', 'View project', additionalProps);
			},

			logProjectView: function (projectId) {
				var project = Bark.seller.getProjectDataById(projectId);
				if (project) {
					// Prevent sending this every single second when a user keeps clicking the link. Delay for 30 seconds.
					var now = Math.round(new Date().getTime() / 1000);
					if (parseInt(projectId) in Bark.seller.projectLogs) {
						if (Bark.seller.projectLogs[parseInt(projectId)] + 30 > now) {
							return false;
						}
					}
					Bark.seller.projectLogs[parseInt(projectId)] = now;

					if (Bark.seller.page === 'leads' || Bark.seller.page === 'bundle') {
						this.logViewProject(projectId);

						// Send project view log
						Bark.api(
							'seller/bark/view',
							{ bid: projectId },
							function () {},
							function (e) {
								bugsnagClient.notify(e);
							},
							'POST',
						);
					} else {
						var source = 'response-dashboard';
						var projectResponseId = project.resp_id;
						Bark.api(
							'seller/response/read/' + projectResponseId + '/',
							{ source: source },
							function () {},
							function (e) {
								bugsnagClient.notify(new Error(`Could not mark response as read`));
							},
							'POST',
							Bark.apiVersionHeader('v2'),
						);
					}
				}
			},

			showProjectInteractionLoader: function () {
				// Disable all the call to action buttons
				$(
					'.seller-dash-v2 .button, .seller-dash-v2 .send-later, .seller-dash-v2 button.not-interested',
				).prop('disabled', true);

				Bark.seller.interactingWithProject = true;
				$('.loading-project-interaction').css({ display: 'flex' });
				$('.loading-project-interaction').fadeIn({ duration: 200 });
			},

			hideProjectInteractionLoader: function () {
				$('.loading-project-interaction').css({ display: 'none' });
				$('.loading-project-interaction').fadeOut({ duration: 100 });
				$(
					'.seller-dash-v2 .button, .seller-dash-v2 .send-later, .seller-dash-v2 button.not-interested',
				).prop('disabled', false);

				setTimeout(function () {
					Bark.seller.interactingWithProject = false;
				}, 700);
				if ($(Bark.seller.currentModalSelector).length > 0) {
					$(Bark.seller.currentModalSelector).modal('hide');
				}
			},

			showListLoader: function (reset) {
				if (shouldShowLeadsListRct() && LeadsListRctDispatcher) {
					if (reset) {
						LeadsListRctDispatcher({ type: 'setListLoading', value: true });
					}
					return;
				}
				var loadingMoreRecords = $('.seller-dash-v2 .loading-more-records');

				if (reset) {
					loadingMoreRecords.addClass('pt-8').removeClass('pt-3');
				} else {
					loadingMoreRecords.addClass('pt-3').removeClass('pt-8');
				}

				$('.seller-dash-v2 .load-more').hide();
				loadingMoreRecords.show();
			},

			hideListLoader: function () {
				$('.seller-dash-v2 .loading-more-records').hide();
			},

			showFilterLoader: function () {
				$('.sidebar-header-right .spinner').removeClass('d-none');
				$('.clear-all-filters').addClass('d-none');
			},

			hideFilterLoader: function () {
				$('.sidebar-header-right .spinner').addClass('d-none');
				if (Bark.seller.filtersAreDirty) {
					$('.clear-all-filters').removeClass('d-none');
				}
			},

			showLoadMoreButton: function () {
				$('.seller-dash-v2 .load-more').show();
			},

			hideLoadMoreButton: function () {
				$('.seller-dash-v2 .load-more').hide();
			},

			removeProjectToRespond: function () {
				QS.deleteValue('prx');
				Bark.ProjectsData.projectToRespond = null;
				Bark.ProjectsData.projectToRespondType = null;

				setTimeout(function () {
					Bark.json({
						url: '/sellers/remove_sticky_project/',
						type: 'get',
					}).done(function (result) {
						// do nothing
					});
				}, 500);
			},

			showFirstProject: function (ignoreRespondingOne = false) {
				Bark.seller.debug('- showFirstProject()');

				var projectId;
				// Responding to a specific project via an email link ?prx=64643564
				if (Bark.seller.isRespondingToAProject() && !ignoreRespondingOne) {
					projectId = Bark.ProjectsData.projectToRespond;
				}
				// If not, show the first project on the list
				else {
					if (shouldShowLeadsListRct() && Bark.ProjectsData.projects.length) {
						projectId = Bark.ProjectsData.projects[0].project_id;
					} else {
						projectId = $('.items .dashboard-projects-item')
							.first()
							.attr('data-project-id');
					}
				}

				if (typeof projectId !== 'undefined') {
					var result = Bark.seller.showProject(projectId);
					if (result) {
						$('.leads-list-container').animate(
							{
								scrollTop:
									$(
										'.dashboard-projects-item[data-project-id="' + projectId + '"]',
									).offset().top - 100,
							},
							100,
						);
					}
					this.logViewProject(projectId);
				} else {
					Bark.seller.debug('No project to show');
				}
				// Reset the seller workflow so that it picks up data from this project
				Bark.sellerResponseFlow && Bark.sellerResponseFlow.resetStatus();
			},

			/**
			 * Checks if the seller is responding to a project (prx) and whether
			 * the project exists
			 */
			isRespondingToAProject: function () {
				return Bark.ProjectsData.projectToRespond !== null;
			},

			isRespondingViaEmail: function () {
				var expm = Bark.seller.getUrlParameter('expm');
				var autoPurchase = Bark.seller.getUrlParameter('apr');
				if (expm == '1' || (autoPurchase && autoPurchase === '1')) {
					return true;
				}
				if (Bark.ProjectsData.projectToRespondType === 'prospect') {
					return true;
				}

				return false;
			},

			isRespondingViaPaymentRedirect: function () {
				let paymentIntent = Bark.seller.getUrlParameter('payment_intent', null);
				let prx = Bark.seller.getUrlParameter('prx', null);
				return !!(paymentIntent && prx);
			},

			isDecliningProjectRequest: function () {
				var decline = Bark.seller.getUrlParameter('decline');
				if (decline == '1') {
					return true;
				}

				return false;
			},

			getNumberOfVisibleProjects: function () {
				return $('.seller-dash-v2 .dashboard-projects .items .dashboard-projects-item')
					.length;
			},

			/**
			 * @param {int} projectId
			 * @returns {ProjectObject|false}
			 */
			getProjectDataById: function (projectId) {
				var allProjects = Bark.seller.getAllProjectsData();

				for (var i = 0; i < allProjects.length; i++) {
					if (allProjects[i].project_id == projectId) {
						return Bark.seller.transformApiProjectResponseToOldFormat(allProjects[i]);
					}
				}

				return false;
			},

			updateLocalProject: function (projectId, name, value) {
				var allProjects = Bark.seller.getAllProjectsData();

				for (var i = 0; i < allProjects.length; i++) {
					if (allProjects[i].id == projectId) {
						Bark.ProjectsData.projects[i][name] = value;
					}
				}
			},

			removeProjectFromLocalData: function (projectId) {
				var allProjects = Bark.seller.getAllProjectsData();

				for (var i = 0; i < allProjects.length; i++) {
					if (allProjects[i].id == projectId) {
						Bark.ProjectsData.projects.splice(i, 1);
					}
				}
			},

			getProjectsTotalCount: function () {
				return Bark.ProjectsData.projectsTotal || 0;
			},

			getAllProjectsData: function () {
				return Bark.ProjectsData.projects;
			},

			addDataRecord: function (data) {
				Bark.ProjectsData.projects.push(data);
			},

			updateDataRecord: function (data) {
				var projects = Bark.ProjectsData.projects;
				var projectFound = false;
				for (var i = 0; i < projects.length; i++) {
					var project = projects[i];
					if (parseInt(project['id']) === parseInt(data['project_id'])) {
						projects[i] = data;
						projectFound = true;
					}
				}
				if (!projectFound) {
					Bark.seller.addDataRecord(data);
				}
			},

			calculateProjectCredits: function (project) {
				var beforeGenericDiscount = parseInt(project.credits_required_before_discount);
				var beforeClearanceDiscount = parseInt(project.credits_required_original);
				var priceBeforeDiscount = Math.max(
					beforeGenericDiscount,
					beforeClearanceDiscount,
					project.credits_required,
				);
				var priceNow = parseInt(project.credits_required);
				var oldCreditsDisplay = Math.max(priceBeforeDiscount, priceNow);

				var beforeDisplay = '';
				var nowDisplay = '';
				var tickHtml = $('.tick-holder').html();

				if (Bark.seller.projectBundle) {
					beforeDisplay =
						'<del>' +
						beforeClearanceDiscount +
						' ' +
						Bark.seller.getCreditsSingularOrPlural(beforeClearanceDiscount) +
						'</del> ';
				} else {
					if (
						(priceBeforeDiscount > priceNow || project.is_free) &&
						oldCreditsDisplay > 0
					) {
						beforeDisplay =
							'<del>' +
							oldCreditsDisplay +
							' ' +
							Bark.seller.getCreditsSingularOrPlural(oldCreditsDisplay) +
							'</del> ';
					}

					if (project.hide_prev_cr) {
						beforeDisplay = '';
					}

					if (project.is_free || priceNow == 0) {
						nowDisplay = _t('common:free');
					} else {
						nowDisplay =
							priceNow + ' ' + Bark.seller.getCreditsSingularOrPlural(priceNow);
					}
				}

				return beforeDisplay + nowDisplay;
			},

			getCreditsSingularOrPlural: function (value) {
				return _t('common:credits', { count: value });
			},

			renderNewProjectList: function () {
				// bark-seller-notifications.js calls this function but seller.js is not present in the dashboard v2
			},

			renderMyProjectList: function () {
				// bark-seller-notifications.js calls this function but seller.js is not present in the dashboard v2
			},

			getUrlParameter: function (name) {
				name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
				var regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
				var results = regex.exec(location.search);
				return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
			},

			setPanelHeights: function () {
				Bark.seller.debug('--setPanelHeights()');

				var headerHeight = document.getElementById('bark-header').offsetHeight;
				var welcomeRow = document.getElementsByClassName('welcomerow')[0];
				var welcomeRowHeight = welcomeRow ? welcomeRow.offsetHeight : 0;

				var clientHeight = Math.max(
					document.documentElement.clientHeight,
					window.innerHeight || 0,
				);
				var sideBarHeight = clientHeight - headerHeight;
				var panelHeightWithWelcomeRow = clientHeight - (headerHeight + welcomeRowHeight);

				var columns = document.getElementsByClassName('fixed-height-column');

				Array.prototype.forEach.call(columns, function (column) {
					if (
						Bark.seller.page !== 'bundle' ||
						$(window).width() > Bark.seller.tabletThreshold
					) {
						column.style.maxHeight = panelHeightWithWelcomeRow + 'px';
					} else {
						column.style.removeProperty('max-height');
						column.style.height = 'auto';
					}
					// unsure border goes to bottom of the page
					if (Bark.seller.page === 'bundle') {
						document.getElementById('dashboard-project-details').style.minHeight =
							panelHeightWithWelcomeRow + 'px';
						document.getElementById('dashboard-projects').style.minHeight =
							panelHeightWithWelcomeRow + 'px';
					}
				});
			},

			debug: function (log) {
				if (Bark.seller.inDebugMode && window.location.hostname.includes('.d.bark.com')) {
					console.log(log);
				}
			},

			/**
			 * @param newData
			 * @returns {ProjectObject}
			 */
			transformApiProjectResponseToOldFormat: function (newData) {
				var hasCardDetails = Bark.ProjectsData.stripeCustomerId != false;
				var estimate = null;

				if (newData.estimator_value) {
					estimate = newData.estimator_value;
				}

				var transformedProject = {
					id: newData['project_id'],
					category_name: newData['project_title'],
					detail: newData['project_detail'] || null,
					avatar_url: newData['buyer_avatar'],
					buyer_name: newData['buyer_share_name'],
					category_id: newData['category_id'],
					country_name: newData['bark_country_name'],
					city_string: newData['city_string'],
					need_list: newData['summary'],
					is_free: newData['is_free'],
					credits_required: newData['credits_required'],
					credits_required_original: newData['credits_required_original'],
					credits_required_before_discount: newData['credits_required_before_discount'],
					expected_credits_required: null,
					credits_price: newData['credits_price'],
					credits_price_after_discount: newData['credits_price_after_discount'],
					expecting_free_responses: newData['expecting_free_responses'],
					has_card_details: hasCardDetails,
					provider_noun: newData['project_title'],
					provider_noun_plural: newData['project_title'],
					one_click_enabled: Bark.seller.one_click_enabled,
					lat: newData['latitude'],
					lng: newData['longitude'],
					time_ago: newData['time_since'],
					is_seller_shortlisted: newData['is_shortlisted'],
					buyer_interaction_callout_text: newData['buyer_interaction_callout_text'],
					buyer_interaction_top_text: newData['buyer_interaction_most_significant'],
					buyer_interaction_messages: newData['buyer_messages']
						? newData['buyer_messages'].split('$#$#')
						: [],
					buyer_interactions: [],
					answers: newData['custom_fields'] || newData['project_details'],
					currency_symbol: Bark.seller.currency_symbol,
					slug: newData['slug'],
					price_per_credit: Bark.ProjectsData.price_per_credit,
					estimate: estimate,
					since: newData['since'],
					highlights: newData['highlights'] ? newData['highlights'] : [],
					has_files: newData?.files?.length > 0,
					has_images: newData?.images?.length > 0,
					project_detail: newData['project_detail'] ? newData['project_detail'] : null,
				};

				return Object.assign(newData, transformedProject);
			},

			getLeadStatus: function (forceDisplay) {
				Bark.api(
					'seller/self?origin=' + (Bark.isMobile() ? 'mobileweb' : 'desktop'),
					{},
					function (data) {
						data = data.data;
						Bark.sellerSelfObject = data;

						if (window.SubscriptionUpsellFunctions.IsSubscriptionUpsellActive()) {
							window.SubscriptionUpsellFunctions.SubscriptionUpsellBindEvents(data);
						}

						if (data.infobox && data.infobox.text) {
							var infoboxContainer = $('.infobox-container'),
								content = Bark.sprintf('<p>%s</p>', data.infobox.text.join('</p><p>'));

							content = content
								.replace(
									/\[\[link:leadprefs\]\]/g,
									'<a href="' + _l('/settings/leads/my_services/') + '">',
								)
								.replace(
									/\[\[link:guarantee\]\]/g,
									'<a href="#" class="credit-back-guarantee">',
								)
								.replace(
									/\[\[link:leads\]\]/g,
									'<a href="#" class="infobox-back-to-list">',
								)
								.replace(
									/\[\[link:dashboard\]\]/g,
									'<a href="' + _l('/sellers/home/') + '">',
								)
								.replace(/\[\[endlink\]\]/g, '</a>');

							$('.title', infoboxContainer).html(data.infobox.title);
							$('.content', infoboxContainer).html(content);
							if (forceDisplay) {
								$('.infobox-container').removeClass('collapsed');
							}
							infoboxContainer.slideDown();
						} else if (!data.infobox) {
							$('.infobox-container').slideUp(400, function () {
								Bark.seller.setViewport(); // to adjust the height of the projects div
								Bark.seller.loadProfileCompPromptBox();
							});
						}
					},
					function (error) {
						console.error(error);
					},
					'GET',
				);
			},

			loadProfileCompPromptBox: function () {
				if ($('#pc-prompt').length) {
					var boxTemplate = Handlebars.compile(
						document.getElementById('pc-prompt-box-template').innerHTML,
					);

					Bark.api(
						'/sellerprofile/completeness-score',
						{},
						function (data) {
							Bark.sellerSelfObject.profile_completion_score = data.data.score;

							if (Bark.sellerSelfObject.profile_completion_score < 100) {
								$('#pc-prompt').html(
									boxTemplate({
										companyName: Bark.sellerSelfObject.company,
										reviewsHalfStars: parseInt(Bark.sellerSelfObject.reviews_score / 0.5),
										reviewsCount: Bark.sellerSelfObject.reviews_count,
										companyDescription: Bark.sellerSelfObject.description,
										avatarUrlThumbnail: Bark.sellerSelfObject.avatar_url_thumbnail,
										avatarUrl: Bark.sellerSelfObject.avatar_url,
										pcScore: Bark.sellerSelfObject.profile_completion_score,
									}),
								);

								$('.welcomerow').slideDown(400, function () {
									Bark.seller.setPanelHeights();
									$('.profile-summary-box-container').slideDown(400, function () {
										if (Bark.sellerSelfObject.profile_completion_score >= 60) {
											$('.profile-summary-box-container .progress-bar')
												.removeClass('bg-warning')
												.addClass('bg-success');
											$('.profile-summary-box-container .profile-score')
												.removeClass('text-warning')
												.addClass('text-success');
											$('.profile-summary-box-container .profile-score-xs')
												.removeClass('text-warning')
												.addClass('text-success');
										}
										$('.profile-summary-box-container .progress-bar').css(
											'width',
											Bark.sellerSelfObject.profile_completion_score + '%',
										);
									});
								});

								Bark.seller.logProfileCompPromptExpAction('completion-banner-impression');
								Bark.seller.registerProfileCompPromptExpListners();
							}
						},
						function (err) {
							console.log(err);
						},
						'GET',
						Bark.apiVersionHeader('v2'),
					);
				}
			},

			logProfileCompPromptExpAction: function (action) {
				const { logExperimentSessionAction } = window;
				logExperimentSessionAction({
					action: action,
					actionValue: Bark.sellerSelfObject.profile_completion_score,
					experimentId: $('#pc-prompt-experiment-id').val(),
				});
			},

			registerProfileCompPromptExpListners: function () {
				$('#pc-prompt').on('click', function (e) {
					Bark.seller.logProfileCompPromptExpAction('completion-banner-click');
				});
			},

			getExperiments: function () {
				Bark.seller.experiments = {};
			},

			showPurchaseSuccessModal: function (n_credits) {
				$('.modal').modal('hide');
				$('#credit-pack-purchase-success-modal').modal('show');
				$('.js-credit-pack-purchase-success-modal .js-n-extra-credits').text(
					n_credits ? _n(n_credits) : '',
				);
			},

			showSofortSuccessModal: async function (payload) {
				Bark.seller.showPurchaseSuccessModal(payload.credits);
				if (Bark.seller.isMultiPaymentModalAvailable()) {
					Bark.seller.lastPackModalSelector = 'from-sofort';
					if (shouldShowLeadDetailsNewDesign()) {
						const cardContainer = `${getContactButtonContainerSelector()} .calls-to-action.for-leads`;
						Bark.seller.waitForElement(cardContainer, () => {
							$(`${cardContainer} .one-click-response`).length > 0
								? $(`${cardContainer} .one-click-response`).trigger('click')
								: $(`${cardContainer} .submit-your-details`).trigger('click');
							Bark.seller.lastPackModalSelector = null;
						});
					} else {
						Bark.seller.waitForElement('.seller-dash-v2 .calls-to-action', () => {
							if ($('.seller-dash-v2 .one-click-response').length > 0) {
								$('.seller-dash-v2 .one-click-response').trigger('click');
							} else {
								$(
									'.seller-dash-v2 .submit-your-details, .seller-dash-v2 .send-custom-message',
								).trigger('click');
							}
							Bark.seller.lastPackModalSelector = null;
						});
					}
				}
			},

			updateNewHeader: function (data) {
				if ($('#js-show-new-header').val() !== '1') {
					// Not in experiment
					return;
				}

				if (!data?.data?.total) {
					// No data
					return;
				}

				const totalLeads = data?.data?.unfiltered_total;
				const filteredLeads = data?.data?.total;
				const filtersEnabled = !(totalLeads === filteredLeads);

				let numberLocations = 0;
				let numberServices = 0;
				let i = 0;
				while (i <= data?.data?.filters.length) {
					let filter = data?.data?.filters[i];
					if (filter?.id === 'locations') {
						numberLocations = filter?.children.length - 1 ?? 0;
					}
					if (filter?.id === 'categories') {
						numberServices = filter?.children.length;
					}
					i++;
				}
				// Lead settings
				const $activeServices = $('#js-number-of-active-services');
				const $activeLocations = $('#js-number-of-active-locations');

				// Filters
				const $clearFiltersButton = $('#js-clear-filters-button');

				$activeServices.text(
					_t('seller_leads-dashboard:leads-header.service-count.number-of-services', {
						count: numberServices,
						interpolation: { escapeValue: false },
					}),
				);
				$activeLocations.text(
					_t('seller_leads-dashboard:leads-header.location-count.number-of-locations', {
						count: numberLocations,
						interpolation: { escapeValue: false },
					}),
				);

				if (filtersEnabled) {
					$clearFiltersButton.removeClass('d-none');
				} else {
					$clearFiltersButton.addClass('d-none');
				}
				Bark.seller.updateNewHeaderLeadCounts(filteredLeads, totalLeads);

				$('#js-lead-summary-container').removeClass('d-none');
				$('#js-filter-row').removeClass('d-none');
			},

			/**
			 * @param countFilteredTotal number
			 * @param countUnfilteredTotal number
			 */
			updateNewHeaderLeadCounts: function (
				countFilteredTotal = 0,
				countUnfilteredTotal = 0,
			) {
				if ($('#js-show-new-header').val() !== '1') {
					// Not in experiment
					return;
				}
				const filtersEnabled = !(countFilteredTotal === countUnfilteredTotal);
				const $matchingLeads = $('#js-number-of-matching-leads');
				const $filteredLeads = $('#js-number-of-filtered-leads');

				Bark.seller.updateNewHeaderLeadCountsReact(
					filtersEnabled,
					countFilteredTotal,
					countUnfilteredTotal,
				);

				$matchingLeads.text(
					_t('seller_leads-dashboard:leads-header.lead-count.number-of-matches', {
						count: countUnfilteredTotal,
						interpolation: { escapeValue: false },
					}),
				);
				if (filtersEnabled) {
					$filteredLeads.text(
						_t('seller_leads-dashboard:filters-bar.filtered.showing-x-of-y-leads', {
							count: countUnfilteredTotal,
							number_of_filtered_leads: countFilteredTotal,
							interpolation: { escapeValue: false },
						}),
					);
				} else {
					$filteredLeads.text(
						_t('seller_leads-dashboard:filters-bar.unfiltered.show-all', {
							count: countUnfilteredTotal,
							interpolation: { escapeValue: false },
						}),
					);
				}
			},

			updateNewHeaderLeadCountsReact: (
				isFiltered,
				filteredTotal = 0,
				unfilteredTotal = 0,
			) => {
				// Whole function is related to leads_filter_header_exp
				if ($(`#leads-filter-header`).length) {
					try {
						let elementBuilt = false;
						$(`#leads-filter-header`).show();

						if (
							LeadsFilterHeaderDangerouslySetState &&
							$(`#leads-filter-header`).children().length
						) {
							try {
								LeadsFilterHeaderDangerouslySetState({
									unfilteredTotal,
									isFiltered,
									filteredTotal,
									appliedFiltersTotal: Bark.seller.getAppliedFiltersTotal(),
								});
								elementBuilt = true;
							} catch (e) {}
						}

						if (!elementBuilt) {
							if (window.SellerAppDashboard) {
								window.SellerAppDashboard.launchModule(
									'leads-filter-header',
									'leads-filter-header',
									{
										sellerProfileId: Number(Bark.seller.id),
										isFiltered,
										servicesCount: Bark.seller.servicesCount,
										serviceLocationsCount: Bark.seller.locationsCount,
										filteredTotal,
										unfilteredTotal,
										onMount: (dangerouslySetState) => {
											LeadsFilterHeaderDangerouslySetState = dangerouslySetState;
										},
										onFilter: () => Bark.seller.showFilterPanel(),
										onClearFilter: () => {
											Bark.seller.processClearFilters(true);
										},
										showAppliedFiltersTotal: !!showQuickFilters(),
										appliedFiltersTotal: Bark.seller.getAppliedFiltersTotal(),
									},
								);
							} else {
								tracking(
									'seller-leads-dashboard',
									'Seller dashboard react app not loaded',
								);
							}
						}
					} catch (e) {
						window.bugsnagClient && window.bugsnagClient.notify(e);
					}
				}
			},

			init: function () {
				moment.fn.fromNowOrNow = function (a) {
					if (Math.abs(moment().diff(this)) < 60000) {
						//for the fist minute
						return _t(
							'seller_dashboard:leads-page.leads-list.last-updated.default-updated-at-just-now',
						);
					}
					return this.fromNow(a);
				};

				moment.defineLocale('dashboard', {
					relativeTime: {
						future: _t('seller_responses:future-time', { time_interval: '%s' }),
						past: _t('seller_responses:time-ago', { time_interval: '%s' }),
						s: _t('common:relative-time.short.seconds', { number: '%d' }),
						m: _t('common:relative-time.short.minutes', { number: '%d' }),
						mm: _t('common:relative-time.short.minutes', { number: '%d' }),
						h: _t('common:relative-time.short.hours', { number: '%d' }),
						hh: _t('common:relative-time.short.hours', { number: '%d' }),
						d: _t('common:relative-time.short.days', { number: '%d' }),
						dd: _t('common:relative-time.short.days', { number: '%d' }),
						M: _t('common:relative-time.short.months', { number: '%d' }),
						MM: _t('common:relative-time.short.months', { number: '%d' }),
						y: _t('common:relative-time.short.years', { number: '%d' }),
						yy: _t('common:relative-time.short.years', { number: '%d' }),
					},
				});
				this.utcTime = moment.utc();

				// A lil' helper function added to our Array prototype.
				// Essentially just array.Reduce but easier to read down the line.
				// https://stackoverflow.com/a/31844649
				Array.prototype.hasMax = function (attrib) {
					const checker = (o, i) => typeof o === 'object' && o[i];
					return (
						(this.length &&
							this.reduce(function (prev, curr) {
								const prevOk = checker(prev, attrib);
								const currOk = checker(curr, attrib);
								if (!prevOk && !currOk) return {};
								if (!prevOk) return curr;
								if (!currOk) return prev;
								return prev[attrib] > curr[attrib] ? prev : curr;
							})) ||
						null
					);
				};

				setInterval(function () {
					Bark.seller.updateLatestRefresh();
				}, 10000);

				if ($('.welcomerow').length) {
					$('.welcomerow').slideDown(400, function () {
						Bark.seller.setPanelHeights();
					});
				}

				// Make sure this SPA only starts on the .seller-dash-v2 page
				if (!$('.seller-dash-v2').length) {
					return false;
				}

				$('.js-close-success-modal')
					.off('click.navigate')
					.on('click.navigate', function () {
						$('.modal').modal('hide');
						Bark.hideLoading();
						if (
							Bark.seller.isMultiPaymentModalAvailable() &&
							Bark.seller.lastPackModalSelector === 'from-sofort'
						) {
							if (shouldShowLeadDetailsNewDesign()) {
								const cardContainer = `${getContactButtonContainerSelector()} .calls-to-action.for-leads`;
								if (!$(`${cardContainer}`).is(':visible')) {
									Bark.seller.waitForElement(cardContainer, () => {
										$(`${cardContainer} .one-click-response`).length > 0
											? $(`${cardContainer} .one-click-response`).trigger('click')
											: $(`${cardContainer} .submit-your-details`).trigger('click');
										Bark.seller.lastPackModalSelector = null;
									});
								}
							} else {
								if (!$('.seller-dash-v2 .calls-to-action').is(':visible')) {
									Bark.seller.waitForElement('.seller-dash-v2 .calls-to-action', () => {
										if ($('.seller-dash-v2 .one-click-response').length > 0) {
											$('.seller-dash-v2 .one-click-response').trigger('click');
										} else {
											$(
												'.seller-dash-v2 .submit-your-details, .seller-dash-v2 .send-custom-message',
											).trigger('click');
										}
										Bark.seller.lastPackModalSelector = null;
									});
								}
							}
						}
					});
				// Remove botton padding only on this page
				// Add min height in vh units
				$('div#main').css({ 'padding-bottom': 0, minHeight: '100vh' });

				// Bind events
				Bark.seller.bindEvents();

				// Get experiments
				Bark.seller.getExperiments();

				setTimeout(function () {
					if (Bark.seller.projectBundle) {
						Bark.seller.fetchBundle();
					} else {
						Bark.seller.fetchRecords(true, true);
						if (QS.getValue('payment_intent', null)) {
							Bark.seller.onPaymentIntentSuccessRedirect();
						}
					}

					if (Bark.seller.page === 'leads') {
						Bark.seller.getLeadStatus();
					}

					if (Bark.seller.page === 'my-responses') {
						Bark.seller.renderPurchasedBundleModal();
					}

					triggerEnquiriesBanner({
						pagename: window.location.pathname,
						number_of_leads: Bark.seller.getNumberOfVisibleProjects(),
					});
				}, 200);

				Bark.seller.checkDeeplinkFallback();
			},
			initUSCompliance: async function () {
				if (isUSTaxComplianceActive()) {
					Bark.seller.isUSTaxCompliant = await isUSTaxCompliant();
					if (!Bark.seller.isUSTaxCompliant) {
						initializeGooglePlacesAutoComplete();
					}
				}
			},
			checkDeeplinkFallback: function () {
				if (QS.hasKey('deeplink_fallback')) {
					tracking('seller-leads-dashboard-&-responses', 'Deeplink fallback', {
						seller_profile_id: Bark?.seller?.id,
					});
					QS.deleteValue('deeplink_fallback');
					QS.updateBrowserQuerystring();
				}
			},
			showLowQualityWarningModal: (projectId) => {
				$('.modal').modal('hide');

				const modal = $('#low-quality-warning-modal');
				modal.modal('show');

				tracking('seller-leads-dashboard', 'Low quality error modal displayed', {
					project_id: projectId,
					seller_profile_id: Bark.seller.id,
				});
			},
			setupEntityTracking: () => {
				entityTracking.createEntities([
					{
						id: 'seller',
						data: {
							seller_profile_id: Bark.seller.id,
						},
					},
					{
						id: 'project',
						data: {},
					},
					{
						id: 'booking_request',
						data: {
							booking_request_id: Bark.seller.getUrlParameter('br_id', null),
						},
					},
				]);
				const projectId = Bark.seller.getUrlParameter('prx', null);
				if (projectId) {
					entityTracking.updateEntity(
						'project',
						Bark.seller.getProjectDataById(projectId),
					);
				}
				entityTracking.createEvents([
					{
						id: 'booking_request_decision',
						name: 'Decision',
						group: 'Booking Requests',
						entities: ['seller', 'project', 'booking_request'],
					},
					{
						id: 'booking_list_loaded',
						name: 'List Loaded',
						group: 'Booking Requests',
						entities: ['seller', 'project', 'booking_request'],
					},
					{
						id: 'cta_click',
						name: 'Page CTA Event',
						group: 'Booking Requests',
						entities: ['seller', 'project'],
					},
				]);
				setTimeout(() => {
					Bark.seller.checkForBookingRequestState();
					showSetupJobsModal(entityTracking);
					showBookingRequestOnboardingModal(entityTracking);
				}, 500);
			},
			acceptBookingRequest: (projectId) => {
				entityTracking.fireTracking('booking_request_decision', { cta_name: 'accept' });
			},
			rejectBookingRequest: (projectId) => {
				entityTracking.fireTracking('booking_request_decision', { cta_name: 'reject' });
			},
			setTrackingProject: (projectId) => {
				entityTracking.updateEntity('project', Bark.seller.getProjectDataById(projectId));
			},
			checkForBookingRequestState: () => {
				const bookingRequestState = Bark.seller.getUrlParameter('_brs', null);
				const projectId = Bark.seller.getUrlParameter('prx', null);
				if (projectId && bookingRequestState) {
					if (bookingRequestState === 'accept') {
						Bark.seller.acceptBookingRequest(projectId);
					} else {
						Bark.seller.rejectBookingRequest(projectId);
					}
				}
			},
		},
		Bark.seller || {},
	);

	Bark.seller.GHGCreditStatus = Bark.seller.GHGCreditStatus || {};
	Bark.seller.purchaser = Bark.seller.purchaser || {};
	Bark.seller.SubscriptionFreeResponse = Bark.seller.SubscriptionFreeResponse =
		Bark.seller.SubscriptionFreeResponse || {};

	$(document).ready(() => {
		$(document)
			.off('click.track', '.js-starter-pack-accordion-item')
			.on('click.track', '.js-starter-pack-accordion-item', function () {
				const $t = $(this);
				tracking('starter_pack_modal', 'seller_clicked_toggle', {
					seller_profile_id: Number(Bark.seller.id),
					project_id: Number(Bark.seller.currentlyShownProjectId),
					toggle_element_name: $t.data('question'),
				});
			});

		$(document)
			.off(
				'change.track',
				'.starter-pack-container .js-atu-toggle, .starter-pack-simple-container .js-atu-toggle',
			)
			.on(
				'change.track',
				'.starter-pack-container .js-atu-toggle, .starter-pack-simple-container .js-atu-toggle',
				function () {
					const isChecked = Number(this.checked);
					tracking('starter_pack_modal', 'seller_checked_auto_topup', {
						seller_profile_id: Number(Bark.seller.id),
						project_id: Number(Bark.seller.currentlyShownProjectId),
						auto_topup_state: isChecked,
					});
				},
			);

		$(document)
			.off(
				'click.track',
				'.seller-response-modal .close, .starter-pack-modal-simple .close',
			)
			.on(
				'click.track',
				'.seller-response-modal .close, .starter-pack-modal-simple .close',
				function () {
					const $t = $(this);
					tracking('starter_pack_modal', 'seller_clicked_close', {
						seller_profile_id: Number(Bark.seller.id),
						project_id: Number(Bark.seller.currentlyShownProjectId),
					});
				},
			);

		$('.seller-response-modal')
			.parent()
			.off('shown.bs.modal.track')
			.on('shown.bs.modal.track', function () {
				// Close the variant FAQ items
				$('.js-credits-section-waiting .faq-card-header').addClass('collapsed');
				$('.js-credits-section-waiting .collapse').removeClass('show');
			});
		tracking('seller-leads-dashboard-&-responses', 'View leads dashboard', {
			pagename: window.location.pathname,
		});

		Bark.seller.init();
		Bark.seller.initUSCompliance();
	});
}
